import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, 
  Filter, 
  Grid, 
  List, 
  X, 
  FileEdit,
  RefreshCw,
  Upload,
  Image as ImageIcon,
  Download,
  Maximize,
  Minimize2,
  Mail,
  Trash,
  Check,
  CheckCircle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  Loader2,
  Calendar as CalendarIcon,
  MessageSquare,
  Send,
  Wand2,
  Layers,
  AlertTriangle,
  RotateCcw,
  FolderOpen,
  FolderPlus,
  Tag,
  Plus,
  Pencil,
  FolderInput,
  Maximize2,
  Sparkles,
  Globe,
} from 'lucide-react';
import { GithubTargetButtons, GithubTargetDetails } from '../../components/blog/GithubTargetPicker';
import { BlogEditor, blogHtmlToText } from '../../components/BlogEditor';
import BlogArticleViewer from '../../components/blog/BlogArticleViewer';
import NewsletterViewer from '../../components/newsletter/NewsletterViewer';
import ProductDescriptionViewer from '../../components/ProductDescriptionViewer';
import LinkedInPostPreview from '../../components/linkedin/LinkedInPostPreview';
import LinkedInPostEditor from '../../components/linkedin/LinkedInPostEditor';
// TODO: entfernen nach erfolgreichem Test des neuen Freigabe-Flows. Render-Stellen
// sind auskommentiert (ersetzt durch Ziel-Chips im Schedule-Dialog); Import bleibt
// als Fallback erhalten, daher Lint-Warnung zum ungenutzten Import unterdrückt.
// eslint-disable-next-line no-unused-vars
import SupabasePublishSection from '../../components/SupabasePublishSection';
// Shared blog fragment renderer — used at approve/schedule time so the rich
// content_html is persisted even when the user never opened the viewer.
import { buildCleanHtml, getHtmlLabels } from '../../components/blog/cleanHtml';
import { RichTextFieldEditor, richFieldHtmlToText } from '../../components/RichTextFieldEditor';
import { 
  FaLinkedin, FaInstagram, 
  FaTwitter, FaPinterest, FaBlog, FaEnvelope, FaNewspaper, FaFacebook, FaMedium, FaYoutube, FaBox
} from 'react-icons/fa';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import PostOptionsSection, { OPTION_PLATFORMS } from '../../components/distribution/PostOptionsSection';
import { useLogoPrompt, appendLogoFields } from '../../components/distribution/LogoUploadPrompt';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { localInputToUTC, utcToLocalInput } from '../../utils/datetime';
import { useSubscription } from '../../context/SubscriptionContext';
import { UpgradeGatePopup } from '../../components/subscription/UpgradeGatePopup';
import ImageStyleModal from '../../components/ImageStyleModal';
import GroundingSourcesPanel from '../../components/GroundingSourcesPanel';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

// Platform configurations - updated February 2026
const PLATFORM_CONFIG = {
  x: { 
    name: 'X', icon: FaTwitter, color: '#000000', 
    maxLength: 280, hasHeadline: false, headlineMax: 0, 
    hashtags: 3, imageLimit: 1, image: '1200×675 (16:9)',
    note: 'Hashtags zählen in die 280 Zeichen!'
  },
  linkedin: { 
    name: 'LinkedIn Beitrag', icon: FaLinkedin, color: '#0077B5', 
    maxLength: 3000, hasHeadline: false, headlineMax: 0, 
    hashtags: 5, imageLimit: 20, image: '1200×627 (1.91:1)'
  },
  instagram: { 
    name: 'Instagram', icon: FaInstagram, color: '#E4405F', 
    maxLength: 2200, hasHeadline: false, headlineMax: 0, 
    hashtags: 5, imageLimit: 10, image: '1080×1350 (4:5)'
  },
  facebook: { 
    name: 'Facebook', icon: FaFacebook, color: '#1877F2', 
    maxLength: 5000, hasHeadline: false, headlineMax: 0, 
    hashtags: 3, imageLimit: 10, image: '1200×630 (1.91:1)'
  },
  pinterest: { 
    name: 'Pinterest', icon: FaPinterest, color: '#BD081C', 
    maxLength: 320, hasHeadline: true, headlineMax: 28, 
    hashtags: 5, imageLimit: 5, image: '1000×1500 (2:3)'
  },
  medium: { 
    name: 'Medium', icon: FaMedium, color: '#000000', 
    maxLength: 4000, hasHeadline: true, headlineMax: 67, 
    hashtags: 0, imageLimit: 10, image: '1200×628 (1.91:1)'
  },
  blog: { 
    name: 'Blogartikel', icon: FaBlog, color: '#00D9C8', 
    maxLength: 2800, hasHeadline: true, headlineMax: 60, 
    hashtags: 0, imageLimit: 10, image: '1200×628 (1.91:1)'
  },
  newsletter: { 
    name: 'Newsletter', icon: FaEnvelope, color: '#F59E0B', 
    maxLength: 600, hasHeadline: true, headlineMax: 70, 
    hashtags: 0, imageLimit: 0, image: null
  },
  pressemitteilung: { 
    name: 'Pressemitteilung', icon: FaNewspaper, color: '#6B7280', 
    maxLength: 5000, hasHeadline: true, headlineMax: 57, 
    hashtags: 0, imageLimit: 1, image: '1200×628 (1.91:1)',
    structured: true
  },
  youtube: {
    name: 'YouTube', icon: FaYoutube, color: '#FF0000',
    maxLength: 450, hasHeadline: true, headlineMax: 100,
    hashtags: 0, imageLimit: 1, image: '1920×1080 (16:9)',
    structured: true, isVideo: true
  },
  produktbeschreibung: {
    name: 'Produktbeschreibung', icon: FaBox, color: '#1e9ead',
    maxLength: 6000, hasHeadline: true, headlineMax: 80,
    hashtags: 0, imageLimit: 1, image: '1200×900 (4:3)',
    structured: true
  },
  // Legacy alias
  twitter: { 
    name: 'X', icon: FaTwitter, color: '#000000', 
    maxLength: 280, hasHeadline: false, headlineMax: 0, 
    hashtags: 3, imageLimit: 4, image: '1200×675 (16:9)'
  }
};

// Platform list for filter dropdown
const platforms = Object.entries(PLATFORM_CONFIG)
  .filter(([key]) => key !== 'twitter') // Exclude legacy alias
  .map(([key, config]) => ({
    key,
    label: config.name,
    icon: config.icon,
    color: config.color,
    limit: config.maxLength
  }));

// Pressemitteilung field configuration
const PRESS_RELEASE_FIELDS_DE = [
  { key: 'headline', label: 'Überschrift', max: 57 },
  { key: 'subtitle', label: 'Untertitel', max: 150, multiline: true },
  { key: 'short_text', label: 'Kurztext zur Meldung', max: 800, multiline: true },
  { key: 'body_text', label: 'Meldungstext', max: 3200, multiline: true },
  { key: 'keywords', label: 'Schlüsselwörter', max: 200, optional: true, multiline: true },
  { key: 'company_description', label: 'Firmenbeschreibung', max: 800, multiline: true },
];
const PRESS_RELEASE_FIELDS_EN = [
  { key: 'headline', label: 'Headline', max: 57 },
  { key: 'subtitle', label: 'Subtitle', max: 150, multiline: true },
  { key: 'short_text', label: 'Short Summary', max: 800, multiline: true },
  { key: 'body_text', label: 'Body Text', max: 3200, multiline: true },
  { key: 'keywords', label: 'Keywords', max: 200, optional: true, multiline: true },
  { key: 'company_description', label: 'Company Description', max: 800, multiline: true },
];

const statusConfig_de = {
  draft: { label: 'Entwurf', color: 'bg-yellow-500/20 text-yellow-500' },
  review: { label: 'Zur Review', color: 'bg-blue-500/20 text-blue-500' },
  approved: { label: 'Freigegeben', color: 'bg-green-500/20 text-green-500' },
  rejected: { label: 'Abgelehnt', color: 'bg-red-500/20 text-red-500' },
  video_generation: { label: 'Zur Generierung', color: 'bg-purple-500/20 text-purple-500' },
  published: { label: 'Gepostet', color: 'bg-[#00D9C8]/20 text-[#00D9C8]' },
  generating: { label: 'Wird generiert…', color: 'bg-[#00D9C8]/20 text-[#00D9C8]' },
};
const statusConfig_en = {
  draft: { label: 'Draft', color: 'bg-yellow-500/20 text-yellow-500' },
  review: { label: 'In Review', color: 'bg-blue-500/20 text-blue-500' },
  approved: { label: 'Approved', color: 'bg-green-500/20 text-green-500' },
  rejected: { label: 'Rejected', color: 'bg-red-500/20 text-red-500' },
  video_generation: { label: 'Generating', color: 'bg-purple-500/20 text-purple-500' },
  published: { label: 'Published', color: 'bg-[#00D9C8]/20 text-[#00D9C8]' },
  generating: { label: 'Generating…', color: 'bg-[#00D9C8]/20 text-[#00D9C8]' },
};

const statusTabs_de = [
  { key: 'all', label: 'Alle' },
  { key: 'draft', label: 'Entwurf' },
  { key: 'review', label: 'Zur Review' },
  { key: 'approved', label: 'Freigegeben' },
  { key: 'published', label: 'Gepostet' },
  { key: 'rejected', label: 'Abgelehnt' },
  { key: 'video_generation', label: 'Zur Generierung' },
];
const statusTabs_en = [
  { key: 'all', label: 'All' },
  { key: 'draft', label: 'Draft' },
  { key: 'review', label: 'In Review' },
  { key: 'approved', label: 'Approved' },
  { key: 'published', label: 'Published' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'video_generation', label: 'Generating' },
];

const Produktion = () => {
  const { getAuthHeader, activeProjectId, isProjectReady, user: authUser } = useAuth();
  const { askLogo, logoDialog } = useLogoPrompt({ apiBase: API, getAuthHeader });
  const { t, lang } = useLanguage();
  const subscription = useSubscription();
  const [showVideoGate, setShowVideoGate] = useState(false);
  
  const statusConfig = lang === 'de' ? statusConfig_de : statusConfig_en;
  const statusTabs = lang === 'de' ? statusTabs_de : statusTabs_en;
  const PRESS_RELEASE_FIELDS = lang === 'de' ? PRESS_RELEASE_FIELDS_DE : PRESS_RELEASE_FIELDS_EN;
  const [searchParams, setSearchParams] = useSearchParams();
  // "30 Days Ahead": Durchklick-Modus für die Beiträge eines Plans (?plan=<id>)
  const [planParam] = useState(() => new URLSearchParams(window.location.search).get('plan'));
  const [planNav, setPlanNav] = useState(null);          // { id, items, counts, plan_status }
  const [planBarDate, setPlanBarDate] = useState('');
  const [planBarTime, setPlanBarTime] = useState('');
  const planQuickRef = useRef(false);
  const planOpenedRef = useRef(false);
  const selectedContentRef = useRef(null);
  const [content, setContent] = useState([]);
  const [filteredContent, setFilteredContent] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [platformFilter, setPlatformFilter] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [selectedContent, setSelectedContent] = useState(null);
  const [activeTab, setActiveTab] = useState('text');
  const [versions, setVersions] = useState([]);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editedText, setEditedText] = useState('');
  const [blogTitle, setBlogTitle] = useState('');
  const [blogSubtitle, setBlogSubtitle] = useState('');
  const [editedMeta, setEditedMeta] = useState({});
  const [gallery, setGallery] = useState([]);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [additionalPlatforms, setAdditionalPlatforms] = useState([]);
  const [deletedContent, setDeletedContent] = useState(null);
  
  // Image selection state
  const [selectedImages, setSelectedImages] = useState([]);
  
  // Press release (Pressemitteilung) structured content state
  const [editedStructuredContent, setEditedStructuredContent] = useState({
    headline: '',
    subtitle: '',
    short_text: '',
    body_text: '',
    keywords: '',
    company_description: ''
  });
  
  // Comments state
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [isPostingComment, setIsPostingComment] = useState(false);
  
  // New state for scheduling flow
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const optionsRef = useRef(null);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [suggestedReason, setSuggestedReason] = useState('');
  const [isLoadingSuggestion, setIsLoadingSuggestion] = useState(false);
  const [isScheduling, setIsScheduling] = useState(false);
  // Blog → Website destination flow in the schedule modal.
  const [blogPublishWhen, setBlogPublishWhen] = useState('scheduled'); // 'now' | 'scheduled'
  // Blog publish destination — a strict either/or between the connected targets.
  // status: null = still loading, true/false = whether that target is connected.
  const [blogTargetStatus, setBlogTargetStatus] = useState({ lovable: null, wordpress: null, shopify: null });
  const [blogTarget, setBlogTarget] = useState(null); // 'lovable' | 'wordpress' | 'shopify' | 'github:<id>' | null
  const [ghTargets, setGhTargets] = useState([]);
  const [blogTranslationOf, setBlogTranslationOf] = useState('');
  const [showPlatformAdaptModal, setShowPlatformAdaptModal] = useState(false);
  const [isAdapting, setIsAdapting] = useState(false);
  const [scheduledContentId, setScheduledContentId] = useState(null);
  const [generatingImageId, setGeneratingImageId] = useState(null);
  // pendingImagePieces: contentId -> { startedAt, prevImageCount, kind }
  // Tracks pieces whose image generation/edit is in flight so the UI can
  // (a) show a "Generation in process…" badge and (b) silently poll for the
  // result even if the HTTP call was dropped by the K8s 60s ingress timeout.
  const pendingImagePiecesRef = useRef(new Map());
  const [pendingImageVersion, setPendingImageVersion] = useState(0); // bump to re-render

  // ── Async completion toasts (regenerate-text / adapt-platforms / refine) ──
  // These endpoints now return immediately (status='generating') and finish in
  // a backend BackgroundTask, so the K8s 60s ingress can no longer drop them.
  // We track the returned content_id here and let the 3s poller fire a single
  // "fertig"-toast when the piece transitions generating→done.
  //   Map<content_id, { kind: 'text' | 'image', platform }>
  const pendingToastsRef = useRef(new Map());
  const trackCompletionToast = (id, meta = {}) => {
    if (!id) return;
    pendingToastsRef.current.set(id, { kind: meta.kind || 'text', platform: meta.platform || '' });
  };
  const [refineInstruction, setRefineInstruction] = useState('');
  const [refineImageInstruction, setRefineImageInstruction] = useState('');
  const refineImageInputRef = useRef(null);
  const [editImageInstruction, setEditImageInstruction] = useState('');
  const [editingImageUrl, setEditingImageUrl] = useState(null);
  const [imageAiMode, setImageAiMode] = useState('prompt');
  const [isEditingImage, setIsEditingImage] = useState(false);
  const editImageInputRef = useRef(null);
  const blogViewerSidebarRef = useRef(null);
  const blogViewerExpandedRef = useRef(null);
  const productViewerSidebarRef = useRef(null);
  const productViewerExpandedRef = useRef(null);
  const [isRefining, setIsRefining] = useState(false);
  const [refineType, setRefineType] = useState('text');
  const [enabledPlatforms, setEnabledPlatforms] = useState(null);
  const [customChannels, setCustomChannels] = useState([]);
  const [platformCharLimits, setPlatformCharLimits] = useState({});
  
  // Conflict dialog state (duplicate schedule detection)
  const [showConflictDialog, setShowConflictDialog] = useState(false);
  const [conflictData, setConflictData] = useState(null);

  // Multi-select & bulk delete state
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Expanded modal state
  const [showExpandedModal, setShowExpandedModal] = useState(false);

  // Tags & Folders state
  const [tags, setTags] = useState([]);
  const [folders, setFolders] = useState([]);
  const [activeFolder, setActiveFolder] = useState(null); // null = all
  const [activeTagFilter, setActiveTagFilter] = useState(null);
  const [showTagManager, setShowTagManager] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#00D9C8');
  const [newFolderName, setNewFolderName] = useState('');
  const [showNewFolderInput, setShowNewFolderInput] = useState(false);
  const [editingFolderId, setEditingFolderId] = useState(null);
  const [editingFolderName, setEditingFolderName] = useState('');
  const [showFolderMoveModal, setShowFolderMoveModal] = useState(false);
  const [movingContentId, setMovingContentId] = useState(null);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [bulkDeletedIds, setBulkDeletedIds] = useState(null);
  const [undoCountdown, setUndoCountdown] = useState(0);
  const [showImageStyleModal, setShowImageStyleModal] = useState(false);

  // Filter platforms based on user's enabled platforms config
  const activePlatforms = (() => {
    if (enabledPlatforms === null || enabledPlatforms.length === 0) return platforms;
    const filtered = platforms.filter(p => enabledPlatforms.includes(p.key));
    customChannels.filter(c => enabledPlatforms.includes(c.id)).forEach(c => {
      const mainField = c.fields?.[0];
      filtered.push({
        key: c.id, label: c.name, icon: Layers, color: '#A0AEC0',
        limit: mainField?.maxLength || 500
      });
    });
    return filtered;
  })();

  // Get platform configuration
  const getPlatformConfig = (platform) => {
    if (PLATFORM_CONFIG[platform]) return PLATFORM_CONFIG[platform];
    // Check custom channels
    const cc = customChannels.find(c => c.id === platform);
    if (cc) {
      const mainField = cc.fields?.[0];
      return {
        name: cc.name, icon: Layers, color: '#A0AEC0',
        maxLength: mainField?.maxLength || 500, hasHeadline: cc.fields?.length > 1,
        headlineMax: cc.fields?.[1]?.maxLength || 100,
        hashtags: 0, imageLimit: 1, image: null
      };
    }
    return PLATFORM_CONFIG.linkedin;
  };

  // Get max images for current platform
  const getMaxImages = () => {
    if (!selectedContent) return 1;
    const config = getPlatformConfig(selectedContent.platform);
    return config.imageLimit || 1;
  };

  // Bug B fix: append a URL to selectedImages ONLY if it isn't already present.
  // The membership check runs inside the functional updater against `prev`, so
  // two racing callers (the 3s image poller + the direct/timeout-recheck
  // handler) can no longer both push the same asset — the previous
  // length-only guard let duplicates through (LinkedIn max=9), which shipped
  // the same image twice and made a single delete remove "both".
  const addSelectedImageUnique = (url) => {
    if (!url) return;
    const max = getMaxImages();
    setSelectedImages(prev =>
      (prev.includes(url) || prev.length >= max) ? prev : [...prev, url]
    );
  };

  // Get character limit for current platform
  const getCharLimit = () => {
    if (!selectedContent) return 3000;
    const config = getPlatformConfig(selectedContent.platform);
    const userLimit = platformCharLimits[selectedContent.platform];
    if (userLimit && userLimit > 0) return userLimit;
    // Blog articles are long-form by nature — the platform config's 2800
    // default is only a soft hint for the char counter, NOT a hard publish
    // gate. Only enforce a hard limit when the user explicitly configured one
    // via platformCharLimits. Everything else stays capped as before.
    if (selectedContent.platform === 'blog' || (selectedContent.platform === 'newsletter' && selectedContent.blog_structured)) return Infinity;
    return config.maxLength || 3000;
  };

  // Toggle image selection
  const toggleImageSelection = (imageUrl) => {
    const maxImages = getMaxImages();
    
    if (selectedImages.includes(imageUrl)) {
      // Remove from selection
      setSelectedImages(prev => prev.filter(img => img !== imageUrl));
    } else {
      // Add to selection if under limit
      if (selectedImages.length < maxImages) {
        setSelectedImages(prev => [...prev, imageUrl]);
      } else {
        const config = getPlatformConfig(selectedContent.platform);
        toast.error(`Max. ${maxImages} image${maxImages !== 1 ? 's' : ''} for ${config.name}`);
      }
    }
  };

  useEffect(() => {
    // Wait until AuthContext resolved active project – otherwise we may fire
    // requests with a stale project_id from localStorage before backend
    // confirms the definitive active project.
    if (!isProjectReady || !activeProjectId) return;
    const controller = new AbortController();
    const signal = controller.signal;

    // Silent refetch whenever the project changes AFTER we already have
    // content in memory — keeps the current list visible during background
    // refresh (e.g. credits update, generation polling side-effects) instead
    // of clearing the UI to a loader flash.
    const isRefetch = content.length > 0;
    fetchContent(signal, { silent: isRefetch });
    fetchGallery(signal);
    fetchTagsAndFolders(signal);
    // Fetch platform config for filtering
    axios.get(`${API}/platform-config`, { headers: getAuthHeader(), signal })
      .then(r => {
        setEnabledPlatforms(r.data.enabled_platforms || []);
        setCustomChannels(r.data.custom_channels || []);
        setPlatformCharLimits(r.data.platform_char_limits || {});
      })
      .catch((err) => {
        if (axios.isCancel?.(err) || err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProjectId, isProjectReady]);

  // Open content piece from URL parameter
  useEffect(() => {
    const contentId = searchParams.get('content');
    if (contentId && content.length > 0 && !selectedContent) {
      const contentPiece = content.find(c => c.id === contentId);
      if (contentPiece) {
        openDetailPanel(contentPiece);
        // Clear the URL parameter
        setSearchParams({});
      }
    }
  }, [content, searchParams]);

  useEffect(() => {
    filterContent();
  }, [content, searchQuery, statusFilter, platformFilter, activeFolder, activeTagFilter]);

  // ── Pending-image polling helpers ──────────────────────────────────────────
  // markImagePending(id, prevCount, kind): start tracking + show badge
  // unmarkImagePending(id): stop tracking + hide badge
  // pollPendingImages(): every 3s, check each pending piece in DB; when
  //   image_urls grew → apply update silently, remove from map.
  // Hard timeout 4 min per piece: silent removal + calm hint toast.
  const POLL_TIMEOUT_MS = 4 * 60 * 1000;

  const markImagePending = (contentId, prevImageCount, kind = 'generate') => {
    pendingImagePiecesRef.current.set(contentId, {
      startedAt: Date.now(),
      prevImageCount,
      kind,
    });
    setPendingImageVersion(v => v + 1);
  };
  const unmarkImagePending = (contentId) => {
    if (pendingImagePiecesRef.current.delete(contentId)) {
      setPendingImageVersion(v => v + 1);
    }
  };
  const isImagePending = (contentId) =>
    pendingImagePiecesRef.current.has(contentId);

  useEffect(() => {
    // Background poller: scan all pending pieces every 3s.
    // Uses the /image-status endpoint (lightweight, backed by
    // content_pieces.image_generation_status) so a real backend failure
    // surfaces as a toast instead of a badge that spins until the 4-min
    // hard timeout hits.
    const interval = setInterval(async () => {
      // ── Async completion toasts (regenerate-text / adapt / refine) ──
      // Runs BEFORE the image-pending early-return so it fires even when no
      // image is generating. Only IDs explicitly registered in pendingToastsRef
      // ever trigger a toast.
      const toastEntries = Array.from(pendingToastsRef.current.entries());
      for (const [tid, meta] of toastEntries) {
        try {
          const cr = await axios.get(`${API}/content/${tid}`, { headers: getAuthHeader() });
          const piece = cr.data?.content;
          if (!piece) { pendingToastsRef.current.delete(tid); continue; }

          // Completion detection depends on the kind of work we're waiting for.
          // - 'text'  (regenerate-text / adapt): piece.status left 'generating'
          // - 'image' (refine image): image_generation_status resolved
          const isTextKind = meta.kind !== 'image';
          const done = isTextKind
            ? (piece.status && piece.status !== 'generating')
            : (piece.image_generation_status === 'success' || piece.image_generation_status === 'error');
          if (!done) continue;

          const isErr = isTextKind
            ? (piece.status === 'failed')
            : (piece.image_generation_status === 'error');

          pendingToastsRef.current.delete(tid);

          // Image-refine errors are already surfaced by the /image-status poller
          // (markImagePending) — don't double-toast. Just stop tracking.
          if (!isTextKind && isErr) continue;

          // Silently fold the finished piece into local state.
          setContent(prev => prev.map(c => (c.id === tid ? { ...c, ...piece } : c)));
          setSelectedContent(prev => (prev && prev.id === tid ? { ...prev, ...piece } : prev));
          // The open detail panel keeps its own editor state (text, meta, …)
          // that was initialised from the empty placeholder — re-initialise it
          // from the finished piece, otherwise the panel stays blank until reload.
          if (!isErr && isTextKind && selectedContent?.id === tid) openDetailPanel(piece);

          if (isErr) {
            toast.error(lang === 'de'
              ? 'Generierung fehlgeschlagen – bitte erneut versuchen.'
              : 'Generation failed – please try again.');
          } else {
            const isBlog = piece.platform === 'blog';
            const msg = isBlog
              ? (lang === 'de' ? 'Blogartikel ist fertig' : 'Blog article is ready')
              : (lang === 'de' ? 'Inhalt ist fertig' : 'Content is ready');
            toast.success(msg, {
              duration: 10000,
              action: {
                label: lang === 'de' ? 'Ansehen' : 'View',
                onClick: () => openDetailPanel(piece),
              },
            });
          }
        } catch { /* network/race — retry next tick */ }
      }

      const entries = Array.from(pendingImagePiecesRef.current.entries());
      if (entries.length === 0) return;
      for (const [cid, info] of entries) {
        // Hard timeout
        if (Date.now() - info.startedAt > POLL_TIMEOUT_MS) {
          unmarkImagePending(cid);
          toast.info('Generierung hat lange gedauert und konnte nicht abgeschlossen werden – bitte erneut versuchen. Dein Kontingent wird nur bei Erfolg verbraucht.', {
            duration: 8000,
            style: { background: '#0E2D2C', color: '#E2F8F6', borderLeft: '4px solid #00D9C8' },
          });
          continue;
        }
        try {
          const r = await axios.get(`${API}/content/${cid}/image-status`, { headers: getAuthHeader() });
          const st = r.data?.status;
          const imgUrls = r.data?.image_urls || [];
          const newCount = imgUrls.length;

          // Success path: image list grew → apply update silently.
          if (newCount > info.prevImageCount) {
            setContent(prev => prev.map(c =>
              c.id === cid ? { ...c, image_urls: imgUrls } : c
            ));
            setSelectedContent(prev => prev && prev.id === cid
              ? { ...prev, image_urls: imgUrls }
              : prev
            );
            const newest = imgUrls[imgUrls.length - 1];
            if (newest && selectedContent?.id === cid) {
              addSelectedImageUnique(newest);
            }
            unmarkImagePending(cid);
            if (typeof subscription?.refetch === 'function') subscription.refetch();
            continue;
          }

          // Real backend error → surface immediately, stop polling.
          if (st === 'error') {
            unmarkImagePending(cid);
            const errMsg = r.data?.error || t.produktion.imageGen.unknownError;
            toast.error(t.produktion.imageGen.failed.replace('{msg}', errMsg), {
              duration: 10000,
            });
            continue;
          }
          // queued / running / idle → keep polling
        } catch { /* network/race — try again next tick */ }
      }
    }, 3000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedContent?.id]);

  // Generic "generating" watcher: ANY piece sitting at status='generating'
  // (adapted here, created in Kreation, regenerated, opened right after
  // creation, …) is polled until it finishes and then folded into the list —
  // and into the open detail panel — without a reload. IDs already tracked by
  // the completion-toast poller above are skipped (it handles those itself).
  const generatingIds = content.filter(c => c.status === 'generating').map(c => c.id).join(',');
  const selectedIdRef = useRef(null);
  selectedIdRef.current = selectedContent?.id || null;
  useEffect(() => {
    if (!generatingIds) return undefined;
    let cancelled = false;
    const tick = async () => {
      for (const id of generatingIds.split(',').slice(0, 20)) {
        if (cancelled) return;
        if (pendingToastsRef.current.has(id)) continue;
        try {
          const r = await axios.get(`${API}/content/${id}`, { headers: getAuthHeader() });
          const piece = r.data?.content;
          if (cancelled || !piece || piece.status === 'generating') continue;
          setContent(prev => prev.map(c => (c.id === id ? { ...c, ...piece } : c)));
          if (selectedIdRef.current === id) {
            setSelectedContent(prev => (prev && prev.id === id ? { ...prev, ...piece } : prev));
            if (piece.status !== 'failed') openDetailPanel(piece);
          }
        } catch { /* network/race — retry next tick */ }
      }
    };
    const interval = setInterval(tick, 3000);
    return () => { cancelled = true; clearInterval(interval); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generatingIds]);

  const fetchContent = async (signal, { silent = false } = {}) => {
    // Only show the loader on the initial fetch (or explicit non-silent
    // refetches). Silent refetches keep the previous content visible until
    // the new list arrives — no blank flash while generation polling / project
    // updates trigger a refresh in the background.
    if (!silent) setIsLoading(true);
    try {
      const response = await axios.get(`${API}/content`, {
        headers: getAuthHeader(),
        signal,
      });
      setContent(response.data.content_pieces || []);
    } catch (err) {
      if (axios.isCancel?.(err) || err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
      toast.error(t.common.toasts.loadContentFailed);
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  const [retryingIds, setRetryingIds] = useState(new Set());

  const handleRetryGeneration = async (e, item) => {
    // Card wrapper opens the detail panel — stop that on retry click.
    e?.stopPropagation?.();
    if (!item?.id || item.status !== 'failed') return;
    setRetryingIds(prev => {
      const next = new Set(prev);
      next.add(item.id);
      return next;
    });
    // Optimistically flip the local card to `generating` so the UI feels
    // instantaneous even before the backend confirms.
    setContent(prev => prev.map(c => (
      c.id === item.id
        ? { ...c, status: 'generating', title: t.produktion.retry.generatingTitle, error_message: null, error_debug: null }
        : c
    )));
    try {
      await axios.post(`${API}/content/${item.id}/retry`, {}, { headers: getAuthHeader() });
      toast.success(t.produktion.retry.restarted);
      // Poll every 4s for up to ~4 min until the piece flips out of
      // `generating`, then refresh the list once so the new draft shows up.
      let attempts = 0;
      const maxAttempts = 60;
      const poll = setInterval(async () => {
        attempts += 1;
        try {
          const { data } = await axios.get(`${API}/content`, { headers: getAuthHeader() });
          const updated = (data?.content_pieces || []).find(p => p.id === item.id);
          if (updated && updated.status !== 'generating') {
            clearInterval(poll);
            setContent(data.content_pieces || []);
            setRetryingIds(prev => {
              const next = new Set(prev);
              next.delete(item.id);
              return next;
            });
            if (updated.status === 'failed') {
              toast.error(t.produktion.retry.failedAgain);
            } else {
              toast.success(t.produktion.retry.ready);
            }
          }
        } catch (_e) {
          /* keep polling — network hiccups are non-fatal */
        }
        if (attempts >= maxAttempts) {
          clearInterval(poll);
          setRetryingIds(prev => {
            const next = new Set(prev);
            next.delete(item.id);
            return next;
          });
        }
      }, 4000);
    } catch (err) {
      const msg = err?.response?.data?.detail || t.produktion.retry.couldNotStart;
      toast.error(msg);
      setContent(prev => prev.map(c => (
        c.id === item.id ? { ...c, status: 'failed' } : c
      )));
      setRetryingIds(prev => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    }
  };

  const fetchGallery = async (signal) => {
    try {
      const response = await axios.get(`${API}/gallery`, {
        headers: getAuthHeader(),
        signal,
      });
      setGallery(response.data.gallery || []);
    } catch (err) {
      if (axios.isCancel?.(err) || err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch gallery:', err);
    }
  };

  const fetchTagsAndFolders = async (signal) => {
    try {
      const [tagsRes, foldersRes] = await Promise.all([
        axios.get(`${API}/tags`, { headers: getAuthHeader(), signal }),
        axios.get(`${API}/folders`, { headers: getAuthHeader(), signal })
      ]);
      setTags(tagsRes.data.tags || []);
      setFolders(foldersRes.data.folders || []);
    } catch (err) {
      if (axios.isCancel?.(err) || err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch tags/folders:', err);
    }
  };

  const filterContent = () => {
    let filtered = [...content];

    if (searchQuery) {
      const query = searchQuery.toLowerCase().trim();
      
      // Check if searching for a content number (e.g., "#5", "5", "#12")
      const numberMatch = query.match(/^#?(\d+)$/);
      
      filtered = filtered.filter(c => {
        // Search by content number
        if (numberMatch) {
          const searchNum = parseInt(numberMatch[1], 10);
          if (c.content_number === searchNum) return true;
        }
        
        // Search in title
        if (c.title?.toLowerCase().includes(query)) return true;
        
        // Search in text content
        if (c.text_content?.toLowerCase().includes(query)) return true;
        
        // Search in platform
        if (c.platform?.toLowerCase().includes(query)) return true;
        
        // Search in goal
        if (c.goal?.toLowerCase().includes(query)) return true;
        
        // Search in target audience
        if (c.target_audience?.toLowerCase().includes(query)) return true;
        
        // Search in SEO metadata (keywords, description)
        if (c.seo_metadata) {
          if (c.seo_metadata.keywords?.some(kw => kw.toLowerCase().includes(query))) return true;
          if (c.seo_metadata.meta_description?.toLowerCase().includes(query)) return true;
          if (c.seo_metadata.primary_keyword?.toLowerCase().includes(query)) return true;
        }
        
        // Search in structured content (Pressemitteilung)
        if (c.structured_content) {
          if (c.structured_content.headline?.toLowerCase().includes(query)) return true;
          if (c.structured_content.subheadline?.toLowerCase().includes(query)) return true;
          if (c.structured_content.lead?.toLowerCase().includes(query)) return true;
        }

        // Search in tags
        if (c.tags?.some(t => t.toLowerCase().includes(query))) return true;
        
        return false;
      });
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(c => c.status === statusFilter);
    }

    if (platformFilter) {
      filtered = filtered.filter(c => c.platform === platformFilter);
    }

    if (activeFolder) {
      filtered = filtered.filter(c => c.folder_id === activeFolder);
    }

    if (activeTagFilter) {
      filtered = filtered.filter(c => c.tags?.includes(activeTagFilter));
    }

    setFilteredContent(filtered);
  };

  // ── Plan-Durchklick (30 Days Ahead) ────────────────────────────────────────
  const loadPlanNav = async () => {
    if (!planParam) return null;
    try {
      const { data } = await axios.get(`${API}/content-plan/${planParam}/pieces`, { headers: getAuthHeader() });
      setPlanNav({ id: planParam, ...data });
      return data;
    } catch (e) {
      return null;
    }
  };

  const openPlanItem = async (item) => {
    if (!item?.content_id) return;
    try {
      const { data } = await axios.get(`${API}/content/${item.content_id}`, { headers: getAuthHeader() });
      const piece = data.content;
      setContent(prev => (prev.some(c => c.id === piece.id) ? prev.map(c => (c.id === piece.id ? piece : c)) : [piece, ...prev]));
      openDetailPanel(piece);
    } catch (e) {
      toast.error(lang === 'de' ? 'Beitrag konnte nicht geladen werden' : 'Could not load the post');
    }
  };

  const goPlan = (delta) => {
    if (!planNav || !selectedContent) return;
    let i = planNav.items.findIndex(x => x.content_id === selectedContent.id) + delta;
    while (i >= 0 && i < planNav.items.length && !planNav.items[i].content_id) i += delta;
    if (planNav.items[i]) openPlanItem(planNav.items[i]);
  };

  useEffect(() => { selectedContentRef.current = selectedContent; }, [selectedContent]);

  useEffect(() => {
    if (planParam && isProjectReady) loadPlanNav();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planParam, activeProjectId, isProjectReady]);

  // Beim ersten Laden den ersten offenen Beitrag des Plans öffnen
  useEffect(() => {
    if (!planNav || planOpenedRef.current) return;
    const first = planNav.items.find(i => i.content_id && i.status === 'draft') || planNav.items.find(i => i.content_id);
    if (first) { planOpenedRef.current = true; openPlanItem(first); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planNav]);

  // Solange Beiträge noch erzeugt werden: Liste aktualisieren, offenen Beitrag nachladen sobald fertig
  useEffect(() => {
    if (!planNav || !(planNav.plan_status === 'generating' || planNav.counts?.generating > 0)) return undefined;
    const timer = setInterval(async () => {
      const d = await loadPlanNav();
      const cur = selectedContentRef.current;
      if (d && cur?.status === 'generating') {
        const it = d.items.find(i => i.content_id === cur.id);
        if (it && it.status !== 'generating') openPlanItem(it);
      }
    }, 6000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planNav?.plan_status, planNav?.counts?.generating]);

  // Datum/Uhrzeit des geöffneten Plan-Beitrags in die Leiste übernehmen
  useEffect(() => {
    if (!planNav || !selectedContent || selectedContent.plan_id !== planNav.id) return;
    const local = utcToLocalInput(selectedContent.scheduled_at);
    if (local) { setPlanBarDate(local.slice(0, 10)); setPlanBarTime(local.slice(11, 16)); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedContent?.id, planNav?.id]);

  const openDetailPanel = async (contentPiece) => {
    setSelectedContent(contentPiece);
    setEditedText(contentPiece.text_content || '');
    setBlogTitle(contentPiece.content_headline || '');
    setBlogSubtitle(contentPiece.content_subtitle || '');
    setEditedMeta({
      platform: contentPiece.platform,
      target_audience: contentPiece.target_audience || '',
      goal: contentPiece.goal || '',
      scheduled_at: contentPiece.scheduled_at || '',
      source_info: contentPiece.source_info || ''
    });
    setActiveTab('text');
    // Initialize selected images: prefer persisted selection, fall back to all images
    setSelectedImages(contentPiece.selected_image_urls ?? contentPiece.image_urls ?? []);
    
    // Initialize structured content for Pressemitteilung or Pinterest
    if (contentPiece.platform === 'pressemitteilung' && contentPiece.structured_content) {
      setEditedStructuredContent(contentPiece.structured_content);
    } else if (contentPiece.platform === 'produktbeschreibung' && contentPiece.structured_content) {
      setEditedStructuredContent(contentPiece.structured_content);
    } else if (contentPiece.platform === 'pinterest') {
      if (contentPiece.structured_content && contentPiece.structured_content.pin_title) {
        setEditedStructuredContent(contentPiece.structured_content);
      } else {
        // Old content without structured pin_title — auto-generate via LLM
        const text = contentPiece.text_content || '';
        setEditedStructuredContent({
          pin_title: '',
          pin_description: text.slice(0, 500)
        });
        if (text) {
          axios.post(`${API}/content/${contentPiece.id}/generate-pin-title`, {}, {
            headers: getAuthHeader()
          }).then(res => {
            setEditedStructuredContent({
              pin_title: res.data.pin_title || '',
              pin_description: res.data.pin_description || text.slice(0, 500)
            });
            // Update content in list and selected
            setContent(prev => prev.map(c => 
              c.id === contentPiece.id ? { ...c, structured_content: res.data.structured_content } : c
            ));
            setSelectedContent(prev => prev ? { ...prev, structured_content: res.data.structured_content } : prev);
          }).catch(() => {});
        }
      }
    } else if (contentPiece.platform === 'youtube') {
      if (contentPiece.structured_content && contentPiece.structured_content.youtube_script) {
        setEditedStructuredContent(contentPiece.structured_content);
      } else {
        setEditedStructuredContent({
          youtube_title: contentPiece.content_headline || '',
          youtube_script: contentPiece.text_content || '',
          youtube_description: '',
          youtube_tags: ''
        });
      }
    } else if (contentPiece.platform?.startsWith('custom_')) {
      // Custom channel: populate from custom_fields or fall back to text_content
      const cf = contentPiece.custom_fields || {};
      const initFields = {};
      // Try to get field config from customChannels
      const ccDef = customChannels.find(c => c.id === contentPiece.platform);
      if (ccDef && ccDef.fields) {
        ccDef.fields.forEach((field, idx) => {
          const val = cf[field.name];
          if (val !== undefined && val !== null) {
            initFields[`custom_${field.name}`] = val;
          } else if (idx === 0 && !Object.keys(cf).length && contentPiece.text_content) {
            // Fallback: first field gets text_content when custom_fields is empty
            initFields[`custom_${field.name}`] = contentPiece.text_content;
          } else {
            initFields[`custom_${field.name}`] = '';
          }
        });
      } else {
        // No channel definition found — put text_content in a generic field
        initFields['custom_Text'] = contentPiece.text_content || '';
      }
      setEditedStructuredContent(initFields);
    } else {
      setEditedStructuredContent({
        headline: '',
        subtitle: '',
        short_text: '',
        body_text: '',
        keywords: '',
        company_description: ''
      });
    }

    // Fetch versions
    try {
      const response = await axios.get(`${API}/content/${contentPiece.id}/versions`, {
        headers: getAuthHeader()
      });
      setVersions(response.data.versions || []);
    } catch (err) {
      console.error('Failed to fetch versions:', err);
    }
    
    // Fetch comments
    fetchComments(contentPiece.id);
  };

  const closeDetailPanel = () => {
    setSelectedContent(null);
    setVersions([]);
    setSelectedImages([]);
    setComments([]);
    setNewComment('');
    setEditedStructuredContent({
      headline: '',
      subtitle: '',
      short_text: '',
      body_text: '',
      keywords: '',
      company_description: ''
    });
  };

  // Comment functions
  const fetchComments = async (contentId) => {
    setIsLoadingComments(true);
    try {
      const response = await axios.get(`${API}/content/${contentId}/comments`, {
        headers: getAuthHeader()
      });
      setComments(response.data.comments || []);
    } catch (err) {
      console.error('Failed to fetch comments:', err);
    } finally {
      setIsLoadingComments(false);
    }
  };

  const handlePostComment = async () => {
    if (!selectedContent || !newComment.trim()) return;
    
    setIsPostingComment(true);
    try {
      const response = await axios.post(
        `${API}/content/${selectedContent.id}/comments`,
        { text: newComment.trim() },
        { headers: getAuthHeader() }
      );
      setComments(prev => [...prev, response.data.comment]);
      setNewComment('');
      toast.success(t.produktion.commentAdded);
    } catch (err) {
      toast.error(t.produktion.commentFailed);
    } finally {
      setIsPostingComment(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!selectedContent) return;
    
    try {
      await axios.delete(
        `${API}/content/${selectedContent.id}/comments/${commentId}`,
        { headers: getAuthHeader() }
      );
      setComments(prev => prev.filter(c => c.id !== commentId));
      toast.success(t.produktion.commentDeleted);
    } catch (err) {
      toast.error(t.common.toasts.deleteFailed);
    }
  };

  const formatCommentDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return t.produktion.justNow;
    if (diffMins < 60) return `vor ${diffMins} Min.`;
    if (diffHours < 24) return `vor ${diffHours} Std.`;
    if (diffDays < 7) return `vor ${diffDays} Tag${diffDays > 1 ? 'en' : ''}`;
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  // Tag management functions
  const handleCreateTag = async () => {
    if (!newTagName.trim()) return;
    try {
      const res = await axios.post(`${API}/tags`, { name: newTagName.trim(), color: newTagColor }, { headers: getAuthHeader() });
      setTags(prev => [...prev, res.data.tag]);
      setNewTagName('');
      toast.success(t.produktion.tagCreated);
    } catch (err) {
      toast.error(err.response?.data?.detail || t.common.toasts.genericError);
    }
  };

  const handleDeleteTag = async (tagId) => {
    try {
      await axios.delete(`${API}/tags/${tagId}`, { headers: getAuthHeader() });
      setTags(prev => prev.filter(t => t.id !== tagId));
      if (activeTagFilter) {
        const deleted = tags.find(t => t.id === tagId);
        if (deleted && activeTagFilter === deleted.name) setActiveTagFilter(null);
      }
      // Refresh content to reflect removed tags
      fetchContent(undefined, { silent: true });
      toast.success(t.produktion.tagDeleted);
    } catch (err) {
      toast.error(t.common.toasts.deleteFailed);
    }
  };

  const handleToggleTag = async (contentId, tagName) => {
    const piece = content.find(c => c.id === contentId);
    if (!piece) return;
    const currentTags = piece.tags || [];
    const newTags = currentTags.includes(tagName) 
      ? currentTags.filter(t => t !== tagName) 
      : [...currentTags, tagName];
    try {
      const res = await axios.put(`${API}/content/${contentId}`, { tags: newTags }, { headers: getAuthHeader() });
      setContent(prev => prev.map(c => c.id === contentId ? res.data.content : c));
      if (selectedContent?.id === contentId) {
        setSelectedContent(res.data.content);
      }
    } catch (err) {
      toast.error(t.common.toasts.tagError);
    }
  };

  // Folder management functions
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      const res = await axios.post(`${API}/folders`, { name: newFolderName.trim() }, { headers: getAuthHeader() });
      setFolders(prev => [...prev, res.data.folder]);
      setNewFolderName('');
      setShowNewFolderInput(false);
      toast.success(t.produktion.folderCreated);
    } catch (err) {
      toast.error(err.response?.data?.detail || t.common.toasts.genericError);
    }
  };

  const handleRenameFolder = async (folderId) => {
    if (!editingFolderName.trim()) return;
    try {
      const res = await axios.put(`${API}/folders/${folderId}`, { name: editingFolderName.trim() }, { headers: getAuthHeader() });
      setFolders(prev => prev.map(f => f.id === folderId ? { ...f, name: res.data.folder.name } : f));
      setEditingFolderId(null);
      toast.success(t.produktion.folderRenamed);
    } catch (err) {
      toast.error(t.common.toasts.genericError);
    }
  };

  const handleDeleteFolder = async (folderId) => {
    try {
      await axios.delete(`${API}/folders/${folderId}`, { headers: getAuthHeader() });
      setFolders(prev => prev.filter(f => f.id !== folderId));
      if (activeFolder === folderId) setActiveFolder(null);
      fetchContent(undefined, { silent: true });
      toast.success(t.produktion.folderDeleted);
    } catch (err) {
      toast.error(t.common.toasts.genericError);
    }
  };

  const handleMoveToFolder = async (contentId, folderId) => {
    try {
      const res = await axios.put(`${API}/content/${contentId}`, { folder_id: folderId || '' }, { headers: getAuthHeader() });
      setContent(prev => prev.map(c => c.id === contentId ? res.data.content : c));
      if (selectedContent?.id === contentId) setSelectedContent(res.data.content);
      fetchTagsAndFolders(); // refresh counts
      setShowFolderMoveModal(false);
      setMovingContentId(null);
      toast.success(folderId ? t.produktion.movedToFolder : t.produktion.removedFromFolder);
    } catch (err) {
      toast.error(t.common.toasts.genericError);
    }
  };

  const handleSaveText = async () => {
    if (!selectedContent) return;
    setIsSaving(true);

    try {
      const payload = { text_content: editedText };
      // Save blog title + subtitle
      if (selectedContent.platform === 'blog') {
        payload.content_headline = blogTitle;
        payload.content_subtitle = blogSubtitle;
      }
      const response = await axios.put(`${API}/content/${selectedContent.id}`, payload, {
        headers: getAuthHeader()
      });

      setSelectedContent(response.data.content);
      setContent(prev => prev.map(c => c.id === selectedContent.id ? response.data.content : c));
      toast.success(t.produktion.changesSaved);

      // Refresh versions
      const versionsRes = await axios.get(`${API}/content/${selectedContent.id}/versions`, {
        headers: getAuthHeader()
      });
      setVersions(versionsRes.data.versions || []);
    } catch (err) {
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setIsSaving(false);
    }
  };

  // Save custom channel fields
  const handleSaveCustomFields = async () => {
    if (!selectedContent) return;
    const cc = customChannels.find(c => c.id === selectedContent.platform);
    if (!cc) return;

    // Build custom_fields from editedStructuredContent
    const customFields = {};
    const textParts = [];
    for (const field of cc.fields) {
      const val = editedStructuredContent[`custom_${field.name}`] ?? selectedContent.custom_fields?.[field.name] ?? '';
      if (val.length > field.maxLength) {
        toast.error(`${field.name}: ${val.length}/${field.maxLength} Zeichen — zu lang!`);
        return;
      }
      customFields[field.name] = val;
      if (val) textParts.push(val);
    }

    setIsSaving(true);
    try {
      const response = await axios.put(`${API}/content/${selectedContent.id}`, {
        text_content: textParts.join('\n\n'),
        custom_fields: customFields
      }, { headers: getAuthHeader() });
      setSelectedContent(response.data.content);
      setContent(prev => prev.map(c => c.id === selectedContent.id ? response.data.content : c));
      toast.success(t.produktion.saved);
    } catch (err) {
      toast.error(t.produktion.saveFailed);
    } finally {
      setIsSaving(false);
    }
  };


  // Save structured content for Pressemitteilung or Pinterest
  const handleSaveStructuredContent = async () => {
    if (!selectedContent) return;
    
    // Validate Pinterest limits
    if (selectedContent.platform === 'pinterest') {
      if ((editedStructuredContent.pin_title || '').length > 100) {
        toast.error('Pin-Titel darf max. 100 Zeichen haben');
        return;
      }
      if ((editedStructuredContent.pin_description || '').length > 500) {
        toast.error('Pin-Beschreibung darf max. 500 Zeichen haben');
        return;
      }
    }
    
    // Validate YouTube limits
    if (selectedContent.platform === 'youtube') {
      if ((editedStructuredContent.youtube_title || '').length > 100) {
        toast.error('Video-Titel darf max. 100 Zeichen haben');
        return;
      }
      if ((editedStructuredContent.youtube_script || '').length > 450) {
        toast.error('Skript darf max. 450 Zeichen haben');
        return;
      }
    }
    
    setIsSaving(true);

    try {
      const response = await axios.put(`${API}/content/${selectedContent.id}`, {
        structured_content: editedStructuredContent
      }, {
        headers: getAuthHeader()
      });

      setSelectedContent(response.data.content);
      setContent(prev => prev.map(c => c.id === selectedContent.id ? response.data.content : c));
      toast.success(
        selectedContent.platform === 'pinterest' ? 'Pin gespeichert' : 
        selectedContent.platform === 'youtube' ? 'YouTube-Skript gespeichert' :
        'Pressemitteilung gespeichert'
      );
    } catch (err) {
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRegenerateText = async () => {
    if (!selectedContent) return;
    setIsRegenerating(true);

    const requestStartTime = Date.now();
    toast.info('To ensure the best quality, the generation can take up to 1 minute. You will be updated once it\'s done.', {
      duration: 8000,
      dismissible: true,
      style: { background: '#0E2D2C', color: '#E2F8F6', borderLeft: '4px solid #00D9C8' },
    });
    try {
      const response = await axios.post(`${API}/content/${selectedContent.id}/regenerate-text`, {}, {
        headers: getAuthHeader()
      });

      // ASYNC path: backend returns immediately with status='generating'.
      // Register the completion toast and let the 3s poller apply the result.
      if (response.data?.async) {
        const cid = selectedContent.id;
        trackCompletionToast(cid, { kind: 'text', platform: selectedContent.platform });
        setSelectedContent(prev => (prev && prev.id === cid ? { ...prev, status: 'generating' } : prev));
        setContent(prev => prev.map(c => (c.id === cid ? { ...c, status: 'generating' } : c)));
        return;
      }

      // Update text content
      setEditedText(response.data.text_content);
      setSelectedContent(prev => ({ 
        ...prev, 
        text_content: response.data.text_content,
        structured_content: response.data.structured_content || prev.structured_content
      }));
      setContent(prev => prev.map(c => 
        c.id === selectedContent.id ? { ...c, text_content: response.data.text_content } : c
      ));
      setVersions(prev => [response.data.version, ...prev]);
      
      // Update structured content if it's a press release, pinterest or youtube
      if ((selectedContent.platform === 'pressemitteilung' || selectedContent.platform === 'pinterest' || selectedContent.platform === 'youtube') && response.data.structured_content) {
        setEditedStructuredContent(response.data.structured_content);
      }
      
      toast.success(t.produktion.textRegenerated);
    } catch (err) {
      // K8s ingress (60s) can drop the HTTP connection before the backend
      // finishes the LLM call, but the backend keeps running and persists
      // the new text. Recheck the piece in DB; if updated_at advanced past
      // our request start, treat as success.
      // K8s ingress (60s) or upstream proxies (502/503/504) can drop the
      // connection while the backend keeps running. Treat all of these as
      // "may have succeeded" — recheck DB, stay silent on plain drops.
      const _status = err.response?.status;
      const _isDropOrGateway = err.code === 'ECONNABORTED' || !err.response || [502, 503, 504].includes(_status);
      if (_isDropOrGateway) {
        try {
          await new Promise(r => setTimeout(r, 2000));
          const recheck = await axios.get(`${API}/content/${selectedContent.id}`, { headers: getAuthHeader() });
          const piece = recheck.data?.content;
          const updatedTs = piece?.updated_at ? new Date(piece.updated_at).getTime() : 0;
          if (piece && (piece.text_content || '').trim() && updatedTs > requestStartTime) {
            setEditedText(piece.text_content);
            setSelectedContent(prev => ({
              ...prev,
              text_content: piece.text_content,
              structured_content: piece.structured_content || prev.structured_content
            }));
            setContent(prev => prev.map(c =>
              c.id === selectedContent.id ? { ...c, text_content: piece.text_content } : c
            ));
            if ((selectedContent.platform === 'pressemitteilung' || selectedContent.platform === 'pinterest' || selectedContent.platform === 'youtube') && piece.structured_content) {
              setEditedStructuredContent(piece.structured_content);
            }
            return;
          }
        } catch (_) { /* fall through */ }
        // No fresh content yet — stay quiet, content will appear on reload.
        return;
      }
      toast.error('Generierung braucht länger als gewohnt – bitte gleich erneut versuchen. Dein Kontingent wird nicht abgerechnet.');
    } finally {
      setIsRegenerating(false);
    }
  };

  const [regeneratingField, setRegeneratingField] = useState(null);
  const handleRegenerateField = async (fieldKey, fieldLabel) => {
    if (!selectedContent) return;
    setRegeneratingField(fieldKey);
    const requestStartTime = Date.now();
    toast.info('To ensure the best quality, the generation can take up to 1 minute. You will be updated once it\'s done.', {
      duration: 8000,
      dismissible: true,
      style: { background: '#0E2D2C', color: '#E2F8F6', borderLeft: '4px solid #00D9C8' },
    });
    try {
      const response = await axios.post(`${API}/content/${selectedContent.id}/regenerate-field`, {
        field_key: fieldKey,
        field_label: fieldLabel,
        current_content: editedStructuredContent
      }, { headers: getAuthHeader() });
      const newValue = response.data.field_value;
      setEditedStructuredContent(prev => ({ ...prev, [fieldKey]: newValue }));
      toast.success(`${fieldLabel} neu generiert`);
    } catch (err) {
      // K8s ingress (60s) can drop the connection before the backend finishes;
      // recheck DB to see if the field was actually written.
      // K8s ingress / upstream gateway drops (502/503/504) → recheck DB silently.
      const _statusF = err.response?.status;
      const _isDropOrGatewayF = err.code === 'ECONNABORTED' || !err.response || [502, 503, 504].includes(_statusF);
      if (_isDropOrGatewayF) {
        try {
          await new Promise(r => setTimeout(r, 2000));
          const recheck = await axios.get(`${API}/content/${selectedContent.id}`, { headers: getAuthHeader() });
          const piece = recheck.data?.content;
          const updatedTs = piece?.updated_at ? new Date(piece.updated_at).getTime() : 0;
          const newValue = piece?.structured_content?.[fieldKey];
          if (piece && newValue && String(newValue).trim() && updatedTs > requestStartTime) {
            setEditedStructuredContent(prev => ({ ...prev, [fieldKey]: newValue }));
            return;
          }
        } catch (_) { /* fall through */ }
        return; // stay quiet on plain drop / gateway
      }
      toast.error(`${fieldLabel}: braucht länger als gewohnt – bitte gleich erneut versuchen. Dein Kontingent wird nicht abgerechnet.`);
    } finally {
      setRegeneratingField(null);
    }
  };

  const handleRefine = async (type = 'text', customInstruction = null) => {
    const instruction = customInstruction || refineInstruction;
    if (!selectedContent || !instruction.trim()) return;
    setIsRefining(true);
    setRefineType(type);
    const requestStartTime = Date.now();
    // If type==='image' AND the existing pending-image polling system is in
    // play, the badge already gives feedback. For text we also want a single
    // brief info toast.
    if (type === 'image') {
      const prevImageCount = (selectedContent.image_urls || []).length;
      markImagePending(selectedContent.id, prevImageCount, 'refine');
      toast.info('To ensure the best quality, the image generation can take up to 3 minutes. You will be updated once it\'s done.', {
        duration: 10000,
        dismissible: true,
        style: { background: '#0E2D2C', color: '#E2F8F6', borderLeft: '4px solid #00D9C8' },
      });
    } else {
      toast.info('To ensure the best quality, the generation can take up to 1 minute. You will be updated once it\'s done.', {
        duration: 8000,
        dismissible: true,
        style: { background: '#0E2D2C', color: '#E2F8F6', borderLeft: '4px solid #00D9C8' },
      });
    }
    try {
      const response = await axios.post(`${API}/content/${selectedContent.id}/refine`, {
        instruction: instruction.trim(),
        type,
      }, { headers: getAuthHeader(), timeout: type === 'text' ? 120000 : 60000 });

      if (response.data.type === 'text') {
        // Blog / Pressemitteilung / Pinterest: strukturierte Daten mit aktualisieren,
        // sonst zeigt der Viewer weiter den alten Inhalt.
        const structPatch = {};
        if (response.data.blog_structured) {
          structPatch.blog_structured = response.data.blog_structured;
          structPatch.content_html = null;
        }
        if (response.data.structured_content) {
          structPatch.structured_content = response.data.structured_content;
          setEditedStructuredContent(response.data.structured_content);
        }
        // updated_at ist Teil des contentKey der Editoren (LinkedIn, Blog …) — ohne neuen Wert zeigen sie den alten Text weiter an.
        structPatch.updated_at = response.data.updated_at || new Date().toISOString();
        setEditedText(response.data.text_content);
        setSelectedContent(prev => ({ ...prev, text_content: response.data.text_content, ...structPatch }));
        setContent(prev => prev.map(c =>
          c.id === selectedContent.id ? { ...c, text_content: response.data.text_content, ...structPatch } : c
        ));
        if (response.data.version) {
          setVersions(prev => [response.data.version, ...prev]);
        }
        setRefineInstruction('');
        toast.success(t.produktion.textRefined);
      } else if (response.data.type === 'image' && response.data.async) {
        // ASYNC path: image is generated in a backend BackgroundTask.
        // markImagePending() (started above for type==='image') already drives
        // the /image-status poller which folds in the new image silently; we
        // only add the completion toast here.
        trackCompletionToast(selectedContent.id, { kind: 'image', platform: selectedContent.platform });
        setRefineImageInstruction('');
      } else if (response.data.type === 'image') {
        setSelectedContent(prev => ({
          ...prev,
          image_urls: response.data.all_images || [...(prev.image_urls || []), response.data.image_url]
        }));
        setContent(prev => prev.map(c =>
          c.id === selectedContent.id
            ? { ...c, image_urls: response.data.all_images || [...(c.image_urls || []), response.data.image_url] }
            : c
        ));
        const maxImages = getMaxImages();
        addSelectedImageUnique(response.data.image_url);
        setRefineImageInstruction('');
        unmarkImagePending(selectedContent.id);
        toast.success(t.produktion.imageGenerated);
      }
    } catch (err) {
      // K8s ingress (60s) / axios 60s timeout can drop the connection while
      // the backend keeps running. Recheck the piece in DB.
      // K8s ingress / upstream gateway drops (502/503/504) → silent recheck.
      const _statusR = err.response?.status;
      const _isDropOrGatewayR = err.code === 'ECONNABORTED' || !err.response || [502, 503, 504].includes(_statusR);
      if (_isDropOrGatewayR) {
        try {
          await new Promise(r => setTimeout(r, 2000));
          const recheck = await axios.get(`${API}/content/${selectedContent.id}`, { headers: getAuthHeader() });
          const piece = recheck.data?.content;
          const updatedTs = piece?.updated_at ? new Date(piece.updated_at).getTime() : 0;
          if (piece && updatedTs > requestStartTime) {
            if (type === 'text' && (piece.text_content || '').trim()) {
              const structPatch = {
                ...(piece.blog_structured ? { blog_structured: piece.blog_structured, content_html: piece.content_html || null } : {}),
                ...(piece.structured_content ? { structured_content: piece.structured_content } : {}),
                updated_at: piece.updated_at,
              };
              if (piece.structured_content) setEditedStructuredContent(piece.structured_content);
              setEditedText(piece.text_content);
              setSelectedContent(prev => ({ ...prev, text_content: piece.text_content, ...structPatch }));
              setContent(prev => prev.map(c =>
                c.id === selectedContent.id ? { ...c, text_content: piece.text_content, ...structPatch } : c
              ));
              setRefineInstruction('');
              return;
            }
            if (type === 'image' && Array.isArray(piece.image_urls) && piece.image_urls.length > (selectedContent.image_urls?.length || 0)) {
              setSelectedContent(prev => ({ ...prev, image_urls: piece.image_urls }));
              setContent(prev => prev.map(c =>
                c.id === selectedContent.id ? { ...c, image_urls: piece.image_urls } : c
              ));
              const maxImages = getMaxImages();
              const newest = piece.image_urls[piece.image_urls.length - 1];
              if (newest) {
                addSelectedImageUnique(newest);
              }
              setRefineImageInstruction('');
              unmarkImagePending(selectedContent.id);
              return;
            }
          }
        } catch (_) { /* fall through */ }
        // Plain drop / gateway: keep image-pending badge alive for polling;
        // for text refines tell the user (previously silent → looked like "nothing happens").
        if (type === 'text') {
          toast.error('Die Überarbeitung hat zu lange gedauert. Bitte erneut versuchen – es wurde nichts abgerechnet.');
        }
        return;
      }
      if (type === 'image') unmarkImagePending(selectedContent.id);
      const msg = err.response?.data?.detail || 'Anpassung konnte nicht gestartet werden – bitte erneut versuchen. Dein Kontingent wird nicht verbraucht.';
      toast.error(msg);
    } finally {
      setIsRefining(false);
    }
  };
  const handleEditImage = async () => {
    if (!selectedContent || !editImageInstruction.trim() || !editingImageUrl) return;
    setIsEditingImage(true);
    const prevImageCount = (selectedContent.image_urls || []).length;
    const contentId = selectedContent.id;
    markImagePending(contentId, prevImageCount, 'edit');
    toast.info(
      'To ensure the best quality, the image edit can take up to 3 minutes. You will be updated once it\'s done.',
      {
        duration: 10000,
        dismissible: true,
        style: {
          background: '#0E2D2C',
          color: '#E2F8F6',
          borderLeft: '4px solid #00D9C8',
        },
      },
    );
    try {
      const response = await axios.post(
        `${API}/content/${contentId}/edit-image`,
        {
          image_url: editingImageUrl,
          instruction: editImageInstruction.trim(),
        },
        { headers: getAuthHeader(), timeout: 90000 }
      );
      setSelectedContent(prev => ({ ...prev, image_urls: response.data.all_images }));
      setContent(prev => prev.map(c =>
        c.id === contentId ? { ...c, image_urls: response.data.all_images } : c
      ));
      addSelectedImageUnique(response.data.new_image_url);
      setEditImageInstruction('');
      setEditingImageUrl(null);
      unmarkImagePending(contentId);
    } catch (err) {
      // Same resilience strategy as handleGenerateImage:
      //   504/502/network drop → silent; poller resolves
      //   5xx → wait 15s; if poller resolved in the meantime, no toast
      //   4xx → immediate error toast
      const status = err.response?.status;
      const isNetworkOrGateway = !err.response || status === 504 || status === 502;
      const is5xxServer = status >= 500 && status < 600;

      if (isNetworkOrGateway) {
        return;
      }
      if (is5xxServer) {
        setTimeout(async () => {
          try {
            const recheck = await axios.get(`${API}/content/${contentId}`, { headers: getAuthHeader() });
            const fresh = recheck.data?.content;
            const newCount = (fresh?.image_urls || []).length;
            if (newCount > prevImageCount) {
              setContent(prev => prev.map(c => c.id === contentId ? { ...c, image_urls: fresh.image_urls } : c));
              if (selectedContent?.id === contentId) {
                setSelectedContent(prev => ({ ...prev, image_urls: fresh.image_urls }));
                const newest = fresh.image_urls[fresh.image_urls.length - 1];
                if (newest) {
                  addSelectedImageUnique(newest);
                }
              }
              setEditImageInstruction('');
              setEditingImageUrl(null);
              unmarkImagePending(contentId);
              return;
            }
          } catch { /* fall through */ }
          if (isImagePending(contentId)) {
            unmarkImagePending(contentId);
            const msg = err.response?.data?.detail || (lang === 'de'
              ? 'Bildbearbeitung konnte nicht abgeschlossen werden – bitte erneut versuchen. Dein Kontingent wird nur bei Erfolg verbraucht.'
              : 'Image edit could not be completed – please try again. You will only be charged on success.');
            toast.error(msg);
          }
        }, 15000);
        return;
      }
      unmarkImagePending(contentId);
      const msg = err.response?.data?.detail || (lang === 'de'
        ? 'Bildbearbeitung konnte nicht gestartet werden – bitte erneut versuchen. Dein Kontingent wird nicht verbraucht.'
        : 'Image edit could not be started – please try again. You will not be charged.');
      toast.error(msg);
    } finally {
      setIsEditingImage(false);
    }
  };
  const handleSaveMeta = async () => {
    if (!selectedContent) return;
    setIsSaving(true);

    try {
      const response = await axios.put(`${API}/content/${selectedContent.id}`, editedMeta, {
        headers: getAuthHeader()
      });

      setSelectedContent(response.data.content);
      setContent(prev => prev.map(c => c.id === selectedContent.id ? response.data.content : c));
      toast.success(t.produktion.changesSaved);
    } catch (err) {
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setIsSaving(false);
    }
  };

  const handleGenerateImage = async (contentId) => {
    setGeneratingImageId(contentId);
    const piece = content.find(c => c.id === contentId);
    const prevImageCount = (piece?.image_urls || []).length;
    // Start polling immediately so the badge appears regardless of HTTP fate.
    markImagePending(contentId, prevImageCount, 'generate');
    // Cyan-tinted info toast — friendly, time-boxed, dismissible.
    toast.info(
      'To ensure the best quality, the image generation can take up to 3 minutes. You will be updated once it\'s done.',
      {
        duration: 10000,
        dismissible: true,
        style: {
          background: '#0E2D2C',
          color: '#E2F8F6',
          borderLeft: '4px solid #00D9C8',
        },
      },
    );
    try {
      const response = await axios.post(`${API}/content/${contentId}/generate-image`, {}, {
        headers: getAuthHeader(),
        timeout: 90000,
      });
      if (response.data?.success) {
        const newImageUrl = response.data.image_url;
        setContent(prev => prev.map(c =>
          c.id === contentId ? { ...c, image_urls: [...(c.image_urls || []), newImageUrl] } : c
        ));
        if (selectedContent?.id === contentId) {
          setSelectedContent(prev => ({
            ...prev,
            image_urls: [...(prev.image_urls || []), newImageUrl],
          }));
          addSelectedImageUnique(newImageUrl);
        }
        unmarkImagePending(contentId);
      }
      // If response succeeded but no image_url, the background poller will catch
      // the next update — keep badge visible.
    } catch (err) {
      // 504 → K8s ingress dropped a still-running request. Stay silent, the
      // background poller will pick the image up when the backend finishes.
      // 5xx → backend really errored. BUT some failure modes save the image
      // just before the error fires (race in the post-save commit). Give the
      // poller a fair window (15s) to detect the image; only then surface
      // the error.
      // 4xx → real user-facing error (quota, payload, etc) — surface immediately.
      const status = err.response?.status;
      const isNetworkOrGateway = !err.response || status === 504 || status === 502;
      const is5xxServer = status >= 500 && status < 600;

      if (isNetworkOrGateway) {
        // No-op — let the poller resolve it.
        return;
      }

      if (is5xxServer) {
        // Give the poller a fair chance: wait 15s. Then directly fetch the
        // piece from DB — if image_urls grew at all, the user got their
        // image (silent success). Only if STILL nothing → error toast.
        setTimeout(async () => {
          try {
            const recheck = await axios.get(`${API}/content/${contentId}`, { headers: getAuthHeader() });
            const fresh = recheck.data?.content;
            const newCount = (fresh?.image_urls || []).length;
            if (newCount > prevImageCount) {
              // Image landed despite 5xx → silent. Make sure UI is up to date.
              setContent(prev => prev.map(c => c.id === contentId ? { ...c, image_urls: fresh.image_urls } : c));
              if (selectedContent?.id === contentId) {
                setSelectedContent(prev => ({ ...prev, image_urls: fresh.image_urls }));
                const newest = fresh.image_urls[fresh.image_urls.length - 1];
                if (newest) {
                  addSelectedImageUnique(newest);
                }
              }
              unmarkImagePending(contentId);
              return;
            }
          } catch { /* fall through */ }
          if (isImagePending(contentId)) {
            unmarkImagePending(contentId);
            const errorMsg = err.response?.data?.detail || 'Bildgenerierung konnte nicht abgeschlossen werden – bitte erneut versuchen. Dein Kontingent wird nur bei Erfolg verbraucht.';
            toast.error(errorMsg);
          }
        }, 15000);
        return;
      }

      // 4xx: immediate, no waiting
      unmarkImagePending(contentId);
      const errorMsg = err.response?.data?.detail || 'Bildgenerierung konnte nicht gestartet werden – bitte erneut versuchen. Dein Kontingent wird nicht verbraucht.';
      toast.error(errorMsg);
    } finally {
      setGeneratingImageId(null);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedContent) return;

    try {
      const response = await axios.put(`${API}/content/${selectedContent.id}`, {
        status: newStatus
      }, {
        headers: getAuthHeader()
      });

      setSelectedContent(response.data.content);
      setContent(prev => prev.map(c => c.id === selectedContent.id ? response.data.content : c));
      toast.success(t.common.toasts.statusUpdated);
    } catch (err) {
      toast.error(t.common.toasts.statusChangeFailed);
    }
  };

  // Submit YouTube content for video generation
  const handleSubmitForGeneration = async () => {
    if (!selectedContent || selectedContent.platform !== 'youtube') return;
    
    try {
      await axios.post(`${API}/content/${selectedContent.id}/submit-for-generation`, {}, {
        headers: getAuthHeader()
      });
      
      setSelectedContent(prev => ({ ...prev, status: 'video_generation' }));
      setContent(prev => prev.map(c => 
        c.id === selectedContent.id ? { ...c, status: 'video_generation' } : c
      ));
      toast.success(t.common.toasts.scriptSubmitted);
    } catch (err) {
      toast.error(t.common.toasts.submitFailed);
    }
  };


  // New scheduling flow functions
  const handleApprove = async () => {
    if (!selectedContent) return;
    
    const platformName = (t.platforms?.[selectedContent.platform]) || platforms.find(p => p.key === selectedContent.platform)?.label || selectedContent.platform;
    
    // Validate image count
    const maxImages = getMaxImages();
    if (selectedImages.length > maxImages) {
      const imageWord = maxImages === 1 ? t.common.toasts.imageWordSingular : t.common.toasts.imageWordPlural;
      toast.error(
        t.common.toasts.maxImagesPerPost
          .replace('{platform}', platformName)
          .replace('{max}', maxImages)
          .replace('{imageWord}', imageWord)
      );
      return;
    }
    
    // Validate character count - special handling for Pressemitteilung and Pinterest
    if (selectedContent.platform === 'pressemitteilung') {
      // Validate each structured field
      for (const field of PRESS_RELEASE_FIELDS) {
        const value = editedStructuredContent[field.key] || '';
        if (value.length > field.max) {
          toast.error(`Das Feld "${field.label}" überschreitet das Limit von ${field.max} Zeichen (aktuell: ${value.length}). Bitte kürzen.`);
          return;
        }
        // Check required fields (non-optional)
        if (!field.optional && !value.trim()) {
          toast.error(`Das Feld "${field.label}" darf nicht leer sein.`);
          return;
        }
      }
    } else if (selectedContent.platform === 'pinterest') {
      const pinTitle = editedStructuredContent.pin_title || '';
      const pinDesc = editedStructuredContent.pin_description || '';
      if (pinTitle.length > 100) {
        toast.error(`Pin-Titel überschreitet das Limit von 100 Zeichen (aktuell: ${pinTitle.length}). Bitte kürzen.`);
        return;
      }
      if (!pinTitle.trim()) {
        toast.error('Pin-Titel darf nicht leer sein.');
        return;
      }
      if (pinDesc.length > 500) {
        toast.error(`Pin-Beschreibung überschreitet das Limit von 500 Zeichen (aktuell: ${pinDesc.length}). Bitte kürzen.`);
        return;
      }
    } else {
      // Standard character validation for other platforms
      const textContent = editedText || selectedContent.text_content || '';
      const charCount = textContent.length;
      const maxChars = getCharLimit();
      const config = getPlatformConfig(selectedContent.platform);
      
      if (charCount > maxChars) {
        toast.error(
          t.produktion.maxCharsExceeded
            .replace('{platform}', (t.platforms?.[selectedContent.platform] || config.name))
            .replace('{max}', maxChars.toLocaleString())
            .replace('{count}', charCount.toLocaleString())
        );
        return;
      }
    }
    
    // Plan-Durchklick: ein Klick auf "Freigeben" terminiert direkt mit Datum/Uhrzeit aus der Leiste.
    // Blogs brauchen ein Ziel (Website/WordPress) und gehen deshalb weiter über den Dialog.
    if (planQuickRef.current && selectedContent.platform !== 'blog' && planBarDate && planBarTime) {
      planQuickRef.current = false;
      setScheduleDate(planBarDate);
      setScheduleTime(planBarTime);
      await handleScheduleContent(null, { date: planBarDate, time: planBarTime });
      return;
    }

    // Reset blog destination defaults each time the modal opens.
    setBlogPublishWhen('scheduled');
    if (selectedContent.platform === 'blog') {
      // Load the actually-connected targets so the modal can present a strict
      // either/or (Lovable-Website vs WordPress) instead of assuming Supabase.
      setBlogTargetStatus({ lovable: null, wordpress: null, shopify: null });
      setBlogTarget(null);
      setGhTargets([]);
      setBlogTranslationOf('');
      loadBlogTargets();
    }
    setIsLoadingSuggestion(true);
    setShowScheduleModal(true);

    try {
      const response = await axios.get(`${API}/content/${selectedContent.id}/suggest-schedule`, {
        headers: getAuthHeader()
      });
      
      const suggestedDate = new Date(response.data.suggested_datetime);
      if (planNav && selectedContent.plan_id === planNav.id && planBarDate && planBarTime) {
        setScheduleDate(planBarDate);
        setScheduleTime(planBarTime);
        setSuggestedReason(lang === 'de' ? 'Termin aus deinem Content-Plan.' : 'Time from your content plan.');
      } else {
        setScheduleDate(suggestedDate.toISOString().split('T')[0]);
        setScheduleTime(suggestedDate.toTimeString().slice(0, 5));
        setSuggestedReason(response.data.reason);
      }
    } catch (err) {
      // Fallback to tomorrow at 10:00
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setScheduleDate(tomorrow.toISOString().split('T')[0]);
      setScheduleTime('10:00');
      setSuggestedReason('Vorgeschlagene Zeit für optimale Reichweite.');
    } finally {
      setIsLoadingSuggestion(false);
    }
  };

  // Probe both blog destinations and default the selection to exactly one.
  // Prefer Lovable when both are connected (preserves prior default); otherwise
  // pick whichever single target is connected.
  const loadBlogTargets = useCallback(async () => {
    const check = async (path) => {
      try {
        const { data } = await axios.get(`${API}/integrations/${path}/status`, { headers: getAuthHeader() });
        return !!data.connected;
      } catch (_e) {
        return false;
      }
    };
    const loadGh = async () => {
      try {
        const { data } = await axios.get(`${API}/website-publishing/targets/for-content/${selectedContent?.id}`, { headers: getAuthHeader() });
        return data.targets || [];
      } catch (_e) {
        return [];
      }
    };
    const [lovable, wordpress, shopify, gh] = await Promise.all([check('supabase'), check('wordpress'), check('shopify'), loadGh()]);
    setGhTargets(gh);
    const ghOk = gh.find(t => !t.reason);
    setBlogTargetStatus({ lovable, wordpress, shopify });
    setBlogTarget(ghOk ? `github:${ghOk.id}` : (lovable ? 'lovable' : (wordpress ? 'wordpress' : (shopify ? 'shopify' : null))));
  }, [getAuthHeader, selectedContent?.id]);

  // Render the rich blog fragment (buildCleanHtml) for a server-side publish.
  const renderBlogHtml = () => {
    if (!selectedContent?.blog_structured) return '';
    try {
      return buildCleanHtml(
        selectedContent.blog_structured,
        getHtmlLabels(lang),
        { lang, fallbackTitle: selectedContent.title || '', heroImageUrl: (selectedContent.image_urls && selectedContent.image_urls[0]) || '' }
      );
    } catch (_e) {
      return '';
    }
  };

  // "Sofort" — publish a blog piece to WordPress right now (status='publish').
  // Scheduled WordPress posts go through the calendar/poller instead (see
  // handleScheduleContent → publish_targets=['wordpress']), so they stay fully
  // editable in the calendar.
  const publishBlogToWordPressNow = async () => {
    if (!selectedContent) return;
    setIsScheduling(true);
    try {
      const contentHtml = renderBlogHtml();
      const { data } = await axios.post(
        `${API}/integrations/wordpress/publish/${selectedContent.id}`,
        { status: 'publish', ...(contentHtml ? { content_html: contentHtml } : {}) },
        { headers: getAuthHeader(), timeout: 60000 }
      );
      const wp = data?.wordpress || {};
      const patch = {
        status: 'approved',
        wordpress_post_id: wp.id,
        wordpress_post_link: wp.link,
        wordpress_status: wp.status,
        wordpress_published_at: new Date().toISOString(),
      };
      setSelectedContent(prev => ({ ...prev, ...patch }));
      setContent(prev => prev.map(c => c.id === selectedContent.id ? { ...c, ...patch } : c));
      toast.success(lang === 'de' ? 'Auf WordPress veröffentlicht.' : 'Published to WordPress.');
      setShowScheduleModal(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || (lang === 'de' ? 'Senden fehlgeschlagen.' : 'Publishing failed.'));
    } finally {
      setIsScheduling(false);
    }
  };

  // "Sofort" — publish a blog piece to Shopify right now (status='publish').
  // Scheduled Shopify posts go through the calendar/poller (publish_targets=['shopify']).
  const publishBlogToShopifyNow = async () => {
    if (!selectedContent) return;
    setIsScheduling(true);
    try {
      const contentHtml = renderBlogHtml();
      const { data } = await axios.post(
        `${API}/integrations/shopify/publish/${selectedContent.id}`,
        { status: 'publish', ...(contentHtml ? { content_html: contentHtml } : {}) },
        { headers: getAuthHeader(), timeout: 60000 }
      );
      const sh = data?.shopify || {};
      const patch = {
        status: 'approved',
        shopify_article_id: sh.id,
        shopify_article_link: sh.link,
        shopify_status: sh.status,
        shopify_published_at: new Date().toISOString(),
      };
      setSelectedContent(prev => ({ ...prev, ...patch }));
      setContent(prev => prev.map(c => c.id === selectedContent.id ? { ...c, ...patch } : c));
      toast.success(lang === 'de' ? 'Auf Shopify veröffentlicht.' : 'Published to Shopify.');
      setShowScheduleModal(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || (lang === 'de' ? 'Senden fehlgeschlagen.' : 'Publishing failed.'));
    } finally {
      setIsScheduling(false);
    }
  };

  // "Sofort" — publish a blog piece to the Website (Supabase) right now via the
  // direct-push endpoint (not the poller). Renders the rich fragment client-side.
  const handlePublishWebsiteNow = async () => {
    if (!selectedContent) return;
    setIsScheduling(true);
    try {
      let contentHtml = '';
      if (selectedContent.blog_structured) {
        try {
          contentHtml = buildCleanHtml(
            selectedContent.blog_structured,
            getHtmlLabels(lang),
            { lang, fallbackTitle: selectedContent.title || '', heroImageUrl: (selectedContent.image_urls && selectedContent.image_urls[0]) || '' }
          );
        } catch (_e) { contentHtml = ''; }
      }
      const { data } = await axios.post(
        `${API}/content/${selectedContent.id}/publish-website-now`,
        contentHtml ? { content_html: contentHtml } : {},
        { headers: getAuthHeader(), timeout: 60000 }
      );
      const patch = {
        status: 'approved',
        supabase_row_id: data?.supabase?.row_id ?? true,
        supabase_published_at: new Date().toISOString(),
      };
      setSelectedContent(prev => ({ ...prev, ...patch }));
      setContent(prev => prev.map(c => c.id === selectedContent.id ? { ...c, ...patch } : c));
      toast.success(lang === 'de' ? 'Auf Website veröffentlicht.' : 'Published to website.');
      setShowScheduleModal(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || (lang === 'de' ? 'Senden fehlgeschlagen.' : 'Publishing failed.'));
    } finally {
      setIsScheduling(false);
    }
  };

  const handleScheduleContent = async (replaceEventId = null, override = null) => {
    const sDate = override?.date || scheduleDate;
    const sTime = override?.time || scheduleTime;
    if (!selectedContent || !sDate || !sTime) return;

    // Platform options (TikTok privacy, IG story, Pinterest board, YouTube title …)
    const isOptionPlatform = OPTION_PLATFORMS.includes(selectedContent.platform);
    if (isOptionPlatform) {
      const optErr = optionsRef.current?.validate();
      if (optErr) {
        toast.error(optErr);
        return;
      }
    }

    setIsScheduling(true);
    
    try {
      const scheduledAt = localInputToUTC(`${sDate}T${sTime}:00`);
      
      const payload = {
        scheduled_at: scheduledAt,
        selected_images: selectedImages
      };
      if (replaceEventId) {
        payload.replace_event_id = replaceEventId;
      }
      if (isOptionPlatform) {
        Object.assign(payload, optionsRef.current?.getPayload() || {});
      }
      // Blog: render + persist the rich content_html fragment now, so a later
      // server-side/scheduled publish always has the styled HTML — even if the
      // user never opened the viewer. Rendered directly from blog_structured.
      if (selectedContent.platform === 'blog' && selectedContent.blog_structured) {
        try {
          payload.content_html = buildCleanHtml(
            selectedContent.blog_structured,
            getHtmlLabels(lang),
            { lang, fallbackTitle: selectedContent.title || '', heroImageUrl: (selectedContent.image_urls && selectedContent.image_urls[0]) || '' }
          );
        } catch (_e) { /* non-fatal: backend falls back to render_blog_html */ }
        // Route the calendar event to the website poller. The provider is a
        // strict either/or; the backend records it on the event so the poller,
        // calendar and edit path treat both uniformly (fully editable in the
        // calendar, published at the due time).
        if (blogTarget === 'lovable') {
          payload.publish_targets = ['website'];
        } else if (blogTarget === 'wordpress') {
          payload.publish_targets = ['wordpress'];
        } else if (blogTarget === 'shopify') {
          payload.publish_targets = ['shopify'];
        } else if (blogTarget?.startsWith('github:')) {
          payload.publish_targets = [blogTarget];
          if (blogTranslationOf) payload.translation_of = blogTranslationOf;
        }
      }

      const response = await axios.post(`${API}/content/${selectedContent.id}/schedule`, payload, {
        headers: getAuthHeader()
      });
      
      // Delete all comments after approval (they are not needed anymore)
      if (comments.length > 0) {
        try {
          await axios.delete(`${API}/content/${selectedContent.id}/comments/all`, {
            headers: getAuthHeader()
          });
          setComments([]);
        } catch (err) {
          console.log('Failed to delete comments:', err);
        }
      }
      
      // Update local state
      setSelectedContent(prev => ({ ...prev, status: 'approved', scheduled_at: scheduledAt, image_urls: selectedImages }));
      setContent(prev => prev.map(c => 
        c.id === selectedContent.id ? { ...c, status: 'approved', scheduled_at: scheduledAt, image_urls: selectedImages } : c
      ));
      
      setScheduledContentId(selectedContent.id);
      setShowScheduleModal(false);
      setShowConflictDialog(false);
      setConflictData(null);
      
      toast.success(t.produktion.scheduledInCalendar);

      if (planNav && selectedContent.plan_id === planNav.id) {
        // Plan-Durchklick: Status lokal nachziehen und zum nächsten offenen Beitrag springen
        const doneId = selectedContent.id;
        const items = planNav.items.map(i => (i.content_id === doneId ? { ...i, status: 'approved', scheduled_at: scheduledAt } : i));
        setPlanNav(prev => (prev ? { ...prev, items, counts: { ...prev.counts, approved: items.filter(i => i.status === 'approved').length } } : prev));
        const at = items.findIndex(i => i.content_id === doneId);
        const next = items.slice(at + 1).find(i => i.content_id && i.status === 'draft') || items.find(i => i.content_id && i.status === 'draft');
        if (next) openPlanItem(next);
        else toast.success(lang === 'de' ? 'Alle Beiträge des Plans sind freigegeben.' : 'All posts of the plan are approved.');
      } else {
        // Show platform adaptation dialog
        setShowPlatformAdaptModal(true);
        setAdditionalPlatforms([]);
      }
      
    } catch (err) {
      console.error('Schedule error:', err.response?.status, err.response?.data);
      const status = err.response?.status;
      const data = err.response?.data;
      // Handle conflict (409) - detail can be object or string
      const detail = data?.detail;
      const isConflict = status === 409 && (
        (typeof detail === 'object' && detail?.conflict) ||
        (typeof detail === 'string' && detail.includes('bereits'))
      );
      
      if (isConflict) {
        const conflictInfo = typeof detail === 'object' ? detail : {};
        setConflictData({
          existingEventId: conflictInfo.existing_event_id || '',
          existingScheduledAt: conflictInfo.existing_scheduled_at || '',
          message: conflictInfo.message || detail || (lang === 'de' 
            ? 'Dieser Beitrag ist bereits für diesen Tag auf dieser Plattform eingeplant.' 
            : 'This post is already scheduled for this day on this platform.')
        });
        setShowScheduleModal(false);
        setShowConflictDialog(true);
      } else {
        toast.error(data?.detail?.message || data?.detail || t.produktion.schedulingFailed);
      }
    } finally {
      setIsScheduling(false);
    }
  };

  const handleConflictReplace = () => {
    if (conflictData?.existingEventId) {
      handleScheduleContent(conflictData.existingEventId);
    }
  };

  const handleConflictSaveDraft = async () => {
    if (!selectedContent) return;
    try {
      await axios.put(`${API}/content/${selectedContent.id}`, {
        status: 'draft'
      }, { headers: getAuthHeader() });
      setSelectedContent(prev => ({ ...prev, status: 'draft' }));
      setContent(prev => prev.map(c => 
        c.id === selectedContent.id ? { ...c, status: 'draft' } : c
      ));
      setShowConflictDialog(false);
      setConflictData(null);
      toast.success(t.produktion.savedAsDraft);
    } catch (err) {
      toast.error(t.produktion.saveFailed);
    }
  };

  // Multi-select helpers
  const toggleSelectId = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredContent.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredContent.map(c => c.id)));
    }
  };

  const exitSelectMode = () => {
    setIsSelectMode(false);
    setSelectedIds(new Set());
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setShowBulkDeleteConfirm(false);

    try {
      await axios.post(`${API}/content/bulk-delete`, { ids }, { headers: getAuthHeader() });
      setBulkDeletedIds(ids);
      setContent(prev => prev.filter(c => !ids.includes(c.id)));
      exitSelectMode();
      if (selectedContent && ids.includes(selectedContent.id)) closeDetailPanel();

      // Start 10s undo countdown
      setUndoCountdown(10);
      const interval = setInterval(() => {
        setUndoCountdown(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            setBulkDeletedIds(null);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      toast.error(t.produktion.deleteFailed);
    }
  };

  const handleBulkUndo = async () => {
    if (!bulkDeletedIds) return;
    try {
      await axios.post(`${API}/content/bulk-restore`, { ids: bulkDeletedIds }, { headers: getAuthHeader() });
      // Refetch content after restore
      const res = await axios.get(`${API}/content`, { headers: getAuthHeader() });
      const items = res.data.content_pieces || res.data.items || res.data || [];
      setContent(Array.isArray(items) ? items : []);
      toast.success(t.produktion.restored);
    } catch (err) {
      toast.error(t.produktion.restoreFailed);
    } finally {
      setBulkDeletedIds(null);
      setUndoCountdown(0);
    }
  };

  const handleAdaptPlatforms = async () => {
    console.log('handleAdaptPlatforms called:', { scheduledContentId, additionalPlatforms });
    
    if (!scheduledContentId || additionalPlatforms.length === 0) {
      console.log('Early return - missing data');
      setShowPlatformAdaptModal(false);
      return;
    }
    
    setIsAdapting(true);
    toast.info('To ensure the best quality, the generation can take up to 3 minutes. You will be updated once it\'s done.', {
      duration: 10000,
      dismissible: true,
      style: { background: '#0E2D2C', color: '#E2F8F6', borderLeft: '4px solid #00D9C8' },
    });
    const requestStart = Date.now();
    // Snapshot current content IDs so we can detect newly-created pieces
    // when the connection drops mid-generation.
    const prevIds = new Set((content || []).map(c => c.id));
    
    try {
      // Longer timeout for multiple platform adaptations (3 minutes)
      const response = await axios.post(`${API}/content/${scheduledContentId}/adapt-platforms`, {
        additional_platforms: additionalPlatforms
      }, {
        headers: getAuthHeader(),
        timeout: 180000 // 3 minutes for AI content generation
      });
      
      console.log('Adapt response:', response.data);
      
      if (response.data.new_pieces?.length > 0) {
        const newPieces = response.data.new_pieces;
        setContent(prev => [...newPieces, ...prev]);
        // ASYNC: placeholders arrive as status='generating'; register each for a
        // completion toast fired by the 3s poller once it flips to draft/failed.
        if (response.data.async) {
          newPieces.forEach(p => trackCompletionToast(p.id, { kind: 'text', platform: p.platform }));
        }
        toast.success(lang === 'de'
          ? `${newPieces.length} Anpassung${newPieces.length === 1 ? '' : 'en'} gestartet`
          : `${newPieces.length} adaptation${newPieces.length > 1 ? 's' : ''} started`
        );
        setShowPlatformAdaptModal(false);
        setScheduledContentId(null);
        // Automatically open the first new piece in the detail panel
        openDetailPanel(newPieces[0]);
      } else if (response.data.success) {
        // Success but no new pieces (e.g., all platforms were same as original)
        toast.info('Keine neuen Entwürfe erstellt (Plattformen bereits vorhanden)');
        setShowPlatformAdaptModal(false);
        setScheduledContentId(null);
        closeDetailPanel();
      }
    } catch (err) {
      console.error('Adapt error:', err);

      // K8s ingress / gateway drops (502/503/504) can drop the connection
      // while the backend keeps running. Recheck the content list silently.
      const _statusA = err.response?.status;
      const _isDropOrGatewayA = err.code === 'ECONNABORTED' || !err.response || err.message?.includes('timeout') || [502, 503, 504].includes(_statusA);
      if (_isDropOrGatewayA) {
        try {
          await new Promise(r => setTimeout(r, 5000));
          const recheck = await axios.get(`${API}/content`, { headers: getAuthHeader() });
          const allPieces = recheck.data?.content_pieces || recheck.data?.items || recheck.data || [];
          const newPieces = (Array.isArray(allPieces) ? allPieces : []).filter(c => {
            const createdTs = c.created_at ? new Date(c.created_at).getTime() : 0;
            return !prevIds.has(c.id) && createdTs >= requestStart - 5000;
          });
          if (newPieces.length > 0) {
            setContent(prev => [...newPieces, ...prev]);
            toast.info(lang === 'de'
              ? `${newPieces.length} neue${newPieces.length === 1 ? 'r Entwurf' : ' Entwürfe'} sind da`
              : `${newPieces.length} new draft${newPieces.length > 1 ? 's' : ''} ready`
            );
            setShowPlatformAdaptModal(false);
            setScheduledContentId(null);
            openDetailPanel(newPieces[0]);
            return;
          }
        } catch { /* fall through */ }
        // No fresh pieces yet and no real error → stay quiet, reload resolves.
        return;
      }

      const errorMessage = err.response?.data?.detail || 'Anpassung braucht länger als gewohnt – bitte gleich erneut versuchen. Dein Kontingent wird nicht abgerechnet.';
      toast.error(errorMessage);
    } finally {
      setIsAdapting(false);
    }
  };

  const skipPlatformAdaption = () => {
    setShowPlatformAdaptModal(false);
    setScheduledContentId(null);
    closeDetailPanel();
  };

  // Legacy function kept for backwards compatibility
  const confirmApprove = async () => {
    if (!selectedContent) return;

    try {
      const response = await axios.post(`${API}/content/${selectedContent.id}/approve`, {
        additional_platforms: additionalPlatforms
      }, {
        headers: getAuthHeader()
      });

      setSelectedContent(prev => ({ ...prev, status: 'approved' }));
      setContent(prev => prev.map(c => c.id === selectedContent.id ? { ...c, status: 'approved' } : c));

      if (response.data.new_pieces?.length > 0) {
        setContent(prev => [...response.data.new_pieces, ...prev]);
        // ASYNC placeholders — register completion toasts (see 3s poller).
        if (response.data.async) {
          response.data.new_pieces.forEach(p => trackCompletionToast(p.id, { kind: 'text', platform: p.platform }));
        }
        toast.success(t.common.toasts.approvedWithDrafts.replace('{n}', response.data.new_pieces.length));
      } else {
        toast.success(t.common.toasts.approvedInCalendar);
      }

      setShowApproveDialog(false);
    } catch (err) {
      toast.error(t.common.toasts.approveFailed);
    }
  };

  const handleDelete = async () => {
    if (!selectedContent) return;

    try {
      await axios.delete(`${API}/content/${selectedContent.id}`, {
        headers: getAuthHeader()
      });

      setDeletedContent(selectedContent);
      setContent(prev => prev.filter(c => c.id !== selectedContent.id));
      closeDetailPanel();

      toast.success(
        <div className="flex items-center gap-2">
          <span>{t.common.toasts.deleted}</span>
          <button 
            onClick={handleUndoDelete}
            className="text-[#00D9C8] hover:underline ml-2"
          >
            {t.common.toasts.undo}
          </button>
        </div>,
        { duration: 5000 }
      );

      setTimeout(() => setDeletedContent(null), 5000);
    } catch (err) {
      toast.error(t.common.toasts.deleteFailed);
    }
  };

  const handleUndoDelete = async () => {
    if (!deletedContent) return;

    try {
      await axios.post(`${API}/content/${deletedContent.id}/restore`, {}, {
        headers: getAuthHeader()
      });

      setContent(prev => [...prev, { ...deletedContent, status: 'draft' }]);
      setDeletedContent(null);
      toast.success(t.produktion.contentRestored);
    } catch (err) {
      toast.error(t.common.toasts.restoreFailed);
    }
  };

  const handleSaveImages = async () => {
    if (!selectedContent) return;
    setIsSaving(true);
    try {
      // Persist ONLY the selected subset: unchecked images are removed from the content piece.
      // This makes "save" match the user's visible selection, so the preview thumbnail
      // and the image grid reflect exactly what was selected after refresh.
      const newImageUrls = selectedImages;
      const response = await axios.put(`${API}/content/${selectedContent.id}`, {
        image_urls: newImageUrls
      }, { headers: getAuthHeader() });
      const updated = response.data.content;
      setSelectedContent(updated);
      setContent(prev => prev.map(c => c.id === selectedContent.id ? updated : c));
      // Keep selection in sync with the persisted list
      setSelectedImages(updated.image_urls || []);
      toast.success(t.produktion.imagesSaved);
    } catch (err) {
      toast.error(t.produktion.saveFailed);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteImage = async (imageUrl) => {
    if (!selectedContent) return;
    // Double confirmation before destructive action
    const firstMsg = lang === 'de'
      ? 'Möchtest du dieses Bild wirklich aus diesem Beitrag entfernen?'
      : 'Do you really want to remove this image from this post?';
    if (!window.confirm(firstMsg)) return;
    const secondMsg = lang === 'de'
      ? 'Dieser Vorgang kann nicht rückgängig gemacht werden. Wirklich endgültig löschen?'
      : 'This action cannot be undone. Really delete permanently?';
    if (!window.confirm(secondMsg)) return;

    try {
      const newImageUrls = (selectedContent.image_urls || []).filter(u => u !== imageUrl);
      const newSelected = (selectedContent.selected_image_urls ?? selectedContent.image_urls ?? []).filter(u => u !== imageUrl);
      const response = await axios.put(`${API}/content/${selectedContent.id}`, {
        image_urls: newImageUrls,
        selected_image_urls: newSelected
      }, { headers: getAuthHeader() });
      const updated = response.data.content;
      setSelectedContent(updated);
      setContent(prev => prev.map(c => c.id === selectedContent.id ? updated : c));
      setSelectedImages(prev => prev.filter(u => u !== imageUrl));
      toast.success(lang === 'de' ? 'Bild gelöscht' : 'Image deleted');
    } catch (err) {
      toast.error(lang === 'de' ? 'Löschen fehlgeschlagen' : 'Delete failed');
    }
  };

  const handleUploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedContent) return;
    const input = e.target;

    const logoPlacement = await askLogo(file);
    if (logoPlacement === undefined) { // abgebrochen → nichts hochladen
      input.value = '';
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    appendLogoFields(formData, logoPlacement);

    try {
      const response = await axios.post(
        `${API}/content/${selectedContent.id}/upload-image`,
        formData,
        {
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      const uploadedImgs = response.data.image_urls || [];
      setSelectedContent(prev => ({ ...prev, image_urls: uploadedImgs }));
      setContent(prev => prev.map(c => 
        c.id === selectedContent.id ? { ...c, image_urls: uploadedImgs } : c
      ));
      // Neu hochgeladenes Bild sofort auswählen — sonst überschreibt das
      // Einplanen/Freigeben (selected_images) die Bilder mit der alten, leeren Auswahl.
      const newestUpload = uploadedImgs[uploadedImgs.length - 1];
      if (newestUpload) addSelectedImageUnique(newestUpload);
      toast.success(lang === 'de' ? 'Bild hochgeladen' : 'Image uploaded');
      fetchGallery();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? `Upload fehlgeschlagen: ${detail}` : `Upload fehlgeschlagen (${err.response?.status || err.message || 'keine Antwort'})`);
    } finally {
      if (e.target) e.target.value = '';
    }
  };

  const handleAttachGalleryImage = async (imageUrl) => {
    if (!selectedContent) return;

    const formData = new FormData();
    formData.append('image_url', imageUrl);

    try {
      const response = await axios.post(
        `${API}/content/${selectedContent.id}/attach-gallery-image`,
        formData,
        {
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      // Also update selectedImages so the newly attached gallery image is
      // persisted on the next schedule/save (selected_image_urls in DB).
      const newImgs = response.data.image_urls;
      setSelectedContent(prev => ({ ...prev, image_urls: newImgs, selected_image_urls: newImgs }));
      setContent(prev => prev.map(c => 
        c.id === selectedContent.id ? { ...c, image_urls: newImgs, selected_image_urls: newImgs } : c
      ));
      setSelectedImages(newImgs);
      setShowGalleryModal(false);
      toast.success(lang === 'de' ? 'Bild hinzugefügt' : 'Image added');
    } catch (err) {
      toast.error(t.common.toasts.addFailed);
    }
  };

  const handleShareEmail = () => {
    if (!selectedContent) return;
    
    const contentNumber = selectedContent.content_number || '—';
    const subject = `Content Piece #${contentNumber} - generiert mit xchannels.ai`;
    
    // Build formatted body
    let bodyText = '';
    
    // For Pressemitteilung, use structured content
    if (selectedContent.platform === 'pressemitteilung' && selectedContent.structured_content) {
      const sc = selectedContent.structured_content;
      if (sc.headline) bodyText += `${sc.headline}\n\n`;
      if (sc.subtitle) bodyText += `${sc.subtitle}\n\n`;
      if (sc.short_text) bodyText += `${sc.short_text}\n\n`;
      if (sc.body_text) bodyText += `${sc.body_text}\n\n`;
      if (sc.keywords) bodyText += `Schlüsselwörter: ${sc.keywords}\n\n`;
      if (sc.company_description) bodyText += `Über uns: ${sc.company_description}\n\n`;
    } else if (selectedContent.platform === 'pinterest') {
      const sc = selectedContent.structured_content || {};
      const title = sc.pin_title || '';
      const desc = sc.pin_description || selectedContent.text_content || '';
      if (title) bodyText += `${title}\n\n`;
      bodyText += desc;
    } else {
      bodyText = selectedContent.text_content || '';
    }
    
    // Add image URL if available
    const imageUrl = selectedImages.length > 0 ? selectedImages[0] : (selectedContent.image_urls?.[0] || '');
    if (imageUrl) {
      bodyText += `\n\n---\nBild: ${imageUrl}\n---`;
    }
    
    // Add footer
    bodyText += `\n\n\nGeneriert mit xchannels.ai\nhttps://xchannels.ai/`;
    
    const encodedSubject = encodeURIComponent(subject);
    const encodedBody = encodeURIComponent(bodyText);
    
    window.location.href = `mailto:?subject=${encodedSubject}&body=${encodedBody}`;
  };

  const loadVersion = (version) => {
    setEditedText(version.text_content);
    // Editoren (LinkedIn, Blog …) übernehmen neuen Text nur bei geändertem contentKey (enthält updated_at).
    setSelectedContent(prev => prev ? { ...prev, updated_at: new Date().toISOString() } : prev);
    toast.success(t.produktion.versionLoaded.replace('{n}', version.version_number));
  };

  const getPlatformIcon = (platformKey, size = 'w-4 h-4') => {
    const platform = platforms.find(p => p.key === platformKey);
    if (platform) {
      const Icon = platform.icon;
      return <Icon className={size} style={{ color: platform.color }} />;
    }
    // Check custom channels
    const cc = customChannels.find(c => c.id === platformKey);
    if (cc) {
      return <Layers className={size} style={{ color: '#A0AEC0' }} />;
    }
    return null;
  };

  const getPlatformLimit = (platformKey) => {
    // Check user's custom char limit first
    const userLimit = platformCharLimits[platformKey];
    if (userLimit && userLimit > 0) return userLimit;
    const platform = platforms.find(p => p.key === platformKey);
    if (platform) return platform?.limit || 3000;
    // Check custom channels
    const cc = customChannels.find(c => c.id === platformKey);
    if (cc) return cc.fields?.[0]?.maxLength || 500;
    return 3000;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div className="flex min-w-0 max-w-full" data-testid="produktion-page">
      {logoDialog}
      {/* Main Content - Hidden on mobile when detail panel is open */}
      <div className={`flex-1 min-w-0 max-w-full transition-all ${selectedContent ? 'hidden lg:block lg:mr-[480px]' : ''}`}>
        {/* Header */}
        <div className="mb-4 lg:mb-6">
          <h1 className="text-2xl lg:text-4xl font-black tracking-tight text-white mb-1 lg:mb-2">
            {t.produktion.title}
          </h1>
          <p className="text-sm lg:text-base text-[#A0AEC0]">
            {t.produktion.subtitle}
          </p>
        </div>

        {/* Top Bar - Responsive */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 lg:gap-4 mb-4 lg:mb-6">
          {/* Search */}
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5A6C7D]" />
            <Input
              placeholder={t.produktion.search}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white placeholder:text-[#5A6C7D] text-sm"
              data-testid="search-input"
            />
          </div>

          {/* Filter & View Toggle Row */}
          <div className="flex items-center gap-2">
            {/* Platform Filter */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="border-[#1A2B42] bg-[#1A2B42] text-[#A0AEC0] text-xs lg:text-sm h-9 lg:h-10 px-2 lg:px-3">
                  <Filter className="w-4 h-4 lg:mr-2" />
                  <span className="hidden lg:inline">{platformFilter ? (t.platforms?.[platformFilter] || platforms.find(p => p.key === platformFilter)?.label) : t.produktion.allPlatforms}</span>
                  <ChevronDown className="w-4 h-4 ml-1 lg:ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[#1A2B42] border-[#0A1628]">
                <DropdownMenuItem 
                  onClick={() => setPlatformFilter(null)}
                  className="text-white hover:bg-[#0A1628]"
                >
                  {t.produktion.allPlatforms}
                </DropdownMenuItem>
                {platforms.map(platform => (
                  <DropdownMenuItem 
                    key={platform.key}
                    onClick={() => setPlatformFilter(platform.key)}
                    className="text-white hover:bg-[#0A1628]"
                  >
                    {getPlatformIcon(platform.key)}
                    <span className="ml-2">{t.platforms?.[platform.key] || platform.label}</span>
                  </DropdownMenuItem>
                ))}
                {customChannels.map(c => (
                  <DropdownMenuItem 
                    key={c.id}
                    onClick={() => setPlatformFilter(c.id)}
                    className="text-white hover:bg-[#0A1628]"
                  >
                    <Layers className="w-4 h-4" style={{ color: '#A0AEC0' }} />
                    <span className="ml-2">{c.name}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* View Toggle */}
            <div className="flex gap-1 bg-[#1A2B42] p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-1.5 lg:p-2 ${viewMode === 'grid' ? 'bg-[#00D9C8] text-[#0A1628]' : 'text-[#A0AEC0]'}`}
                data-testid="grid-view-btn"
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-1.5 lg:p-2 ${viewMode === 'list' ? 'bg-[#00D9C8] text-[#0A1628]' : 'text-[#A0AEC0]'}`}
                data-testid="list-view-btn"
              >
                <List className="w-4 h-4" />
              </button>
            </div>

            {/* Select Mode Toggle */}
            <Button
              variant={isSelectMode ? 'default' : 'outline'}
              onClick={() => isSelectMode ? exitSelectMode() : setIsSelectMode(true)}
              className={isSelectMode 
                ? 'bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628] text-xs lg:text-sm h-9 lg:h-10'
                : 'border-[#1A2B42] bg-[#1A2B42] text-[#A0AEC0] text-xs lg:text-sm h-9 lg:h-10'
              }
              data-testid="select-mode-btn"
            >
              <Check className="w-4 h-4 lg:mr-1" />
              <span className="hidden lg:inline">{t.produktion.select}</span>
            </Button>
          </div>
        </div>

        {/* Status Tabs - Scrollable on mobile */}
        <div className="flex gap-1 mb-4 lg:mb-6 border-b border-[#1A2B42] overflow-x-auto pb-px">
          {statusTabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setStatusFilter(tab.key)}
              className={`px-3 lg:px-4 py-2 text-xs lg:text-sm transition-colors whitespace-nowrap ${
                statusFilter === tab.key
                  ? 'text-[#00D9C8] border-b-2 border-[#00D9C8]'
                  : 'text-[#A0AEC0] hover:text-white'
              }`}
              data-testid={`status-tab-${tab.key}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Folders & Tags Bar */}
        <div className="flex flex-col gap-3 mb-4">
          {/* Folders Row */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1" data-testid="folders-bar">
            <button
              onClick={() => setActiveFolder(null)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                !activeFolder ? 'bg-[#00D9C8]/20 text-[#00D9C8] border border-[#00D9C8]/40' : 'bg-[#1A2B42] text-[#A0AEC0] hover:text-white border border-transparent'
              }`}
              data-testid="folder-all-btn"
            >
              <FolderOpen className="w-3.5 h-3.5" />
              {lang === 'de' ? 'Alle' : 'All'}
            </button>
            {folders.map(folder => (
              <div key={folder.id} className="relative group flex items-center" data-testid={`folder-${folder.id}`}>
                {editingFolderId === folder.id ? (
                  <form onSubmit={(e) => { e.preventDefault(); handleRenameFolder(folder.id); }} className="flex items-center gap-1">
                    <Input
                      value={editingFolderName}
                      onChange={(e) => setEditingFolderName(e.target.value)}
                      className="h-7 w-28 text-xs bg-[#0A1628] border-[#00D9C8] text-white"
                      autoFocus
                      onBlur={() => { if (editingFolderName.trim()) handleRenameFolder(folder.id); else setEditingFolderId(null); }}
                      data-testid={`folder-rename-input-${folder.id}`}
                    />
                  </form>
                ) : (
                  <button
                    onClick={() => setActiveFolder(activeFolder === folder.id ? null : folder.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                      activeFolder === folder.id ? 'bg-[#00D9C8]/20 text-[#00D9C8] border border-[#00D9C8]/40' : 'bg-[#1A2B42] text-[#A0AEC0] hover:text-white border border-transparent'
                    }`}
                  >
                    <FolderOpen className="w-3.5 h-3.5" />
                    {folder.name}
                    <span className="text-[10px] opacity-60">({folder.content_count})</span>
                  </button>
                )}
                {editingFolderId !== folder.id && (
                  <div className="hidden group-hover:flex items-center ml-1 gap-0.5">
                    <button onClick={() => { setEditingFolderId(folder.id); setEditingFolderName(folder.name); }} className="p-0.5 text-[#5A6C7D] hover:text-white" data-testid={`folder-edit-${folder.id}`}>
                      <Pencil className="w-3 h-3" />
                    </button>
                    <button onClick={() => handleDeleteFolder(folder.id)} className="p-0.5 text-[#5A6C7D] hover:text-red-400" data-testid={`folder-delete-${folder.id}`}>
                      <Trash className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
            ))}
            {showNewFolderInput ? (
              <form onSubmit={(e) => { e.preventDefault(); handleCreateFolder(); }} className="flex items-center gap-1">
                <Input
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  placeholder={t.produktion.folderName}
                  className="h-7 w-32 text-xs bg-[#0A1628] border-[#1A2B42] text-white placeholder:text-[#5A6C7D]"
                  autoFocus
                  onBlur={() => { if (!newFolderName.trim()) setShowNewFolderInput(false); }}
                  data-testid="new-folder-input"
                />
                <Button type="submit" size="sm" className="h-7 px-2 bg-[#00D9C8] text-[#0A1628]" data-testid="new-folder-submit">
                  <Check className="w-3 h-3" />
                </Button>
              </form>
            ) : (
              <button
                onClick={() => setShowNewFolderInput(true)}
                className="flex items-center gap-1 px-2 py-1.5 rounded-full text-xs text-[#5A6C7D] hover:text-[#00D9C8] hover:bg-[#1A2B42] transition-colors"
                data-testid="add-folder-btn"
              >
                <FolderPlus className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Tags Row */}
          {tags.length > 0 && (
            <div className="flex items-center gap-2 overflow-x-auto pb-1" data-testid="tags-bar">
              <Tag className="w-3.5 h-3.5 text-[#5A6C7D] flex-shrink-0" />
              {tags.map(tag => (
                <button
                  key={tag.id}
                  onClick={() => setActiveTagFilter(activeTagFilter === tag.name ? null : tag.name)}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors border ${
                    activeTagFilter === tag.name 
                      ? 'border-current opacity-100' 
                      : 'border-transparent opacity-70 hover:opacity-100'
                  }`}
                  style={{ color: tag.color, backgroundColor: `${tag.color}15` }}
                  data-testid={`tag-filter-${tag.id}`}
                >
                  {tag.name}
                </button>
              ))}
              <button
                onClick={() => setShowTagManager(true)}
                className="flex items-center gap-1 px-2 py-1 rounded-full text-xs text-[#5A6C7D] hover:text-[#00D9C8] hover:bg-[#1A2B42] transition-colors"
                data-testid="manage-tags-btn"
              >
                <Plus className="w-3 h-3" />
              </button>
              {activeTagFilter && (
                <button onClick={() => setActiveTagFilter(null)} className="text-xs text-[#5A6C7D] hover:text-white ml-1">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          )}
          {tags.length === 0 && (
            <button
              onClick={() => setShowTagManager(true)}
              className="flex items-center gap-1.5 text-xs text-[#5A6C7D] hover:text-[#00D9C8] transition-colors"
              data-testid="create-first-tag-btn"
            >
              <Tag className="w-3.5 h-3.5" />
              {lang === 'de' ? 'Tags erstellen' : 'Create tags'}
            </button>
          )}
        </div>

        {/* Selection Bar - shown when items are selected */}
        {isSelectMode && (
          <div className="flex items-center justify-between bg-[#1A2B42] border border-[#00D9C8]/30 p-3 mb-4 rounded" data-testid="selection-bar">
            <div className="flex items-center gap-3">
              <button
                onClick={toggleSelectAll}
                className="w-5 h-5 border-2 border-[#00D9C8] rounded flex items-center justify-center transition-colors hover:bg-[#00D9C8]/20"
                data-testid="select-all-checkbox"
              >
                {selectedIds.size === filteredContent.length && filteredContent.length > 0 && (
                  <Check className="w-3 h-3 text-[#00D9C8]" />
                )}
                {selectedIds.size > 0 && selectedIds.size < filteredContent.length && (
                  <div className="w-2 h-2 bg-[#00D9C8] rounded-sm" />
                )}
              </button>
              <span className="text-sm text-white">
                {selectedIds.size > 0 
                  ? `${selectedIds.size} ${t.common.selected}`
                  : t.common.selectAll}
              </span>
            </div>
            {selectedIds.size > 0 && (
              <Button
                onClick={() => setShowBulkDeleteConfirm(true)}
                variant="destructive"
                size="sm"
                className="bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30"
                data-testid="bulk-delete-btn"
              >
                <Trash className="w-4 h-4 mr-1" />
                {t.common.delete} ({selectedIds.size})
              </Button>
            )}
          </div>
        )}

        {/* Undo Bar - 10s countdown after deletion */}
        {bulkDeletedIds && undoCountdown > 0 && (
          <div className="flex items-center justify-between bg-[#1A2B42] border border-amber-500/30 p-3 mb-4 rounded animate-in fade-in" data-testid="undo-bar">
            <span className="text-sm text-[#A0AEC0]">
              {bulkDeletedIds.length} {t.produktion.postsDeleted} ({undoCountdown}s)
            </span>
            <Button
              onClick={handleBulkUndo}
              variant="outline"
              size="sm"
              className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
              data-testid="undo-delete-btn"
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              {t.common.undo}
            </Button>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 mx-auto animate-spin text-[#00D9C8]" />
          </div>
        )}

        {/* Empty State */}
        {!isLoading && filteredContent.length === 0 && (
          <div className="text-center py-12">
            <FileEdit className="w-12 h-12 mx-auto mb-4 text-[#5A6C7D]" />
            <h3 className="text-lg font-bold text-white mb-2">{t.produktion.noContent}</h3>
            <p className="text-[#A0AEC0]">
              {searchQuery || statusFilter !== 'all' || platformFilter
                ? t.produktion.tryDifferentFilters
                : t.produktion.noContentDesc}
            </p>
          </div>
        )}

        {/* Grid View - 1 col on mobile (<768px), 2 cols on md, 3 cols on lg */}
        {!isLoading && viewMode === 'grid' && filteredContent.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-2 lg:gap-4">
            {filteredContent.map((item, index) => {
              // Extract first two sentences for preview
              const getPreviewText = (text) => {
                if (!text) return '';
                const sentences = text.split(/[.!?]+/).filter(s => s.trim());
                return sentences.slice(0, 2).join('. ').trim() + (sentences.length > 0 ? '.' : '');
              };
              const previewText = getPreviewText(item.text_content);
              
              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.03 }}
                >
                  <Card 
                    className={`bg-[#1A2B42] border-transparent hover:border-[#00D9C8] transition-colors cursor-pointer group h-full ${
                      isSelectMode && selectedIds.has(item.id) ? 'border-[#00D9C8] ring-1 ring-[#00D9C8]/50' : ''
                    }`}
                    onClick={() => isSelectMode ? toggleSelectId(item.id) : openDetailPanel(item)}
                    data-testid={`content-card-${item.id}`}
                  >
                    <div className="aspect-video bg-[#0A1628] relative overflow-hidden">
                      {/* Select checkbox overlay */}
                      {isSelectMode && (
                        <div className="absolute top-2 left-2 z-10">
                          <div className={`w-5 h-5 border-2 rounded flex items-center justify-center transition-colors ${
                            selectedIds.has(item.id) 
                              ? 'bg-[#00D9C8] border-[#00D9C8]' 
                              : 'border-white/60 bg-[#0A1628]/60'
                          }`} data-testid={`select-checkbox-${item.id}`}>
                            {selectedIds.has(item.id) && <Check className="w-3 h-3 text-[#0A1628]" />}
                          </div>
                        </div>
                      )}
                      {item.image_urls?.length > 0 ? (
                        <img 
                          src={(item.selected_image_urls && item.selected_image_urls.length > 0) ? item.selected_image_urls[0] : item.image_urls[0]} 
                          alt={item.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center">
                          {getPlatformIcon(item.platform, 'w-10 h-10')}
                          {item.image_prompt && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="mt-2 text-xs text-[#00D9C8] hover:bg-[#00D9C8]/10"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleGenerateImage(item.id);
                              }}
                              disabled={generatingImageId === item.id}
                              data-testid={`generate-image-card-${item.id}`}
                            >
                              {generatingImageId === item.id ? (
                                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                              ) : (
                                <ImageIcon className="w-3 h-3 mr-1" />
                              )}
                              {generatingImageId === item.id ? t.produktion.imageLoading : t.produktion.generateImage}
                            </Button>
                          )}
                        </div>
                      )}
                      {/* Content Number Badge */}
                      <div className="absolute top-2 right-2 flex flex-col items-end gap-1.5">
                        <div className="bg-[#0A1628]/90 px-2 py-1 rounded text-xs font-mono text-[#00D9C8]" data-testid={`content-number-${item.id}`}>
                          #{item.content_number || '—'}
                        </div>
                        {/* Generation-in-process sticker — shown in BOTH the
                            grid view and the detail panel until the image
                            actually arrives via the background poller. */}
                        {isImagePending(item.id) && (
                          <div
                            className="flex items-center gap-1.5 bg-[#00D9C8] px-2 py-1 rounded text-[10px] font-medium text-[#0A1628] shadow-md"
                            data-testid={`generation-badge-${item.id}`}
                          >
                            <Loader2 className="w-3 h-3 animate-spin" />
                            <span>Generation in process…</span>
                          </div>
                        )}
                      </div>
                      <div className="absolute top-2 left-2">
                        <div className="bg-[#0A1628]/80 p-1.5">
                          {getPlatformIcon(item.platform)}
                        </div>
                      </div>
                    </div>
                    <CardContent className="p-2 lg:p-4">
                      {(() => {
                        const pConfig = PLATFORM_CONFIG[item.platform];
                        const showHeadline = pConfig ? pConfig.hasHeadline : item.platform?.startsWith('custom_');
                        if (showHeadline) {
                          return (
                            <>
                              <h3 className="text-xs lg:text-sm font-medium text-white mb-1 lg:mb-2 leading-tight line-clamp-2">
                                {item.content_headline || item.title}
                              </h3>
                              {previewText && (
                                <p className="hidden lg:block text-xs text-[#A0AEC0] mb-3 line-clamp-3 leading-relaxed">
                                  {previewText}
                                </p>
                              )}
                            </>
                          );
                        }
                        return (
                          <p className="text-xs lg:text-sm text-[#A0AEC0] mb-3 line-clamp-3 leading-relaxed">
                            {previewText || item.text_content?.substring(0, 120)}
                          </p>
                        );
                      })()}
                      
                      <Badge className={statusConfig[item.status]?.color || 'bg-gray-500/20 text-gray-500'}>
                        {statusConfig[item.status]?.label || item.status}
                      </Badge>
                      {item.status === 'failed' && (
                        <div className="mt-2 flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid={`retry-generation-card-${item.id}`}
                            onClick={(e) => handleRetryGeneration(e, item)}
                            disabled={retryingIds.has(item.id)}
                            className="h-7 text-[11px] px-2 border-[#00D9C8]/40 text-[#00D9C8] hover:bg-[#00D9C8]/10"
                          >
                            {retryingIds.has(item.id) ? (
                              <>
                                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                {t.produktion.retry.starting}
                              </>
                            ) : (
                              <>
                                <RotateCcw className="w-3 h-3 mr-1" />
                                {t.produktion.retry.button}
                              </>
                            )}
                          </Button>
                        </div>
                      )}
                      {item.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {item.tags.slice(0, 3).map(tagName => {
                            const tagObj = tags.find(t => t.name === tagName);
                            return (
                              <span key={tagName} className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ color: tagObj?.color || '#A0AEC0', backgroundColor: `${tagObj?.color || '#A0AEC0'}15` }}>
                                {tagName}
                              </span>
                            );
                          })}
                          {item.tags.length > 3 && <span className="text-[10px] text-[#5A6C7D]">+{item.tags.length - 3}</span>}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* List View */}
        {!isLoading && viewMode === 'list' && filteredContent.length > 0 && (
          <div className="space-y-2">
            {filteredContent.map((item, index) => {
              // Extract first two sentences for preview
              const getPreviewText = (text) => {
                if (!text) return '';
                const sentences = text.split(/[.!?]+/).filter(s => s.trim());
                return sentences.slice(0, 2).join('. ').trim() + (sentences.length > 0 ? '.' : '');
              };
              const previewText = getPreviewText(item.text_content);
              
              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.02 }}
                  onClick={() => isSelectMode ? toggleSelectId(item.id) : openDetailPanel(item)}
                  className={`flex flex-wrap md:flex-nowrap items-start gap-2 md:gap-4 p-3 md:p-4 bg-[#1A2B42] hover:border-[#00D9C8] border cursor-pointer transition-colors ${
                    isSelectMode && selectedIds.has(item.id) ? 'border-[#00D9C8] ring-1 ring-[#00D9C8]/50' : 'border-transparent'
                  }`}
                  data-testid={`content-row-${item.id}`}
                >
                  {/* Selection checkbox */}
                  {isSelectMode && (
                    <div className={`w-5 h-5 border-2 rounded flex-shrink-0 flex items-center justify-center mt-1 transition-colors ${
                      selectedIds.has(item.id) 
                        ? 'bg-[#00D9C8] border-[#00D9C8]' 
                        : 'border-[#5A6C7D] bg-transparent'
                    }`} data-testid={`select-checkbox-${item.id}`}>
                      {selectedIds.has(item.id) && <Check className="w-3 h-3 text-[#0A1628]" />}
                    </div>
                  )}
                  {/* Content Number */}
                  <span className="text-xs font-mono text-[#00D9C8] flex-shrink-0 w-6 md:w-8">
                    #{item.content_number || '—'}
                  </span>
                  <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center mt-1">
                    {getPlatformIcon(item.platform)}
                  </div>
                  <div className="flex-1 min-w-0 basis-[calc(100%-88px)] md:basis-auto">
                    {(() => {
                      const pConfig = PLATFORM_CONFIG[item.platform];
                      const showHeadline = pConfig ? pConfig.hasHeadline : item.platform?.startsWith('custom_');
                      if (showHeadline) {
                        return (
                          <>
                            <h3 className="text-sm font-medium text-white mb-1 leading-tight line-clamp-2 md:line-clamp-none break-words">
                              {item.content_headline || item.title}
                            </h3>
                            {previewText && (
                              <p className="text-xs text-[#A0AEC0] line-clamp-2 leading-relaxed break-words">
                                {previewText}
                              </p>
                            )}
                          </>
                        );
                      }
                      return (
                        <p className="text-sm text-[#A0AEC0] line-clamp-2 leading-relaxed break-words">
                          {previewText || item.text_content?.substring(0, 120)}
                        </p>
                      );
                    })()}
                  </div>
                  <div className="hidden md:flex items-center gap-2 flex-shrink-0">
                    {isImagePending(item.id) && (
                      <span className="flex items-center gap-1 bg-[#00D9C8] px-2 py-0.5 rounded text-[10px] font-medium text-[#0A1628]" data-testid={`generation-badge-list-${item.id}`}>
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Generation in process…
                      </span>
                    )}
                    {item.tags?.slice(0, 2).map(tagName => {
                      const tagObj = tags.find(t => t.name === tagName);
                      return (
                        <span key={tagName} className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ color: tagObj?.color || '#A0AEC0', backgroundColor: `${tagObj?.color || '#A0AEC0'}15` }}>
                          {tagName}
                        </span>
                      );
                    })}
                  </div>
                  <Badge className={`flex-shrink-0 ${statusConfig[item.status]?.color || 'bg-gray-500/20 text-gray-500'}`}>
                    {statusConfig[item.status]?.label || item.status}
                  </Badge>
                  {item.status === 'failed' && (
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`retry-generation-list-${item.id}`}
                      onClick={(e) => handleRetryGeneration(e, item)}
                      disabled={retryingIds.has(item.id)}
                      className="h-7 text-[11px] px-2 border-[#00D9C8]/40 text-[#00D9C8] hover:bg-[#00D9C8]/10 flex-shrink-0"
                    >
                      {retryingIds.has(item.id) ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <>
                          <RotateCcw className="w-3 h-3 mr-1" />
                          {t.produktion.retry.buttonShort}
                        </>
                      )}
                    </Button>
                  )}
                  <span className="text-xs text-[#5A6C7D] flex-shrink-0">
                    {formatDate(item.created_at)}
                  </span>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Detail Panel - Full screen on mobile */}
      <AnimatePresence>
        {selectedContent && (
          <motion.div
            initial={{ x: 480 }}
            animate={{ x: 0 }}
            exit={{ x: 480 }}
            className="fixed right-0 top-0 bottom-0 w-full lg:w-[480px] bg-[#0A1628] border-l border-[#1A2B42] overflow-y-auto overflow-x-hidden z-50"
            data-testid="detail-panel"
          >
            {/* Panel Header */}
            <div className="sticky top-0 bg-[#0A1628] border-b border-[#1A2B42] p-3 lg:p-4 flex items-center justify-between">
              <div className="flex items-center gap-2 lg:gap-3 min-w-0">
                {getPlatformIcon(selectedContent.platform)}
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-xs lg:text-sm font-mono text-[#00D9C8]" data-testid="detail-content-number">
                    #{selectedContent.content_number || '—'}
                  </span>
                  <h2 className="font-bold text-white truncate text-sm lg:text-base max-w-[200px] lg:max-w-[280px]">
                    {selectedContent.title}
                  </h2>
                  {isImagePending(selectedContent.id) && (
                    <span
                      className="flex items-center gap-1 bg-[#00D9C8] px-2 py-0.5 rounded text-[10px] font-medium text-[#0A1628] flex-shrink-0"
                      data-testid={`generation-badge-detail-${selectedContent.id}`}
                    >
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Generation in process…
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={() => setShowExpandedModal(true)}
                  className="p-2 text-[#A0AEC0] hover:text-[#00D9C8] transition-colors"
                  title={t.produktion.expand}
                  data-testid="expand-modal-btn"
                >
                  <Maximize2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => { setMovingContentId(selectedContent.id); setShowFolderMoveModal(true); }}
                  className="p-2 text-[#A0AEC0] hover:text-[#00D9C8] transition-colors"
                  title={t.produktion.moveToFolder}
                  data-testid="move-to-folder-btn"
                >
                  <FolderInput className="w-4 h-4" />
                </button>
                <button 
                  onClick={closeDetailPanel}
                  className="p-2 text-[#A0AEC0] hover:text-white flex-shrink-0"
                  data-testid="close-panel-btn"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Plan-Leiste (30 Days Ahead): durchklicken, Termin ansehen/ändern, freigeben */}
            {planNav && selectedContent.plan_id === planNav.id && (() => {
              const items = planNav.items;
              const idx = items.findIndex(i => i.content_id === selectedContent.id);
              const approved = items.filter(i => i.status === 'approved').length;
              const isGenerating = selectedContent.status === 'generating';
              const isApproved = selectedContent.status === 'approved';
              const hasPrev = items.slice(0, Math.max(idx, 0)).some(i => i.content_id);
              const hasNext = items.slice(idx + 1).some(i => i.content_id);
              return (
                <div className="p-3 lg:p-4 border-b border-[#1A2B42] bg-[#0F1F35]" data-testid="plan-bar">
                  <div className="flex items-center justify-between mb-3">
                    <button onClick={() => goPlan(-1)} disabled={!hasPrev} className="p-2 rounded bg-[#1A2B42] text-white disabled:opacity-30 hover:bg-[#223650]" data-testid="plan-prev-btn" aria-label="zurück">
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <div className="text-center">
                      <div className="text-sm font-semibold text-white">{lang === 'de' ? 'Beitrag' : 'Post'} {idx + 1} {lang === 'de' ? 'von' : 'of'} {items.length}</div>
                      <div className="text-xs text-[#8B9BB0]">{approved} {lang === 'de' ? 'freigegeben' : 'approved'}</div>
                    </div>
                    <button onClick={() => goPlan(1)} disabled={!hasNext} className="p-2 rounded bg-[#1A2B42] text-white disabled:opacity-30 hover:bg-[#223650]" data-testid="plan-next-btn" aria-label="weiter">
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                  {selectedContent.plan_needs_input && !isApproved && (
                    <div className="mb-3 p-2.5 rounded bg-[#3A3220] text-[#F5D78E] text-xs">
                      {lang === 'de' ? 'Dieser Beitrag enthält Platzhalter in eckigen Klammern. Bitte vor der Freigabe ergänzen und speichern.' : 'This post contains placeholders in square brackets. Please fill them in and save before approving.'}
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <input type="date" value={planBarDate} onChange={(e) => setPlanBarDate(e.target.value)} disabled={isApproved || isGenerating}
                      className="bg-[#0A1628] border border-[#2D3F59] rounded px-2 py-2 text-sm text-white disabled:opacity-60" data-testid="plan-bar-date" />
                    <input type="time" value={planBarTime} onChange={(e) => setPlanBarTime(e.target.value)} disabled={isApproved || isGenerating}
                      className="bg-[#0A1628] border border-[#2D3F59] rounded px-2 py-2 text-sm text-white disabled:opacity-60" data-testid="plan-bar-time" />
                  </div>
                  {isApproved ? (
                    <div className="flex items-center justify-center gap-2 py-3 rounded bg-[#00D9C8]/15 text-[#00D9C8] font-semibold text-sm" data-testid="plan-bar-approved">
                      <Check className="w-4 h-4" />{lang === 'de' ? 'Freigegeben und eingeplant' : 'Approved and scheduled'}
                    </div>
                  ) : (
                    <button
                      onClick={async () => { planQuickRef.current = true; try { await handleApprove(); } finally { planQuickRef.current = false; } }}
                      disabled={isGenerating || isScheduling || !planBarDate || !planBarTime}
                      className="w-full py-3.5 rounded bg-[#00D9C8] hover:bg-[#00C4B4] text-[#0A1628] font-black text-base disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      data-testid="plan-approve-piece-btn"
                    >
                      {isGenerating ? <><Loader2 className="w-5 h-5 animate-spin" />{lang === 'de' ? 'Wird noch erzeugt …' : 'Still generating …'}</>
                        : isScheduling ? <Loader2 className="w-5 h-5 animate-spin" />
                        : <><Check className="w-5 h-5" />{lang === 'de' ? 'Freigeben' : 'Approve'}</>}
                    </button>
                  )}
                </div>
              );
            })()}

            {/* Tabs */}
            <div className="flex border-b border-[#1A2B42]">
              {['text', 'visual', 'meta', 'kommentare'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`flex-1 py-2 lg:py-3 text-xs lg:text-sm font-medium transition-colors ${
                    activeTab === tab
                      ? 'text-[#00D9C8] border-b-2 border-[#00D9C8]'
                      : 'text-[#A0AEC0] hover:text-white'
                  }`}
                  data-testid={`tab-${tab}`}
                >
                  {tab === 'text' ? 'Text' : tab === 'visual' ? (lang === 'de' ? 'Bild' : 'Image') : tab === 'meta' ? 'Meta' : (lang === 'de' ? 'Komm.' : 'Comm.')}
                  {tab === 'kommentare' && comments.length > 0 && (
                    <span className="ml-1 lg:ml-1.5 px-1 lg:px-1.5 py-0.5 text-[10px] lg:text-xs bg-[#00D9C8]/20 text-[#00D9C8] rounded-full">
                      {comments.length}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="p-3 lg:p-4 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 280px)' }}>
              {/* Generating placeholder — shown instead of an empty editor */}
              {activeTab === 'text' && selectedContent.status === 'generating' && (
                <div className="flex flex-col items-center justify-center text-center py-24 space-y-4" data-testid="content-generating-placeholder">
                  <Loader2 className="w-10 h-10 animate-spin text-[#00D9C8]" />
                  <p className="text-white font-semibold">
                    {lang === 'de' ? 'Dein Beitrag wird gerade generiert…' : 'Your post is being generated…'}
                  </p>
                  <p className="text-sm text-[#8B9BB0] max-w-xs">
                    {lang === 'de'
                      ? 'Das kann bis zu 3 Minuten dauern. Der Text erscheint hier automatisch, sobald er fertig ist.'
                      : 'This can take up to 3 minutes. The text will appear here automatically once it is ready.'}
                  </p>
                </div>
              )}

              {/* Text Tab */}
              {activeTab === 'text' && selectedContent.status !== 'generating' && (
                <div className="space-y-4">
                  {/* Anti-Halluzination: Quellen & Grounding (self-hiding wenn leer) */}
                  <GroundingSourcesPanel
                    sources={selectedContent.grounding_sources}
                    linkWarnings={selectedContent.link_warnings || []}
                    factWarnings={selectedContent.fact_warnings || []}
                  />
                  {/* Pressemitteilung (Press Release) - Structured Editor */}
                  {selectedContent.platform === 'pressemitteilung' ? (
                    <div className="space-y-5" data-testid="press-release-editor">
                      {PRESS_RELEASE_FIELDS.map(field => {
                        const value = editedStructuredContent[field.key] || '';
                        
                        // Dynamische Höhe basierend auf Feldtyp
                        const getMinHeight = () => {
                          if (field.key === 'body_text') return '280px';
                          if (field.key === 'short_text' || field.key === 'company_description') return '160px';
                          if (field.key === 'subtitle' || field.key === 'keywords') return '80px';
                          return '48px';
                        };
                        
                        return (
                          <div key={field.key} className="space-y-2">
                            <div className="flex items-center justify-between bg-[#0A1628] py-1">
                              <Label className="text-[#A0AEC0] text-sm font-medium flex items-center gap-2">
                                {field.label}
                                {field.optional && <span className="text-[#5A6C7D] text-xs">(optional)</span>}
                              </Label>
                              {field.multiline && (
                                <button
                                  onClick={() => handleRegenerateField(field.key, field.label)}
                                  disabled={regeneratingField === field.key}
                                  className="p-1 text-[#5A6C7D] hover:text-[#00D9C8] transition-colors"
                                  title={`${field.label} neu generieren`}
                                  data-testid={`regenerate-field-${field.key}`}
                                >
                                  {regeneratingField === field.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                                </button>
                              )}
                            </div>
                            {field.multiline ? (
                              <RichTextFieldEditor
                                content={value}
                                onChange={(html) => setEditedStructuredContent(prev => ({
                                  ...prev,
                                  [field.key]: html
                                }))}
                                maxChars={field.max}
                                placeholder={field.label}
                                minHeight={getMinHeight()}
                                testId={`press-release-${field.key}`}
                              />
                            ) : (
                              <Input
                                value={value}
                                onChange={(e) => setEditedStructuredContent(prev => ({
                                  ...prev,
                                  [field.key]: e.target.value
                                }))}
                                className={`bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white h-11 text-sm ${
                                  value.length > field.max ? 'ring-2 ring-red-400' : ''
                                }`}
                                placeholder={field.label}
                                data-testid={`press-release-${field.key}`}
                              />
                            )}
                          </div>
                        );
                      })}
                      
                      <div className="flex gap-2 pt-6 mt-4 border-t border-[#1A2B42]">
                        <Button
                          onClick={handleSaveStructuredContent}
                          disabled={isSaving}
                          className="flex-1 bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628]"
                          data-testid="save-press-release-btn"
                        >
                          {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : t.common.save}
                        </Button>
                        <Button
                          onClick={handleRegenerateText}
                          disabled={isRegenerating}
                          variant="outline"
                          className="border-[#00D9C8] text-[#00D9C8] hover:bg-[#00D9C8]/10"
                          data-testid="regenerate-press-release-btn"
                        >
                          {isRegenerating ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <RefreshCw className="w-4 h-4 mr-1" />
                          )}
                          {t.common.regenerate}
                        </Button>
                      </div>
                    </div>
                  ) : selectedContent.platform === 'pinterest' ? (
                    /* Pinterest - Structured Editor (Title + Description) */
                    <div className="space-y-5" data-testid="pinterest-editor">
                      {/* Pin Title */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label className="text-[#A0AEC0] text-sm font-medium">Pin-Titel</Label>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            (editedStructuredContent.pin_title || '').length > 100 
                              ? 'bg-red-500/20 text-red-400' 
                              : 'bg-[#1A2B42] text-[#5A6C7D]'
                          }`}>
                            {(editedStructuredContent.pin_title || '').length} / 100
                          </span>
                        </div>
                        <Input
                          value={editedStructuredContent.pin_title || ''}
                          onChange={(e) => setEditedStructuredContent(prev => ({
                            ...prev,
                            pin_title: e.target.value.slice(0, 100)
                          }))}
                          maxLength={100}
                          className="bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white h-11 text-sm"
                          placeholder="Ansprechender Pin-Titel..."
                          data-testid="pinterest-pin-title"
                        />
                      </div>
                      
                      {/* Pin Description */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label className="text-[#A0AEC0] text-sm font-medium">Pin-Beschreibung</Label>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            (editedStructuredContent.pin_description || '').length > 500 
                              ? 'bg-red-500/20 text-red-400' 
                              : 'bg-[#1A2B42] text-[#5A6C7D]'
                          }`}>
                            {(editedStructuredContent.pin_description || '').length} / 500
                          </span>
                        </div>
                        <Textarea
                          value={editedStructuredContent.pin_description || ''}
                          onChange={(e) => setEditedStructuredContent(prev => ({
                            ...prev,
                            pin_description: e.target.value.slice(0, 500)
                          }))}
                          maxLength={500}
                          style={{ minHeight: '160px' }}
                          className="bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white text-sm leading-relaxed resize-y"
                          placeholder="Inspirierende Beschreibung mit Mehrwert..."
                          data-testid="pinterest-pin-description"
                        />
                      </div>
                      
                      {/* Pin Link */}
                      <div className="space-y-2">
                        <Label className="text-[#A0AEC0] text-sm font-medium">Link (optional)</Label>
                        <Input
                          value={editedStructuredContent.pin_link || ''}
                          onChange={(e) => setEditedStructuredContent(prev => ({
                            ...prev,
                            pin_link: e.target.value
                          }))}
                          className="bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white h-11 text-sm"
                          placeholder="https://www.beispiel.de/seite"
                          data-testid="pinterest-pin-link"
                        />
                        <p className="text-[#5A6C7D] text-xs">Wohin soll der Pin verlinken?</p>
                      </div>

                      {(editedStructuredContent.pin_title || '').length > 100 && (
                        <p className="text-red-400 text-xs font-medium">
                          Pin-Titel: {(editedStructuredContent.pin_title || '').length - 100} Zeichen zu viel!
                        </p>
                      )}
                      
                      <div className="flex gap-2 pt-6 mt-4 border-t border-[#1A2B42]">
                        <Button
                          onClick={handleSaveStructuredContent}
                          disabled={isSaving || (editedStructuredContent.pin_title || '').length > 100 || (editedStructuredContent.pin_description || '').length > 500}
                          className="flex-1 bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628]"
                          data-testid="save-pinterest-btn"
                        >
                          {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : t.common.save}
                        </Button>
                        <Button
                          onClick={handleRegenerateText}
                          disabled={isRegenerating}
                          variant="outline"
                          className="border-[#00D9C8] text-[#00D9C8] hover:bg-[#00D9C8]/10"
                        >
                          {isRegenerating ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <RefreshCw className="w-4 h-4 mr-1" />
                          )}
                          {t.common.regenerate}
                        </Button>
                      </div>
                    </div>
                  ) : selectedContent.platform === 'youtube' ? (
                    /* YouTube - Script Editor */
                    <div className="space-y-5" data-testid="youtube-editor">
                      {selectedContent.status === 'video_generation' ? (
                        /* Read-only mode when submitted for generation */
                        <div className="space-y-4">
                          <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge className="bg-purple-500/20 text-purple-400">Zur Generierung</Badge>
                            </div>
                            <p className="text-sm text-purple-300">Dieses Skript wurde zur Video-Generierung aufgegeben und kann nicht mehr bearbeitet werden.</p>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-[#A0AEC0] text-sm font-medium">Video-Titel</Label>
                            <div className="bg-[#1A2B42] p-3 rounded text-white text-sm">
                              {editedStructuredContent.youtube_title || 'Kein Titel'}
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-[#A0AEC0] text-sm font-medium">Skript (max 30 Sek.)</Label>
                            <div className="bg-[#1A2B42] p-3 rounded text-white text-sm whitespace-pre-wrap">
                              {editedStructuredContent.youtube_script || 'Kein Skript'}
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-[#A0AEC0] text-sm font-medium">YouTube-Beschreibung</Label>
                            <div className="bg-[#1A2B42] p-3 rounded text-white text-sm whitespace-pre-wrap max-h-[150px] overflow-y-auto">
                              {editedStructuredContent.youtube_description || 'Keine Beschreibung'}
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-[#A0AEC0] text-sm font-medium">Tags</Label>
                            <div className="bg-[#1A2B42] p-3 rounded text-white text-sm">
                              {editedStructuredContent.youtube_tags || 'Keine Tags'}
                            </div>
                          </div>
                          {selectedContent.video_url ? (
                            <div className="space-y-2">
                              <Label className="text-[#A0AEC0] text-sm font-medium">Video</Label>
                              <video src={selectedContent.video_url} controls className="w-full rounded-lg border border-[#1A2B42]" />
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <Label className="text-[#A0AEC0] text-sm font-medium">Video</Label>
                              <label className="cursor-pointer" data-testid="youtube-video-upload-readonly">
                                <input type="file" accept="video/*" className="hidden"
                                  onChange={async (e) => {
                                    const f = e.target.files?.[0];
                                    if (!f) return;
                                    if (f.size > 100 * 1024 * 1024) { toast.error('Video zu groß (max. 100MB)'); return; }
                                    const fd = new FormData();
                                    fd.append('file', f);
                                    try {
                                      setIsSaving(true);
                                      const res = await axios.post(`${API}/content/${selectedContent.id}/upload-video`, fd, {
                                        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }, timeout: 120000
                                      });
                                      setSelectedContent(prev => ({ ...prev, video_url: res.data.video_url, status: 'draft' }));
                                      setContentPieces(prev => prev.map(c => c.id === selectedContent.id ? { ...c, video_url: res.data.video_url, status: 'draft' } : c));
                                      toast.success('Video hochgeladen');
                                    } catch (err) { toast.error(err.response?.data?.detail || 'Upload fehlgeschlagen'); }
                                    finally { setIsSaving(false); }
                                  }}
                                />
                                <div className="flex flex-col items-center justify-center gap-2 p-6 border-2 border-dashed border-[#1A2B42] rounded-lg hover:border-purple-500/40 transition-colors bg-[#0A1628]">
                                  <Upload className="w-8 h-8 text-[#5A6C7D]" />
                                  <span className="text-sm text-[#A0AEC0]">Video hochladen</span>
                                  <span className="text-xs text-[#5A6C7D]">MP4, MOV, etc. (max. 100MB)</span>
                                </div>
                              </label>
                            </div>
                          )}
                        </div>
                      ) : (
                        /* Editable mode */
                        <>
                          {/* Video Title */}
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <Label className="text-[#A0AEC0] text-sm font-medium">Video-Titel</Label>
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                (editedStructuredContent.youtube_title || '').length > 100 
                                  ? 'bg-red-500/20 text-red-400' : 'bg-[#1A2B42] text-[#5A6C7D]'
                              }`}>
                                {(editedStructuredContent.youtube_title || '').length} / 100
                              </span>
                            </div>
                            <Input
                              value={editedStructuredContent.youtube_title || ''}
                              onChange={(e) => setEditedStructuredContent(prev => ({
                                ...prev, youtube_title: e.target.value
                              }))}
                              className="bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white h-11 text-sm"
                              placeholder="Ansprechender YouTube-Titel..."
                              maxLength={100}
                              data-testid="youtube-title-input"
                            />
                          </div>
                          {/* Script */}
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <Label className="text-[#A0AEC0] text-sm font-medium">Skript (max 30 Sek.)</Label>
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                (editedStructuredContent.youtube_script || '').length > 450 
                                  ? 'bg-red-500/20 text-red-400' : 'bg-[#1A2B42] text-[#5A6C7D]'
                              }`}>
                                {(editedStructuredContent.youtube_script || '').length} / 450
                              </span>
                            </div>
                            <Textarea
                              value={editedStructuredContent.youtube_script || ''}
                              onChange={(e) => setEditedStructuredContent(prev => ({
                                ...prev, youtube_script: e.target.value
                              }))}
                              style={{ minHeight: '120px' }}
                              className="bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white text-sm leading-relaxed resize-y"
                              placeholder="Dein Video-Skript zum Vorlesen..."
                              data-testid="youtube-script-input"
                            />
                          </div>
                          {/* Description */}
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <Label className="text-[#A0AEC0] text-sm font-medium">YouTube-Beschreibung</Label>
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                (editedStructuredContent.youtube_description || '').length > 5000 
                                  ? 'bg-red-500/20 text-red-400' : 'bg-[#1A2B42] text-[#5A6C7D]'
                              }`}>
                                {(editedStructuredContent.youtube_description || '').length} / 5.000
                              </span>
                            </div>
                            <Textarea
                              value={editedStructuredContent.youtube_description || ''}
                              onChange={(e) => setEditedStructuredContent(prev => ({
                                ...prev, youtube_description: e.target.value
                              }))}
                              style={{ minHeight: '100px' }}
                              className="bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white text-sm leading-relaxed resize-y"
                              placeholder="YouTube-Beschreibung mit Links und Hashtags..."
                              data-testid="youtube-description-input"
                            />
                          </div>
                          {/* Tags */}
                          <div className="space-y-2">
                            <Label className="text-[#A0AEC0] text-sm font-medium">Tags (SEO)</Label>
                            <Input
                              value={editedStructuredContent.youtube_tags || ''}
                              onChange={(e) => setEditedStructuredContent(prev => ({
                                ...prev, youtube_tags: e.target.value
                              }))}
                              className="bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white h-11 text-sm"
                              placeholder="keyword1, keyword2, keyword3"
                              data-testid="youtube-tags-input"
                            />
                          </div>
                          {/* Video Upload / Preview */}
                          <div className="space-y-2">
                            <Label className="text-[#A0AEC0] text-sm font-medium">Video</Label>
                            {selectedContent.video_url ? (
                              <div className="space-y-2">
                                <video src={selectedContent.video_url} controls className="w-full rounded-lg border border-[#1A2B42]" />
                                <div className="flex items-center justify-between">
                                  <span className="text-xs text-green-400 flex items-center gap-1"><Check className="w-3 h-3" /> Video hochgeladen</span>
                                  <label className="cursor-pointer">
                                    <input type="file" accept="video/*" className="hidden" data-testid="youtube-video-replace"
                                      onChange={async (e) => {
                                        const f = e.target.files?.[0];
                                        if (!f) return;
                                        if (f.size > 100 * 1024 * 1024) { toast.error('Video zu groß (max. 100MB)'); return; }
                                        const fd = new FormData();
                                        fd.append('file', f);
                                        try {
                                          setIsSaving(true);
                                          const res = await axios.post(`${API}/content/${selectedContent.id}/upload-video`, fd, {
                                            headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }, timeout: 120000
                                          });
                                          setSelectedContent(prev => ({ ...prev, video_url: res.data.video_url, status: 'draft' }));
                                          setContentPieces(prev => prev.map(c => c.id === selectedContent.id ? { ...c, video_url: res.data.video_url, status: 'draft' } : c));
                                          toast.success('Video ersetzt');
                                        } catch (err) { toast.error(err.response?.data?.detail || 'Upload fehlgeschlagen'); }
                                        finally { setIsSaving(false); }
                                      }}
                                    />
                                    <span className="text-xs text-[#00D9C8] hover:underline">Video ersetzen</span>
                                  </label>
                                </div>
                              </div>
                            ) : (
                              <label className="cursor-pointer" data-testid="youtube-video-upload-label">
                                <input type="file" accept="video/*" className="hidden" data-testid="youtube-video-input"
                                  onChange={async (e) => {
                                    const f = e.target.files?.[0];
                                    if (!f) return;
                                    if (f.size > 100 * 1024 * 1024) { toast.error('Video zu groß (max. 100MB)'); return; }
                                    const fd = new FormData();
                                    fd.append('file', f);
                                    try {
                                      setIsSaving(true);
                                      const res = await axios.post(`${API}/content/${selectedContent.id}/upload-video`, fd, {
                                        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }, timeout: 120000
                                      });
                                      setSelectedContent(prev => ({ ...prev, video_url: res.data.video_url, status: 'draft' }));
                                      setContentPieces(prev => prev.map(c => c.id === selectedContent.id ? { ...c, video_url: res.data.video_url, status: 'draft' } : c));
                                      toast.success('Video hochgeladen');
                                    } catch (err) { toast.error(err.response?.data?.detail || 'Upload fehlgeschlagen'); }
                                    finally { setIsSaving(false); }
                                  }}
                                />
                                <div className="flex flex-col items-center justify-center gap-2 p-6 border-2 border-dashed border-[#1A2B42] rounded-lg hover:border-[#00D9C8]/40 transition-colors bg-[#0A1628]">
                                  <Upload className="w-8 h-8 text-[#5A6C7D]" />
                                  <span className="text-sm text-[#A0AEC0]">Video hochladen</span>
                                  <span className="text-xs text-[#5A6C7D]">MP4, MOV, etc. (max. 100MB)</span>
                                </div>
                              </label>
                            )}
                          </div>
                          <div className="flex gap-2 pt-6 mt-4 border-t border-[#1A2B42]">
                            <Button
                              onClick={handleSaveStructuredContent}
                              disabled={isSaving}
                              className="flex-1 bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628]"
                              data-testid="save-youtube-btn"
                            >
                              {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : t.common.save}
                            </Button>
                            <Button
                              onClick={handleRegenerateText}
                              disabled={isRegenerating}
                              variant="outline"
                              className="border-[#00D9C8] text-[#00D9C8] hover:bg-[#00D9C8]/10"
                            >
                              {isRegenerating ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <RefreshCw className="w-4 h-4 mr-1" />
                              )}
                              Neu generieren
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  ) : selectedContent.platform?.startsWith('custom_') ? (
                    /* Custom Channel - Structured Editor with custom fields */
                    <div className="space-y-5" data-testid="custom-channel-editor">
                      {(() => {
                        const cc = customChannels.find(c => c.id === selectedContent.platform);
                        if (!cc) return <p className="text-[#5A6C7D] text-sm">{lang === 'de' ? 'Kanal-Konfiguration nicht gefunden.' : 'Channel configuration not found.'}</p>;
                        const customFields = selectedContent.custom_fields || {};
                        return cc.fields.map((field, idx) => {
                          const fieldValue = editedStructuredContent[`custom_${field.name}`] ?? customFields[field.name] ?? '';
                          const charCount = fieldValue.length;
                          const isOverLimit = charCount > field.maxLength;
                          const isNearLimit = charCount > field.maxLength * 0.9;
                          return (
                            <div key={field.name} className="space-y-2">
                              <div className="flex items-center justify-between">
                                <Label className="text-[#A0AEC0] text-sm font-medium">{field.name}</Label>
                                <span className={`text-xs px-2 py-0.5 rounded ${
                                  isOverLimit ? 'bg-red-500/20 text-red-400' : 
                                  isNearLimit ? 'bg-yellow-500/20 text-yellow-400' : 
                                  'bg-[#1A2B42] text-[#5A6C7D]'
                                }`}>
                                  {charCount.toLocaleString()} / {field.maxLength.toLocaleString()}
                                </span>
                              </div>
                              {field.maxLength > 200 ? (
                                <Textarea
                                  value={fieldValue}
                                  onChange={(e) => {
                                    setEditedStructuredContent(prev => ({
                                      ...prev, [`custom_${field.name}`]: e.target.value
                                    }));
                                  }}
                                  style={{ minHeight: field.maxLength > 1000 ? '200px' : '100px' }}
                                  className={`bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white text-sm resize-y ${isOverLimit ? 'ring-2 ring-red-400' : ''}`}
                                  placeholder={field.name}
                                  data-testid={`custom-field-${field.name}`}
                                />
                              ) : (
                                <Input
                                  value={fieldValue}
                                  onChange={(e) => {
                                    setEditedStructuredContent(prev => ({
                                      ...prev, [`custom_${field.name}`]: e.target.value
                                    }));
                                  }}
                                  className={`bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white h-11 text-sm ${isOverLimit ? 'ring-2 ring-red-400' : ''}`}
                                  placeholder={field.name}
                                  data-testid={`custom-field-${field.name}`}
                                />
                              )}
                            </div>
                          );
                        });
                      })()}
                      <div className="flex gap-2 pt-6 mt-4 border-t border-[#1A2B42]">
                        <Button
                          onClick={handleSaveCustomFields}
                          disabled={isSaving}
                          className="flex-1 bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628]"
                          data-testid="save-custom-content-btn"
                        >
                          {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : t.common.save}
                        </Button>
                        <Button
                          onClick={handleRegenerateText}
                          disabled={isRegenerating}
                          variant="outline"
                          className="border-[#00D9C8] text-[#00D9C8] hover:bg-[#00D9C8]/10"
                          data-testid="regenerate-custom-content-btn"
                        >
                          {isRegenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                          {t.common.regenerate}
                        </Button>
                      </div>
                    </div>
                  ) : selectedContent.platform === 'produktbeschreibung' ? (
                    <ProductDescriptionViewer
                      ref={productViewerSidebarRef}
                      data={selectedContent.structured_content || {}}
                      factWarnings={selectedContent.fact_warnings || []}
                      heroImageUrl={(selectedContent.image_urls && selectedContent.image_urls[0]) || ''}
                      language={selectedContent.language}
                      contentId={selectedContent.id}
                      articleKey={`${selectedContent.id}-${selectedContent.updated_at || ''}`}
                      onSave={async (updated) => {
                        try {
                          const r = await axios.put(`${API}/content/${selectedContent.id}`,
                            { structured_content: updated },
                            { headers: getAuthHeader() });
                          if (r.data?.content) {
                            const newUpdatedAt = r.data.content.updated_at || new Date().toISOString();
                            setSelectedContent(prev => ({ ...prev, structured_content: updated, updated_at: newUpdatedAt }));
                            setContent(prev => prev.map(c => c.id === selectedContent.id ? { ...c, structured_content: updated, updated_at: newUpdatedAt } : c));
                          }
                          toast.success('Gespeichert');
                        } catch (e) {
                          toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
                          throw e;
                        }
                      }}
                      getAuthHeader={getAuthHeader}
                    />
                  ) : selectedContent.platform === 'newsletter' && selectedContent.blog_structured ? (
                    <NewsletterViewer
                      ref={blogViewerSidebarRef}
                      blogData={selectedContent.blog_structured}
                      fallbackTitle={selectedContent.title || ''}
                      heroImageUrl={(selectedContent.image_urls && selectedContent.image_urls[0]) || ''}
                      language={selectedContent.language}
                      brandName={authUser?.company_name || ''}
                      articleKey={`${selectedContent.id}-${selectedContent.updated_at || ''}`}
                      onSave={async (updated) => {
                        try {
                          const r = await axios.put(`${API}/content/${selectedContent.id}`,
                            { blog_structured: updated },
                            { headers: getAuthHeader() });
                          if (r.data?.content) {
                            const newUpdatedAt = r.data.content.updated_at || new Date().toISOString();
                            setSelectedContent(prev => ({ ...prev, blog_structured: updated, updated_at: newUpdatedAt, text_content: r.data.content.text_content || prev.text_content }));
                            setContent(prev => prev.map(c => c.id === selectedContent.id ? { ...c, blog_structured: updated, updated_at: newUpdatedAt, text_content: r.data.content.text_content || c.text_content } : c));
                          }
                          toast.success('Gespeichert');
                        } catch (e) {
                          toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
                          throw e;
                        }
                      }}
                    />
                  ) : selectedContent.platform === 'blog' ? (
                    <>
                    {selectedContent.blog_structured ? (
                      <BlogArticleViewer
                        ref={blogViewerSidebarRef}
                        blogData={selectedContent.blog_structured}
                        fallbackTitle={selectedContent.title || blogTitle || ''}
                        heroImageUrl={(selectedContent.image_urls && selectedContent.image_urls[0]) || ''}
                        primaryColor="#1e9ead"
                        language={selectedContent.language}
                        articleKey={`${selectedContent.id}-${selectedContent.updated_at || ''}`}
                        onSave={async (updated, cleanHtml) => {
                          try {
                            const r = await axios.put(`${API}/content/${selectedContent.id}`,
                              { blog_structured: updated, ...(cleanHtml ? { content_html: cleanHtml } : {}) },
                              { headers: getAuthHeader() });
                            if (r.data?.content) {
                              const newUpdatedAt = r.data.content.updated_at || new Date().toISOString();
                              setSelectedContent(prev => ({ ...prev, blog_structured: updated, content_html: cleanHtml || prev.content_html, updated_at: newUpdatedAt, text_content: r.data.content.text_content || prev.text_content }));
                              setContent(prev => prev.map(c => c.id === selectedContent.id ? { ...c, blog_structured: updated, content_html: cleanHtml || c.content_html, updated_at: newUpdatedAt, text_content: r.data.content.text_content || c.text_content } : c));
                            }
                            toast.success('Gespeichert');
                          } catch (e) {
                            toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
                            throw e;
                          }
                        }}
                      />
                    ) : (
                    /* Blog WYSIWYG Editor */
                    <>
                      {/* Blog Title */}
                      <div>
                        <Label className="text-[#A0AEC0] text-xs mb-1.5 block">Titel (optional)</Label>
                        <Input
                          value={blogTitle}
                          onChange={(e) => setBlogTitle(e.target.value)}
                          placeholder="Blogartikel-Titel eingeben..."
                          className="bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white text-lg font-semibold"
                          data-testid="blog-title-input"
                        />
                      </div>

                      {/* Blog Subtitle */}
                      <div>
                        <Label className="text-[#A0AEC0] text-xs mb-1.5 block">Überschrift (optional)</Label>
                        <Input
                          value={blogSubtitle}
                          onChange={(e) => setBlogSubtitle(e.target.value)}
                          placeholder="Untertitel / Überschrift eingeben..."
                          className="bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white text-base"
                          data-testid="blog-subtitle-input"
                        />
                      </div>

                      {/* WYSIWYG Rich Text Editor */}
                      <BlogEditor
                        content={editedText}
                        onChange={setEditedText}
                        maxChars={getPlatformLimit(selectedContent.platform)}
                      />

                      <div className="flex gap-2">
                        <Button
                          onClick={handleSaveText}
                          disabled={isSaving}
                          className="flex-1 bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628]"
                          data-testid="save-text-btn"
                        >
                          {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : t.common.save}
                        </Button>
                        <Button
                          onClick={handleRegenerateText}
                          disabled={isRegenerating}
                          variant="outline"
                          className="border-[#00D9C8] text-[#00D9C8] hover:bg-[#00D9C8]/10"
                          data-testid="regenerate-text-btn"
                        >
                          {isRegenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                          {t.common.regenerate}
                        </Button>
                      </div>
                    </>
                    )}
                    {/* WordPress-Publish läuft jetzt über die Ziel-Auswahl im
                        Schedule-/Freigeben-Dialog (striktes Entweder/Oder mit
                        Lovable-Website). Die alte Direkt-Sektion wurde entfernt. */}
                    {/* TODO: entfernen nach erfolgreichem Test des neuen Freigabe-Flows —
                        alter "An Website senden"-Direkt-Button. Ersetzt durch die Ziel-Chips
                        (Website) + Sofort/Geplant im Freigeben-/Schedule-Dialog. Komponente +
                        Import bleiben als Fallback erhalten, bis explizit "jetzt löschen".
                    <SupabasePublishSection
                      contentPiece={selectedContent}
                      getAuthHeader={getAuthHeader}
                      getContentHtml={() => blogViewerSidebarRef.current?.getCleanHtml?.()}
                      onPublished={(sb) => {
                        setSelectedContent(prev => ({ ...prev, ...sb }));
                        setContent(prev => prev.map(c => c.id === selectedContent.id ? { ...c, ...sb } : c));
                      }}
                    />
                    */}
                    </>
                  ) : selectedContent.platform === 'linkedin' ? (
                    /* LinkedIn dual-tab editor — Preview + Text — mirrors the
                       Blog editor structure (Preview / HTML). */
                    <div className="space-y-3" data-testid="linkedin-editor-wrapper">
                      <LinkedInPostEditor
                        text={editedText}
                        hashtags={selectedContent.content_hashtags || ''}
                        authorName={authUser?.company_name || authUser?.name || 'Dein Unternehmen'}
                        authorTagline={
                          (selectedContent.linkedin_planning?.stance)
                          || (lang === 'de' ? 'LinkedIn-Beitrag · Vorschau' : 'LinkedIn post · preview')
                        }
                        language={lang}
                        maxChars={getPlatformLimit(selectedContent.platform)}
                        minHeight={420}
                        contentKey={`${selectedContent.id}-${selectedContent.updated_at || ''}`}
                        onChange={(next) => setEditedText(next)}
                        onSave={async (next) => {
                          try {
                            setEditedText(next);
                            const r = await axios.put(
                              `${API}/content/${selectedContent.id}`,
                              { text_content: next, content_hashtags: '' },
                              { headers: getAuthHeader() }
                            );
                            const newUpdatedAt = r.data?.content?.updated_at || new Date().toISOString();
                            setSelectedContent(prev => ({ ...prev, text_content: next, content_hashtags: '', updated_at: newUpdatedAt }));
                            setContent(prev => prev.map(c => c.id === selectedContent.id ? { ...c, text_content: next, content_hashtags: '', updated_at: newUpdatedAt } : c));
                            toast.success(lang === 'de' ? 'Gespeichert' : 'Saved');
                          } catch (e) {
                            toast.error(e.response?.data?.detail || (lang === 'de' ? 'Speichern fehlgeschlagen' : 'Save failed'));
                            throw e;
                          }
                        }}
                      />
                      <div className="flex gap-2">
                        <Button
                          onClick={handleRegenerateText}
                          disabled={isRegenerating}
                          variant="outline"
                          className="border-[#00D9C8] text-[#00D9C8] hover:bg-[#00D9C8]/10"
                          data-testid="regenerate-text-btn"
                        >
                          {isRegenerating ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <RefreshCw className="w-4 h-4 mr-1" />
                          )}
                          {t.common.regenerate}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    /* Standard Text Editor for other platforms */
                    <>
                      <Textarea
                        value={editedText}
                        onChange={(e) => setEditedText(e.target.value)}
                        className="min-h-[300px] bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white resize-none"
                        data-testid="content-text-editor"
                      />
                      {/* Character Counter */}
                      {(() => {
                        const charCount = editedText.length;
                        const maxChars = getPlatformLimit(selectedContent.platform);
                        const isOverLimit = charCount > maxChars;
                        const isNearLimit = charCount > maxChars * 0.9;
                        
                        return (
                          <div className={`flex items-center justify-between text-xs px-2 py-1.5 rounded ${
                            isOverLimit ? 'bg-red-500/20 text-red-400' : 
                            isNearLimit ? 'bg-yellow-500/20 text-yellow-400' : 
                            'bg-[#0A1628] text-[#5A6C7D]'
                          }`}>
                            <span>
                              {charCount.toLocaleString()} / {maxChars.toLocaleString()} {t.produktion.charactersLabel}
                            </span>
                            {isOverLimit && (
                              <span className="font-medium">
                                {t.produktion.charsTooMany.replace('{n}', (charCount - maxChars).toLocaleString())}
                              </span>
                            )}
                          </div>
                        );
                      })()}

                      <div className="flex gap-2">
                        <Button
                          onClick={handleSaveText}
                          disabled={isSaving}
                          className="flex-1 bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628]"
                          data-testid="save-text-btn"
                        >
                          {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : t.common.save}
                        </Button>
                        <Button
                          onClick={handleRegenerateText}
                          disabled={isRegenerating}
                          variant="outline"
                          className="border-[#00D9C8] text-[#00D9C8] hover:bg-[#00D9C8]/10"
                          data-testid="regenerate-text-btn"
                        >
                          {isRegenerating ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <RefreshCw className="w-4 h-4 mr-1" />
                          )}
                          {t.common.regenerate}
                        </Button>
                      </div>
                    </>
                  )}

                  {/* KI-Anweisung — Text anpassen */}
                  <div className="bg-[#1A2B42]/50 rounded-lg p-3 space-y-2" data-testid="refine-text-section">
                    <div className="flex items-center gap-2 mb-1">
                      <Wand2 className="w-3.5 h-3.5 text-[#00D9C8]" />
                      <span className="text-xs text-[#A0AEC0] font-medium">
                        {t.produktion.aiRefineText}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <Input
                        value={refineInstruction}
                        onChange={(e) => setRefineInstruction(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && refineInstruction.trim()) { e.preventDefault(); handleRefine('text'); } }}
                        placeholder={t.produktion.aiRefinePlaceholder}
                        className="flex-1 bg-[#0A1628] border-transparent focus:border-[#00D9C8] text-white text-sm h-9"
                        disabled={isRefining}
                        data-testid="refine-instruction-input"
                      />
                      <Button
                        onClick={() => handleRefine('text')}
                        disabled={isRefining || !refineInstruction.trim()}
                        size="sm"
                        className="bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628] h-9 px-3"
                        data-testid="refine-text-btn"
                      >
                        {isRefining && refineType === 'text' ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Wand2 className="w-3.5 h-3.5" />
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Version History */}
                  {versions.length > 0 && (
                    <div>
                      <Label className="text-[#A0AEC0] text-xs mb-2 block">{t.produktion.versions}</Label>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm" className="w-full border-[#1A2B42] bg-[#1A2B42] text-[#A0AEC0]">
                            <Clock className="w-4 h-4 mr-2" />
                            {versions.length} {versions.length === 1 ? t.produktion.versionSingular : t.produktion.versionsLabel}
                            <ChevronDown className="w-4 h-4 ml-auto" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="bg-[#1A2B42] border-[#0A1628] w-full">
                          {versions.map(v => (
                            <DropdownMenuItem 
                              key={v.id}
                              onClick={() => loadVersion(v)}
                              className="text-white hover:bg-[#0A1628]"
                            >
                              Version {v.version_number} - {formatDate(v.created_at)}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  )}
                </div>
              )}

              {/* Visual Tab */}
              {activeTab === 'visual' && (
                <div className="space-y-4">
                  {/* Selection Info */}
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-[#A0AEC0]">
                      {selectedImages.length} {lang === 'de' ? 'von max.' : 'of max.'} {getMaxImages()} {lang === 'de' ? `Bild${getMaxImages() !== 1 ? 'ern' : ''}` : `image${getMaxImages() !== 1 ? 's' : ''}`} {lang === 'de' ? 'ausgewählt' : 'selected'}
                    </span>
                    {selectedImages.length > 0 && (
                      <button 
                        onClick={() => setSelectedImages([])}
                        className="text-[#00D9C8] hover:underline text-xs"
                      >
                        {t.produktion.clearSelection}
                      </button>
                    )}
                  </div>

                  {/* Images Grid with Selection */}
                  {selectedContent.image_urls?.length > 0 ? (
                    <div className="grid grid-cols-2 gap-3">
                      {selectedContent.image_urls.map((url, idx) => {
                        const isSelected = selectedImages.includes(url);
                        return (
                          <div 
                            key={idx} 
                            className={`relative group aspect-square bg-[#1A2B42] cursor-pointer transition-all ${
                              isSelected ? 'ring-2 ring-[#00D9C8]' : 'hover:ring-1 hover:ring-[#5A6C7D]'
                            }`}
                            onClick={() => toggleImageSelection(url)}
                          >
                            <img src={url} alt="" className="w-full h-full object-cover" />
                            
                            {/* Number badge */}
                            <div className="absolute top-2 left-2 w-6 h-6 bg-[#00D9C8] rounded-full flex items-center justify-center text-xs font-bold text-[#0A1628]">
                              {idx + 1}
                            </div>

                            {/* Selection Checkbox - Top Right */}
                            <div 
                              className={`absolute top-2 right-2 w-6 h-6 rounded flex items-center justify-center transition-all ${
                                isSelected 
                                  ? 'bg-[#00D9C8] text-[#0A1628]' 
                                  : 'bg-[#0A1628]/80 text-white border border-[#5A6C7D] group-hover:border-[#00D9C8]'
                              }`}
                            >
                              {isSelected && <Check className="w-4 h-4" />}
                            </div>

                            {/* Reorder buttons */}
                            {selectedContent.image_urls.length > 1 && (
                              <div className="absolute bottom-2 left-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                {idx > 0 && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const imgs = [...selectedContent.image_urls];
                                      [imgs[idx - 1], imgs[idx]] = [imgs[idx], imgs[idx - 1]];
                                      setSelectedContent(prev => ({ ...prev, image_urls: imgs }));
                                      setContentList(prev => prev.map(c => c.id === selectedContent.id ? { ...c, image_urls: imgs } : c));
                                      // Update selected images order accordingly
                                      setSelectedImages(prev => prev.map(u => u === imgs[idx] ? imgs[idx - 1] : u === imgs[idx - 1] ? imgs[idx] : u));
                                    }}
                                    className="w-6 h-6 bg-[#0A1628]/90 hover:bg-[#0A1628] rounded flex items-center justify-center text-white text-xs"
                                  >
                                    &larr;
                                  </button>
                                )}
                                {idx < selectedContent.image_urls.length - 1 && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const imgs = [...selectedContent.image_urls];
                                      [imgs[idx], imgs[idx + 1]] = [imgs[idx + 1], imgs[idx]];
                                      setSelectedContent(prev => ({ ...prev, image_urls: imgs }));
                                      setContentList(prev => prev.map(c => c.id === selectedContent.id ? { ...c, image_urls: imgs } : c));
                                      setSelectedImages(prev => prev.map(u => u === imgs[idx + 1] ? imgs[idx] : u === imgs[idx] ? imgs[idx + 1] : u));
                                    }}
                                    className="w-6 h-6 bg-[#0A1628]/90 hover:bg-[#0A1628] rounded flex items-center justify-center text-white text-xs"
                                  >
                                    &rarr;
                                  </button>
                                )}
                              </div>
                            )}

                            {/* Hover Actions */}
                            <div className="absolute bottom-2 right-2 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity flex gap-2">
                              <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setImageAiMode('prompt');
                                    setTimeout(() => {
                                      refineImageInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                      refineImageInputRef.current?.focus();
                                    }, 100);
                                  }}
                                  className="p-1.5 bg-[#0A1628]/90 hover:bg-[#00D9C8] transition-colors rounded"
                                  title={lang === 'de' ? 'Neues Bild generieren' : 'Generate new image'}
                                  data-testid={`magic-wand-image-${idx}`}
                                >
                                  <Wand2 className="w-3 h-3 text-white" />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingImageUrl(url);
                                    setImageAiMode('edit');
                                    setTimeout(() => {
                                      editImageInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                      editImageInputRef.current?.focus();
                                    }, 100);
                                  }}
                                  className="p-1.5 bg-[#0A1628]/90 hover:bg-[#7C3AED] transition-colors rounded"
                                  title={lang === 'de' ? 'Dieses Bild bearbeiten' : 'Edit this image'}
                                  data-testid={`edit-image-${idx}`}
                                >
                                  <Pencil className="w-3 h-3 text-white" />
                                </button>
                              <a 
                                href={url} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="p-1.5 bg-[#0A1628]/90 hover:bg-[#0A1628] rounded"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Maximize className="w-3 h-3 text-white" />
                              </a>
                              <a 
                                href={url} 
                                download 
                                className="p-1.5 bg-[#0A1628]/90 hover:bg-[#0A1628] rounded"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Download className="w-3 h-3 text-white" />
                              </a>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteImage(url);
                                }}
                                className="p-1.5 bg-[#0A1628]/90 hover:bg-red-500 transition-colors rounded"
                                title={lang === 'de' ? 'Bild löschen' : 'Delete image'}
                                data-testid={`delete-image-${idx}`}
                              >
                                <Trash className="w-3 h-3 text-white" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-[#1A2B42]">
                      <ImageIcon className="w-8 h-8 mx-auto mb-2 text-[#5A6C7D]" />
                      <p className="text-[#A0AEC0] text-sm">{lang === 'de' ? 'Keine Bilder vorhanden' : 'No images available'}</p>
                    </div>
                  )}

                  {/* Bild hinzufügen: 3 gleichwertige Wege */}
                  <div className="space-y-2" data-testid="image-add-section">
                    <span className="text-[11px] uppercase tracking-wider text-[#5A6C7D] font-medium">
                      {lang === 'de' ? 'Bild hinzufügen' : 'Add image'}
                    </span>
                    <div className="grid grid-cols-3 gap-2">
                      <Button
                        variant="outline"
                        onClick={() => handleGenerateImage(selectedContent.id)}
                        disabled={generatingImageId === selectedContent.id || !selectedContent.image_prompt}
                        className="h-auto flex-col gap-1.5 py-3 border-[#00D9C8]/40 text-[#00D9C8] hover:bg-[#00D9C8]/10 hover:text-[#00D9C8]"
                        data-testid="generate-image-btn"
                      >
                        {generatingImageId === selectedContent.id ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <ImageIcon className="w-5 h-5" />
                        )}
                        <span className="text-xs font-medium">
                          {generatingImageId === selectedContent.id
                            ? (lang === 'de' ? 'Generiere...' : 'Generating...')
                            : (lang === 'de' ? 'KI generieren' : 'AI generate')}
                        </span>
                      </Button>
                      <label className="block">
                        <Button variant="outline" className="w-full h-auto flex-col gap-1.5 py-3 border-[#1A2B42] text-[#A0AEC0] hover:text-white cursor-pointer" asChild>
                          <span>
                            <Upload className="w-5 h-5" />
                            <span className="text-xs font-medium">{lang === 'de' ? 'Hochladen' : 'Upload'}</span>
                          </span>
                        </Button>
                        <input type="file" accept="image/*" className="hidden" onChange={handleUploadImage} />
                      </label>
                      <Button
                        variant="outline"
                        onClick={() => setShowGalleryModal(true)}
                        className="h-auto flex-col gap-1.5 py-3 border-[#1A2B42] text-[#A0AEC0] hover:text-white"
                      >
                        <ImageIcon className="w-5 h-5" />
                        <span className="text-xs font-medium">{lang === 'de' ? 'Galerie' : 'Gallery'}</span>
                      </Button>
                    </div>
                    <button
                      onClick={() => setShowImageStyleModal(true)}
                      className="flex items-center gap-1.5 text-[11px] text-[#5A6C7D] hover:text-[#00D9C8] transition-colors"
                      title="Bildstil anpassen"
                      data-testid="image-style-btn"
                    >
                      <Wand2 className="w-3 h-3" />
                      {lang === 'de' ? 'Bildstil anpassen' : 'Adjust image style'}
                    </button>
                    <p className="text-[10px] text-[#5A6C7D] leading-relaxed" data-testid="generate-image-hint">
                      {lang === 'de'
                        ? 'Tipp: Für stilgetreuere Bilder zuerst ein Referenzbild anklicken.'
                        : 'Tip: For more on-brand images, click a reference image first.'}
                    </p>
                  </div>

                  {/* KI-Assistent: ein Bereich, zwei Modi */}
                  <div className="bg-[#1A2B42]/50 rounded-lg p-3 space-y-3" data-testid="refine-image-section">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[11px] uppercase tracking-wider text-[#5A6C7D] font-medium">
                        {lang === 'de' ? 'KI-Assistent' : 'AI assistant'}
                      </span>
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#F59E0B]/15 text-[#F59E0B] border border-[#F59E0B]/30 uppercase tracking-wide" data-testid="refine-image-beta-badge">
                        Beta
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-1 p-1 bg-[#0A1628] rounded-md">
                      <button
                        onClick={() => setImageAiMode('prompt')}
                        className={`flex items-center justify-center gap-1.5 py-1.5 rounded text-xs font-medium transition-colors ${imageAiMode === 'prompt' ? 'bg-[#00D9C8]/15 text-[#00D9C8]' : 'text-[#A0AEC0] hover:text-white'}`}
                        data-testid="ai-mode-prompt"
                      >
                        <Wand2 className="w-3.5 h-3.5" />
                        {lang === 'de' ? 'Neu prompten' : 'New prompt'}
                      </button>
                      <button
                        onClick={() => setImageAiMode('edit')}
                        className={`flex items-center justify-center gap-1.5 py-1.5 rounded text-xs font-medium transition-colors ${imageAiMode === 'edit' ? 'bg-[#7C3AED]/20 text-[#A78BFA]' : 'text-[#A0AEC0] hover:text-white'}`}
                        data-testid="ai-mode-edit"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                        {lang === 'de' ? 'Bild bearbeiten' : 'Edit image'}
                      </button>
                    </div>

                    {imageAiMode === 'prompt' && (
                      <div className="flex gap-2" data-testid="refine-image-prompt-row">
                        <Input
                          ref={refineImageInputRef}
                          value={refineImageInstruction}
                          onChange={(e) => setRefineImageInstruction(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && refineImageInstruction.trim()) { e.preventDefault(); handleRefine('image', refineImageInstruction); } }}
                          placeholder={t.produktion.aiGenerateImagePlaceholder}
                          className="flex-1 bg-[#0A1628] border-transparent focus:border-[#00D9C8] text-white text-sm h-9"
                          disabled={isRefining}
                          data-testid="refine-image-instruction-input"
                        />
                        <Button
                          onClick={() => handleRefine('image', refineImageInstruction)}
                          disabled={isRefining || !refineImageInstruction.trim()}
                          size="sm"
                          className="bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628] h-9 px-3"
                          data-testid="refine-image-btn"
                        >
                          {isRefining && refineType === 'image' ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Wand2 className="w-3.5 h-3.5" />
                          )}
                        </Button>
                      </div>
                    )}

                    {imageAiMode === 'edit' && (
                      <div className="space-y-2" data-testid="edit-image-section">
                        {!editingImageUrl ? (
                          <p className="text-xs text-[#5A6C7D]">
                            {lang === 'de'
                              ? 'Klicke auf das Stift-Icon eines Bildes oben, um es zu bearbeiten.'
                              : 'Click the pencil icon on an image above to edit it.'}
                          </p>
                        ) : (
                          <>
                            <div className="flex items-center gap-2">
                              <img src={editingImageUrl} className="w-8 h-8 rounded object-cover" alt="" />
                              <span className="text-xs text-[#A0AEC0] flex-1">
                                {lang === 'de' ? 'Ausgewähltes Bild' : 'Selected image'}
                              </span>
                              <button
                                onClick={() => { setEditingImageUrl(null); setEditImageInstruction(''); }}
                                className="text-[#5A6C7D] hover:text-white text-xs"
                              >
                                ✕
                              </button>
                            </div>
                            <div className="flex gap-2">
                              <Input
                                ref={editImageInputRef}
                                value={editImageInstruction}
                                onChange={(e) => setEditImageInstruction(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && !e.shiftKey && editImageInstruction.trim()) {
                                    e.preventDefault();
                                    handleEditImage();
                                  }
                                }}
                                placeholder={lang === 'de'
                                  ? 'z.B. "Hintergrund weiß machen", "Logo entfernen"'
                                  : 'e.g. "Make background white", "Remove logo"'}
                                className="flex-1 bg-[#0A1628] border-transparent focus:border-[#7C3AED] text-white text-sm h-9"
                                disabled={isEditingImage}
                                data-testid="edit-image-instruction-input"
                              />
                              <Button
                                onClick={handleEditImage}
                                disabled={isEditingImage || !editImageInstruction.trim()}
                                size="sm"
                                className="bg-[#7C3AED] hover:bg-[#7C3AED]/90 text-white h-9 px-3"
                                data-testid="edit-image-btn"
                              >
                                {isEditingImage ? (
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                  <Pencil className="w-3.5 h-3.5" />
                                )}
                              </Button>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Save Images: bewusst zurückhaltend */}
                  <div className="flex justify-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleSaveImages}
                      disabled={isSaving}
                      className="text-[#A0AEC0] hover:text-[#00D9C8] hover:bg-[#00D9C8]/10"
                      data-testid="save-images-btn"
                    >
                      {isSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                      {t.produktion.saveImages}
                    </Button>
                  </div>
                </div>
              )}

              {/* Meta Tab */}
              {activeTab === 'meta' && (
                <div className="space-y-4 min-w-0 max-w-full">
                  <div>
                    <Label className="text-[#A0AEC0] text-xs mb-2 block">{t.produktion.platform}</Label>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" className="w-full border-[#1A2B42] bg-[#1A2B42] text-white justify-between">
                          <span className="flex items-center gap-2">
                            {getPlatformIcon(editedMeta.platform)}
                            {t.platforms?.[editedMeta.platform] || platforms.find(p => p.key === editedMeta.platform)?.label}
                          </span>
                          <ChevronDown className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-[#1A2B42] border-[#0A1628]">
                        {activePlatforms.map(p => (
                          <DropdownMenuItem 
                            key={p.key}
                            onClick={() => {
                              const newConfig = getPlatformConfig(p.key);
                              const textContent = editedText || selectedContent?.text_content || '';
                              if (textContent.length > newConfig.maxLength) {
                                toast.warning(
                                  lang === 'de' 
                                    ? `Achtung: Dein Text hat ${textContent.length.toLocaleString()} Zeichen, ${newConfig.name || p.label} erlaubt max. ${newConfig.maxLength.toLocaleString()}.`
                                    : `Warning: Your text has ${textContent.length.toLocaleString()} characters, ${newConfig.name || p.label} allows max. ${newConfig.maxLength.toLocaleString()}.`
                                );
                              }
                              setEditedMeta(prev => ({ ...prev, platform: p.key }));
                            }}
                            className="text-white hover:bg-[#0A1628]"
                          >
                            {getPlatformIcon(p.key)}
                            <span className="ml-2">{t.platforms?.[p.key] || p.label}</span>
                            <span className="ml-auto text-[10px] text-[#5A6C7D]">{getPlatformConfig(p.key).maxLength}</span>
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    {/* Platform rules info */}
                    {editedMeta.platform && (() => {
                      const cfg = getPlatformConfig(editedMeta.platform);
                      const textLen = (editedText || selectedContent?.text_content || '').length;
                      const overLimit = textLen > cfg.maxLength;
                      return (
                        <div className={`mt-2 p-2 rounded text-xs ${overLimit ? 'bg-red-500/10 border border-red-500/30 text-red-400' : 'bg-[#0A1628] text-[#5A6C7D]'}`} data-testid="platform-rules-info">
                          <span>{t.common.max} {cfg.maxLength.toLocaleString()} {t.common.chars}</span>
                          {cfg.imageLimit > 0 && <span className="ml-3">| {cfg.imageLimit} {t.common.images}</span>}
                          {cfg.hashtags > 0 && <span className="ml-3">| {cfg.hashtags} Hashtags</span>}
                          {overLimit && <span className="ml-3 font-medium">({textLen.toLocaleString()} / {cfg.maxLength.toLocaleString()})</span>}
                        </div>
                      );
                    })()}
                  </div>

                  <div>
                    <Label className="text-[#A0AEC0] text-xs mb-2 block">{lang === 'de' ? 'Zielgruppe' : 'Target audience'}</Label>
                    <Input
                      value={editedMeta.target_audience}
                      onChange={(e) => setEditedMeta(prev => ({ ...prev, target_audience: e.target.value }))}
                      className="bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white"
                      placeholder={lang === 'de' ? 'z.B. Marketing Manager, Entscheider' : 'e.g. Marketing Manager, Decision Makers'}
                    />
                  </div>

                  <div>
                    <Label className="text-[#A0AEC0] text-xs mb-2 block">{lang === 'de' ? 'Ziel' : 'Goal'}</Label>
                    <Input
                      value={editedMeta.goal}
                      onChange={(e) => setEditedMeta(prev => ({ ...prev, goal: e.target.value }))}
                      className="bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white"
                      placeholder={lang === 'de' ? 'z.B. Lead Generation, Awareness' : 'e.g. Lead Generation, Awareness'}
                    />
                  </div>

                  <div className="min-w-0 w-full max-w-full">
                    <Label className="text-[#A0AEC0] text-xs mb-2 block">{t.produktion.scheduledDate}</Label>
                    <Input
                      type="datetime-local"
                      value={utcToLocalInput(editedMeta.scheduled_at)}
                      onChange={(e) => setEditedMeta(prev => ({ ...prev, scheduled_at: localInputToUTC(e.target.value) }))}
                      className="bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white !w-full min-w-0 max-w-full h-9 text-sm px-2 appearance-none [&::-webkit-date-and-time-value]:text-left [&::-webkit-calendar-picker-indicator]:opacity-70"
                      data-testid="meta-scheduled-date-input"
                    />
                  </div>

                  <div>
                    <Label className="text-[#A0AEC0] text-xs mb-2 block">{lang === 'de' ? 'Quellinformation' : 'Source information'}</Label>
                    <Textarea
                      value={editedMeta.source_info}
                      onChange={(e) => setEditedMeta(prev => ({ ...prev, source_info: e.target.value }))}
                      className="min-h-[80px] bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white resize-none"
                      placeholder={lang === 'de' ? 'Ursprüngliche Beschreibung oder Quelle' : 'Original description or source'}
                    />
                  </div>

                  {/* LinkedIn planning — shown only for LinkedIn posts that
                      were generated with the new Brand-DNA-first prompt. */}
                  {selectedContent.platform === 'linkedin' && selectedContent.linkedin_planning && (
                    <div
                      className="rounded-lg border border-[#1A2B42] bg-gradient-to-br from-[#0A1628] to-[#0C1A2E] p-3"
                      data-testid="linkedin-planning-section"
                    >
                      <Label className="text-[#00D9C8] text-xs mb-3 block flex items-center gap-1.5 uppercase tracking-wide">
                        <Sparkles className="w-3 h-3" />
                        {lang === 'de' ? 'LinkedIn-Planung des Generators' : 'LinkedIn planning by the generator'}
                      </Label>
                      <div className="space-y-2.5 text-xs">
                        {selectedContent.linkedin_planning.post_type && (
                          <div>
                            <div className="text-[#5A6C7D] mb-0.5">
                              {lang === 'de' ? 'Post-Typ' : 'Post type'}
                            </div>
                            <div className="text-white leading-snug" data-testid="linkedin-planning-post-type">
                              {selectedContent.linkedin_planning.post_type}
                            </div>
                          </div>
                        )}
                        {selectedContent.linkedin_planning.audience && (
                          <div>
                            <div className="text-[#5A6C7D] mb-0.5">
                              {lang === 'de' ? 'Zielgruppe' : 'Audience'}
                            </div>
                            <div className="text-white leading-snug" data-testid="linkedin-planning-audience">
                              {selectedContent.linkedin_planning.audience}
                            </div>
                          </div>
                        )}
                        {selectedContent.linkedin_planning.problem && (
                          <div>
                            <div className="text-[#5A6C7D] mb-0.5">
                              {lang === 'de' ? 'Problem / Frage' : 'Problem / question'}
                            </div>
                            <div className="text-white leading-snug" data-testid="linkedin-planning-problem">
                              {selectedContent.linkedin_planning.problem}
                            </div>
                          </div>
                        )}
                        {selectedContent.linkedin_planning.insight && (
                          <div>
                            <div className="text-[#5A6C7D] mb-0.5">
                              {lang === 'de' ? 'Erkenntnis' : 'Insight'}
                            </div>
                            <div className="text-white leading-snug" data-testid="linkedin-planning-insight">
                              {selectedContent.linkedin_planning.insight}
                            </div>
                          </div>
                        )}
                        {selectedContent.linkedin_planning.stance && (
                          <div>
                            <div className="text-[#5A6C7D] mb-0.5">
                              {lang === 'de' ? 'Markenhaltung' : 'Brand stance'}
                            </div>
                            <div className="text-white leading-snug" data-testid="linkedin-planning-stance">
                              {selectedContent.linkedin_planning.stance}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Tags */}
                  <div data-testid="meta-tags-section">
                    <Label className="text-[#A0AEC0] text-xs mb-2 block flex items-center gap-1.5">
                      <Tag className="w-3 h-3" /> Tags
                    </Label>
                    <div className="flex flex-wrap gap-1.5">
                      {tags.map(tag => {
                        const isActive = (selectedContent.tags || []).includes(tag.name);
                        return (
                          <button
                            key={tag.id}
                            onClick={() => handleToggleTag(selectedContent.id, tag.name)}
                            className={`text-xs px-2.5 py-1 rounded-full border transition-all ${
                              isActive 
                                ? 'border-current opacity-100' 
                                : 'border-transparent opacity-50 hover:opacity-80'
                            }`}
                            style={{ color: tag.color, backgroundColor: `${tag.color}15` }}
                            data-testid={`meta-tag-${tag.id}`}
                          >
                            {tag.name} {isActive && <span className="ml-0.5">&#10003;</span>}
                          </button>
                        );
                      })}
                      <button
                        onClick={() => setShowTagManager(true)}
                        className="text-xs px-2 py-1 rounded-full text-[#5A6C7D] hover:text-[#00D9C8] border border-dashed border-[#5A6C7D] hover:border-[#00D9C8] transition-colors"
                        data-testid="meta-add-tag-btn"
                      >
                        + Tag
                      </button>
                    </div>
                  </div>

                  {/* Folder */}
                  <div data-testid="meta-folder-section">
                    <Label className="text-[#A0AEC0] text-xs mb-2 block flex items-center gap-1.5">
                      <FolderOpen className="w-3 h-3" /> {lang === 'de' ? 'Ordner' : 'Folder'}
                    </Label>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="w-full border-[#1A2B42] bg-[#1A2B42] text-[#A0AEC0] justify-between text-xs">
                          <span className="flex items-center gap-1.5">
                            <FolderOpen className="w-3.5 h-3.5" />
                            {selectedContent.folder_id 
                              ? folders.find(f => f.id === selectedContent.folder_id)?.name || 'Ordner'
                              : t.common.noFolder}
                          </span>
                          <ChevronDown className="w-3.5 h-3.5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-[#1A2B42] border-[#0A1628]">
                        <DropdownMenuItem 
                          onClick={() => handleMoveToFolder(selectedContent.id, null)}
                          className="text-white hover:bg-[#0A1628] text-xs"
                        >
                          {t.common.noFolder}
                        </DropdownMenuItem>
                        {folders.map(folder => (
                          <DropdownMenuItem 
                            key={folder.id}
                            onClick={() => handleMoveToFolder(selectedContent.id, folder.id)}
                            className="text-white hover:bg-[#0A1628] text-xs"
                          >
                            <FolderOpen className="w-3.5 h-3.5 mr-1.5" />
                            {folder.name}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  <Button
                    onClick={handleSaveMeta}
                    disabled={isSaving}
                    className="w-full bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628]"
                  >
                    {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : t.common.save}
                  </Button>
                </div>
              )}

              {/* Comments Tab */}
              {activeTab === 'kommentare' && (
                <div className="space-y-4 flex flex-col h-full" data-testid="comments-tab">
                  {/* Comments List */}
                  <div className="flex-1 space-y-3 overflow-y-auto max-h-[400px]">
                    {isLoadingComments ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-[#00D9C8]" />
                      </div>
                    ) : comments.length > 0 ? (
                      comments.map(comment => (
                        <div 
                          key={comment.id} 
                          className="bg-[#1A2B42] p-3 rounded-lg group"
                          data-testid={`comment-${comment.id}`}
                        >
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-full bg-[#00D9C8]/20 flex items-center justify-center">
                                <span className="text-xs font-medium text-[#00D9C8]">
                                  {comment.user_name?.charAt(0)?.toUpperCase() || 'U'}
                                </span>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-white">
                                  {comment.user_name || 'Benutzer'}
                                </p>
                                <p className="text-xs text-[#5A6C7D]">
                                  {formatCommentDate(comment.created_at)}
                                </p>
                              </div>
                            </div>
                            <button
                              onClick={() => handleDeleteComment(comment.id)}
                              className="opacity-0 group-hover:opacity-100 p-1 text-[#5A6C7D] hover:text-red-400 transition-all"
                              data-testid={`delete-comment-${comment.id}`}
                            >
                              <Trash className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          <p className="text-sm text-[#A0AEC0] whitespace-pre-wrap pl-9">
                            {comment.text}
                          </p>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-12 bg-[#1A2B42] rounded-lg">
                        <MessageSquare className="w-10 h-10 mx-auto mb-3 text-[#5A6C7D]" />
                        <p className="text-[#A0AEC0] text-sm mb-1">{t.produktion.noComments}</p>
                        <p className="text-[#5A6C7D] text-xs">{t.produktion.firstComment}</p>
                      </div>
                    )}
                  </div>

                  {/* New Comment Input */}
                  <div className="border-t border-[#1A2B42] pt-4">
                    <div className="flex gap-2">
                      <Textarea
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder={t.produktion.commentPlaceholder}
                        className="flex-1 min-h-[80px] bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white resize-none text-sm"
                        data-testid="comment-input"
                      />
                    </div>
                    <Button
                      onClick={handlePostComment}
                      disabled={isPostingComment || !newComment.trim()}
                      className="w-full mt-2 bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628]"
                      data-testid="post-comment-btn"
                    >
                      {isPostingComment ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          {t.produktion.addComment}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Actions Bar */}
            <div className="sticky bottom-0 bg-[#0A1628] border-t border-[#1A2B42] p-4 space-y-3">
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={handleShareEmail}
                  className="flex-1 border-[#1A2B42] text-[#A0AEC0]"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  {lang === 'de' ? 'Per E-Mail teilen' : 'Share via email'}
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="border-[#1A2B42] text-[#A0AEC0]">
                      <Badge className={statusConfig[selectedContent.status]?.color}>
                        {statusConfig[selectedContent.status]?.label}
                      </Badge>
                      <ChevronDown className="w-4 h-4 ml-2" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-[#1A2B42] border-[#0A1628]">
                    {Object.entries(statusConfig)
                      .filter(([key]) => key !== 'generating')
                      .filter(([key]) => key !== 'video_generation' || selectedContent.platform === 'youtube')
                      .map(([key, config]) => (
                      <DropdownMenuItem 
                        key={key}
                        onClick={() => handleStatusChange(key)}
                        className="text-white hover:bg-[#0A1628]"
                      >
                        <Badge className={config.color}>{config.label}</Badge>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {selectedContent.platform === 'youtube' && selectedContent.status !== 'video_generation' ? (
                <Button
                  onClick={handleSubmitForGeneration}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold"
                  data-testid="submit-for-generation-btn"
                >
                  <FaYoutube className="w-4 h-4 mr-2" />
                  Zur Generierung aufgeben
                </Button>
              ) : selectedContent.platform === 'youtube' && selectedContent.status === 'video_generation' ? (
                <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3 text-center">
                  <p className="text-sm text-purple-300 font-medium">{t.produktion.videoGenerating}</p>
                </div>
              ) : (
                <Button
                  onClick={handleApprove}
                  disabled={selectedContent.status === 'generating'}
                  className="w-full bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628] font-semibold disabled:opacity-50"
                  data-testid="approve-btn"
                >
                  <Check className="w-4 h-4 mr-2" />
                  {t.common.approve}
                </Button>
              )}

              {selectedContent.platform !== 'youtube' && (
                <button
                  onClick={() => {
                    setScheduledContentId(selectedContent.id);
                    setAdditionalPlatforms([]);
                    setShowPlatformAdaptModal(true);
                  }}
                  className="w-full text-center text-sm text-[#5A6C7D] hover:text-[#00D9C8] py-2"
                  data-testid="adapt-platforms-btn"
                >
                  {lang === 'de' ? 'Für andere Kanäle umformatieren' : 'Adapt for other channels'}
                </button>
              )}

              <button
                onClick={handleDelete}
                className="w-full text-center text-sm text-[#5A6C7D] hover:text-red-400 py-2"
                data-testid="delete-btn"
              >
                <Trash className="w-4 h-4 inline mr-1" />
                {t.common.delete}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Gallery Modal */}
      <Dialog open={showGalleryModal} onOpenChange={setShowGalleryModal}>
        <DialogContent className="bg-[#1A2B42] border-[#0A1628] max-w-4xl w-[92vw] max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-white">{t.produktion.chooseFromGallery}</DialogTitle>
          </DialogHeader>
          <div className="columns-2 sm:columns-3 gap-3 overflow-y-auto p-1 flex-1 min-h-0">
            {gallery.filter(g => g.file_type === 'image').map(item => (
              <button
                key={item.id}
                onClick={() => handleAttachGalleryImage(item.file_url)}
                className="group mb-3 block w-full break-inside-avoid bg-[#0A1628] rounded-lg hover:ring-2 ring-[#00D9C8] transition-all overflow-hidden"
              >
                <img
                  src={item.file_url}
                  alt=""
                  loading="lazy"
                  className="w-full h-auto block group-hover:opacity-90 transition-opacity"
                />
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Approve Dialog - Legacy, kept for backwards compatibility */}
      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent className="bg-[#1A2B42] border-[#0A1628]">
          <DialogHeader>
            <DialogTitle className="text-white">{t.produktion.approveDialogTitle}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-[#A0AEC0] mb-4">
              {t.produktion.approveDialogQuestion}
            </p>
            <div className="grid grid-cols-2 gap-2">
              {activePlatforms.filter(p => p.key !== selectedContent?.platform).map(platform => {
                const Icon = platform.icon;
                const isSelected = additionalPlatforms.includes(platform.key);
                const isVideoLocked = platform.key === 'youtube' && !subscription.features?.video;
                return (
                  <button
                    key={platform.key}
                    onClick={() => {
                      if (isVideoLocked) { setShowVideoGate(true); return; }
                      setAdditionalPlatforms(prev =>
                        isSelected ? prev.filter(p => p !== platform.key) : [...prev, platform.key]
                      );
                    }}
                    className={`flex items-center gap-2 p-3 transition-all ${
                      isVideoLocked
                        ? 'bg-[#0A1628]/50 border border-[#1A2B42] opacity-50 cursor-not-allowed'
                        : isSelected
                          ? 'bg-[#00D9C8]/10 border border-[#00D9C8]'
                          : 'bg-[#0A1628] border border-transparent'
                    }`}
                    data-testid={`additional-platform-${platform.key}${isVideoLocked ? '-locked' : ''}`}
                  >
                    <Icon className="w-4 h-4" style={{ color: platform.color }} />
                    <span className="text-sm text-white">{t.platforms?.[platform.key] || platform.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApproveDialog(false)} className="border-[#1A2B42] text-[#A0AEC0]">
              {t.common.cancel}
            </Button>
            <Button onClick={confirmApprove} className="bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628]">
              {t.common.approve}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule Modal - New Flow */}
      <Dialog open={showScheduleModal} onOpenChange={setShowScheduleModal}>
        <DialogContent className="bg-[#1A2B42] border-[#0A1628] max-w-md w-[calc(100vw-1rem)] sm:w-full p-4 sm:p-6 overflow-y-auto max-h-[calc(100vh-2rem)]" data-testid="schedule-modal">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#00D9C8]" />
              {t.produktion.schedulePost}
            </DialogTitle>
            {selectedContent && (() => {
              const cfg = getPlatformConfig(selectedContent.platform);
              const Icon = cfg.icon;
              return (
                <div className="flex items-center gap-2 mt-1">
                  <Icon className="w-4 h-4" style={{ color: cfg.color }} />
                  <span className="text-sm text-[#A0AEC0]">{t.platforms?.[selectedContent.platform] || cfg.name}</span>
                </div>
              );
            })()}
          </DialogHeader>
          
          {isLoadingSuggestion ? (
            <div className="py-8 flex flex-col items-center">
              <Loader2 className="w-8 h-8 animate-spin text-[#00D9C8] mb-3" />
              <p className="text-[#A0AEC0]">{t.produktion.aiCalculatingTime}</p>
            </div>
          ) : (
            <div className={`py-4 ${selectedContent?.platform === 'blog' ? 'space-y-6' : 'space-y-4'}`}>
              {/* Blog destination: strict either/or between the connected
                  targets (Lovable-Website vs WordPress) + Sofort/Geplant. */}
              {selectedContent?.platform === 'blog' && (
                <>
                  <div>
                    <Label className="text-[#A0AEC0] text-xs mb-2 block">
                      {lang === 'de' ? 'Ziel' : 'Destination'}
                    </Label>
                    {blogTargetStatus.lovable === null && blogTargetStatus.wordpress === null && blogTargetStatus.shopify === null ? (
                      <span className="text-xs text-[#5A6C7D] flex items-center gap-1.5">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        {lang === 'de' ? 'Verbindungen werden geprüft…' : 'Checking connections…'}
                      </span>
                    ) : (!blogTargetStatus.lovable && !blogTargetStatus.wordpress && !blogTargetStatus.shopify && ghTargets.length === 0) ? (
                      <p className="text-xs text-[#F97066] flex items-start gap-1.5">
                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                        {lang === 'de'
                          ? 'Keine Website-Verbindung. Bitte zuerst unter Integrationen WordPress, Shopify oder Lovable verbinden.'
                          : 'No website connection. Connect WordPress, Shopify or Lovable under Integrations first.'}
                      </p>
                    ) : (
                      <div className="flex gap-2 flex-wrap">
                        {blogTargetStatus.lovable && (
                          <button
                            type="button"
                            onClick={() => setBlogTarget('lovable')}
                            className={`flex items-center gap-2 px-3 py-2 rounded border transition-all text-sm ${
                              blogTarget === 'lovable'
                                ? 'bg-[#00D9C8]/10 border-[#00D9C8] text-white'
                                : 'bg-[#0A1628] border-transparent text-[#A0AEC0] hover:border-[#5A6C7D]'
                            }`}
                            data-testid="schedule-target-lovable"
                          >
                            <Globe className="w-4 h-4 text-[#3ECF8E]" />
                            Lovable-Website
                            {blogTarget === 'lovable' && <Check className="w-3.5 h-3.5 ml-1" />}
                          </button>
                        )}
                        {blogTargetStatus.wordpress && (
                          <button
                            type="button"
                            onClick={() => setBlogTarget('wordpress')}
                            className={`flex items-center gap-2 px-3 py-2 rounded border transition-all text-sm ${
                              blogTarget === 'wordpress'
                                ? 'bg-[#00D9C8]/10 border-[#00D9C8] text-white'
                                : 'bg-[#0A1628] border-transparent text-[#A0AEC0] hover:border-[#5A6C7D]'
                            }`}
                            data-testid="schedule-target-wordpress"
                          >
                            <Globe className="w-4 h-4 text-[#21759B]" />
                            WordPress
                            {blogTarget === 'wordpress' && <Check className="w-3.5 h-3.5 ml-1" />}
                          </button>
                        )}
                        {blogTargetStatus.shopify && (
                          <button
                            type="button"
                            onClick={() => setBlogTarget('shopify')}
                            className={`flex items-center gap-2 px-3 py-2 rounded border transition-all text-sm ${
                              blogTarget === 'shopify'
                                ? 'bg-[#00D9C8]/10 border-[#00D9C8] text-white'
                                : 'bg-[#0A1628] border-transparent text-[#A0AEC0] hover:border-[#5A6C7D]'
                            }`}
                            data-testid="schedule-target-shopify"
                          >
                            <Globe className="w-4 h-4 text-[#95BF47]" />
                            Shopify
                            {blogTarget === 'shopify' && <Check className="w-3.5 h-3.5 ml-1" />}
                          </button>
                        )}
                        <GithubTargetButtons targets={ghTargets} value={blogTarget} onChange={setBlogTarget} lang={lang} />
                      </div>
                    )}
                    {blogTarget?.startsWith('github:') && (
                      <GithubTargetDetails
                        target={ghTargets.find(t => `github:${t.id}` === blogTarget)}
                        translationOf={blogTranslationOf}
                        onTranslationChange={setBlogTranslationOf}
                        lang={lang}
                      />
                    )}
                  </div>

                  <div>
                    <div className="flex gap-2">
                      {[
                        { key: 'now', label: lang === 'de' ? 'Sofort' : 'Now', Icon: Send },
                        { key: 'scheduled', label: lang === 'de' ? 'Geplant' : 'Scheduled', Icon: Clock },
                      ].map(({ key, label, Icon }) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setBlogPublishWhen(key)}
                          className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded border transition-all text-sm ${
                            blogPublishWhen === key
                              ? 'bg-[#00D9C8]/10 border-[#00D9C8] text-white'
                              : 'bg-[#0A1628] border-transparent text-[#A0AEC0] hover:border-[#5A6C7D]'
                          }`}
                          data-testid={`schedule-when-${key}`}
                        >
                          <Icon className="w-3.5 h-3.5" />
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Scheduling controls — hidden for a blog "sofort" publish */}
              {(selectedContent?.platform !== 'blog' || blogPublishWhen === 'scheduled') && (
                <>
                  {/* AI Suggestion Info */}
                  <div className="bg-[#0A1628] p-3 border-l-2 border-[#00D9C8]">
                    <p className="text-xs text-[#00D9C8] uppercase tracking-wider mb-1">{t.produktion.aiRecommendation}</p>
                    <p className="text-sm text-[#A0AEC0]">{
                      suggestedReason === 'Optimale Posting-Zeit basierend auf Plattform-Daten.'
                        ? t.produktion.aiReasonDefault
                        : suggestedReason
                    }</p>
                  </div>

                  {/* Date Input */}
                  <div className="min-w-0">
                    <Label className="text-[#A0AEC0] text-xs mb-2 block">{t.produktion.dateLabel}</Label>
                    <Input
                      type="date"
                      value={scheduleDate}
                      onChange={(e) => setScheduleDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="bg-[#0A1628] border-transparent focus:border-[#00D9C8] text-white w-full max-w-[240px] min-w-0 text-sm px-2"
                      data-testid="schedule-date-input"
                    />
                  </div>

                  {/* Time Input */}
                  <div className="min-w-0">
                    <Label className="text-[#A0AEC0] text-xs mb-2 block">{t.produktion.timeLabel}</Label>
                    <Input
                      type="time"
                      value={scheduleTime}
                      onChange={(e) => setScheduleTime(e.target.value)}
                      className="bg-[#0A1628] border-transparent focus:border-[#00D9C8] text-white w-full max-w-[240px] min-w-0 text-sm px-2"
                      data-testid="schedule-time-input"
                    />
                  </div>

                  {/* Preview */}
                  <div className="bg-[#0A1628] p-3 space-y-2">
                    <p className="text-xs text-[#5A6C7D] mb-1">{t.produktion.plannedPublication}</p>
                    <p className="text-white font-medium">
                      {scheduleDate && scheduleTime && (
                        <>
                          {new Date(`${scheduleDate}T${scheduleTime}`).toLocaleDateString(lang === 'de' ? 'de-DE' : 'en-US', {
                            weekday: 'long',
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          })} {t.produktion.atTime.replace('{time}', scheduleTime)}
                        </>
                      )}
                    </p>
                    {selectedContent && (
                      <p className="text-xs text-[#00D9C8] flex items-center gap-1.5 pt-1">
                        <Send className="w-3.5 h-3.5" />
                        {t.produktion.autoPostOn.replace('{platform}', (t.platforms?.[selectedContent.platform] || platforms.find(p => p.key === selectedContent.platform)?.label || selectedContent.platform))}
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {selectedContent && OPTION_PLATFORMS.includes(selectedContent.platform) && !isLoadingSuggestion && (
            <div className="pb-2" data-testid="schedule-platform-options">
              <PostOptionsSection
                ref={optionsRef}
                platform={selectedContent.platform}
                text={selectedContent.text_content || ''}
                imageUrls={selectedImages}
                videoUrl={selectedContent.video_url || ''}
                apiBase={API}
                getAuthHeader={getAuthHeader}
              />
            </div>
          )}
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowScheduleModal(false)}
              className="border-[#1A2B42] text-[#A0AEC0]"
            >
              {t.common.cancel}
            </Button>
            {selectedContent?.platform === 'blog' && blogPublishWhen === 'now' ? (
              <Button
                onClick={() => {
                  if (blogTarget?.startsWith('github:')) {
                    const n = new Date();
                    const pad = (x) => String(x).padStart(2, '0');
                    return handleScheduleContent(null, { date: `${n.getFullYear()}-${pad(n.getMonth() + 1)}-${pad(n.getDate())}`, time: `${pad(n.getHours())}:${pad(n.getMinutes())}` });
                  }
                  if (blogTarget === 'wordpress') return publishBlogToWordPressNow();
                  if (blogTarget === 'shopify') return publishBlogToShopifyNow();
                  return handlePublishWebsiteNow();
                }}
                disabled={isScheduling || isLoadingSuggestion || !blogTarget}
                className="bg-[#3ECF8E] hover:bg-[#34b87c] text-[#0A1628] font-medium"
                data-testid="schedule-publish-now-btn"
              >
                {isScheduling ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                {lang === 'de'
                  ? `Jetzt an ${blogTarget === 'wordpress' ? 'WordPress' : blogTarget === 'shopify' ? 'Shopify' : (blogTarget?.startsWith('github:') ? (ghTargets.find(t => `github:${t.id}` === blogTarget)?.name || 'Website') : 'Website')} senden`
                  : `Publish to ${blogTarget === 'wordpress' ? 'WordPress' : blogTarget === 'shopify' ? 'Shopify' : (blogTarget?.startsWith('github:') ? (ghTargets.find(t => `github:${t.id}` === blogTarget)?.name || 'website') : 'website')} now`}
              </Button>
            ) : (
              <Button
                onClick={() => handleScheduleContent()}
                disabled={isScheduling || isLoadingSuggestion || !scheduleDate || !scheduleTime || (selectedContent?.platform === 'blog' && !blogTarget)}
                className="bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628]"
                data-testid="schedule-confirm-btn"
              >
                {isScheduling ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                {t.produktion.scheduleInCalendar}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={showBulkDeleteConfirm} onOpenChange={setShowBulkDeleteConfirm}>
        <DialogContent className="bg-[#1A2B42] border-[#0A1628] max-w-sm" data-testid="bulk-delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              {t.produktion.bulkDeleteTitle}
            </DialogTitle>
          </DialogHeader>
          <p className="text-[#A0AEC0] text-sm py-2">
            {selectedIds.size} {lang === 'de' 
              ? `Beiträge werden permanent gelöscht. Das kann nicht rückgängig gemacht werden.`
              : `posts will be permanently deleted. This cannot be undone.`}
          </p>
          <div className="flex gap-2 mt-2">
            <Button
              variant="outline"
              onClick={() => setShowBulkDeleteConfirm(false)}
              className="flex-1 border-[#5A6C7D] text-[#A0AEC0]"
              data-testid="bulk-delete-cancel-btn"
            >
              {t.common.cancel}
            </Button>
            <Button
              onClick={handleBulkDelete}
              className="flex-1 bg-red-500 hover:bg-red-600 text-white"
              data-testid="bulk-delete-confirm-btn"
            >
              <Trash className="w-4 h-4 mr-1" />
              {t.produktion.deletePermanently}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Platform Adaptation Modal */}
      <Dialog open={showPlatformAdaptModal} onOpenChange={(open) => { if (!isAdapting && !open) { setShowPlatformAdaptModal(false); setScheduledContentId(null); } }}>
        <DialogContent className="bg-[#1A2B42] border-[#0A1628] max-w-md" data-testid="adapt-platforms-dialog">
          {isAdapting ? (
            <>
              <div className="flex flex-col items-center justify-center py-12 gap-4">
                <Loader2 className="w-10 h-10 animate-spin text-[#00D9C8]" />
                <p className="text-white font-medium text-center">
                  {lang === 'de' 
                    ? `Content wird für ${additionalPlatforms.length} ${additionalPlatforms.length === 1 ? 'Kanal' : 'Kanäle'} erstellt...`
                    : `Creating content for ${additionalPlatforms.length} channel${additionalPlatforms.length > 1 ? 's' : ''}...`}
                </p>
                <p className="text-[#5A6C7D] text-xs text-center">
                  {t.produktion.takeMoment}
                </p>
              </div>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="text-white">
                  {t.produktion.adaptChannels}
                </DialogTitle>
              </DialogHeader>
              
              <div className="py-4">
                <p className="text-[#A0AEC0] mb-4">
                  {lang === 'de'
                    ? 'Möchtest du diesen Content auch für andere Kanäle aufbereiten lassen? Die KI passt den Text automatisch an die jeweilige Plattform an.'
                    : 'Want to adapt this content for other channels? The AI will automatically adjust the text for each platform.'}
                </p>
                
                <div className="grid grid-cols-2 gap-2">
                  {activePlatforms.filter(p => p.key !== selectedContent?.platform).map(platform => {
                    const Icon = platform.icon;
                    const isSelected = additionalPlatforms.includes(platform.key);
                    const isVideoLocked = platform.key === 'youtube' && !subscription.features?.video;
                    return (
                      <button
                        key={platform.key}
                        data-testid={`adapt-platform-${platform.key}${isVideoLocked ? '-locked' : ''}`}
                        onClick={() => {
                          if (isVideoLocked) { setShowVideoGate(true); return; }
                          setAdditionalPlatforms(prev =>
                            isSelected ? prev.filter(p => p !== platform.key) : [...prev, platform.key]
                          );
                        }}
                        className={`flex items-center gap-2 p-3 transition-all ${
                          isVideoLocked
                            ? 'bg-[#0A1628]/50 border border-[#1A2B42] opacity-50 cursor-not-allowed'
                            : isSelected
                              ? 'bg-[#00D9C8]/10 border border-[#00D9C8]'
                              : 'bg-[#0A1628] border border-transparent hover:border-[#5A6C7D]'
                        }`}
                      >
                        <Icon className="w-4 h-4" style={{ color: platform.color }} />
                        <span className="text-sm text-white">{t.platforms?.[platform.key] || platform.label}</span>
                        {isSelected && <Check className="w-4 h-4 text-[#00D9C8] ml-auto" />}
                      </button>
                    );
                  })}
                </div>
              </div>
              
              <DialogFooter className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={skipPlatformAdaption}
                  className="border-[#1A2B42] text-[#A0AEC0] flex-1"
                >
                  {t.produktion.noThanks}
                </Button>
                <Button 
                  onClick={handleAdaptPlatforms}
                  disabled={additionalPlatforms.length === 0}
                  className="bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628] flex-1"
                  data-testid="adapt-platforms-submit-btn"
                >
                  {additionalPlatforms.length > 0 
                    ? `${additionalPlatforms.length} ${t.produktion.channelsAdapt}`
                    : t.produktion.selectChannels
                  }
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Conflict Dialog - duplicate schedule detection */}
      <Dialog open={showConflictDialog} onOpenChange={(open) => { if (!open) { setShowConflictDialog(false); setConflictData(null); } }}>
        <DialogContent className="bg-[#1A2B42] border-[#0A1628] max-w-md" data-testid="conflict-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              {t.produktion.postAlreadyScheduled}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-[#A0AEC0] text-sm">
              {conflictData?.message || (lang === 'de' 
                ? 'Dieser Beitrag ist bereits für diesen Tag auf dieser Plattform eingeplant.' 
                : 'This post is already scheduled for this day on this platform.')}
            </p>
            {conflictData?.existingScheduledAt && (
              <p className="text-[#A0AEC0] text-xs mt-2">
                {t.produktion.scheduledAt}
                <span className="text-white font-medium">
                  {new Date(conflictData.existingScheduledAt).toLocaleString(lang === 'de' ? 'de-DE' : 'en-US', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </span>
              </p>
            )}
          </div>
          
          <div className="flex flex-col gap-2">
            <Button
              onClick={handleConflictReplace}
              disabled={isScheduling}
              className="bg-amber-500 hover:bg-amber-600 text-[#0A1628] w-full font-medium"
              data-testid="conflict-replace-btn"
            >
              {isScheduling ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {t.produktion.replaceScheduled}
            </Button>
            <Button
              onClick={handleConflictSaveDraft}
              variant="outline"
              className="border-[#5A6C7D] text-[#A0AEC0] hover:text-white w-full"
              data-testid="conflict-save-draft-btn"
            >
              {t.produktion.saveAsDraft}
            </Button>
            <Button
              onClick={() => { setShowConflictDialog(false); setConflictData(null); }}
              variant="ghost"
              className="text-[#5A6C7D] hover:text-[#A0AEC0] w-full"
              data-testid="conflict-cancel-btn"
            >
              {t.common.cancel}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Expanded Content Modal */}
      <Dialog open={showExpandedModal} onOpenChange={setShowExpandedModal}>
        <DialogContent className="bg-[#0A1628] border-[#1A2B42] max-w-4xl w-[95vw] max-h-[90vh] overflow-hidden flex flex-col" data-testid="expanded-modal">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-white flex items-center gap-3">
              {selectedContent && getPlatformIcon(selectedContent?.platform)}
              <span className="font-mono text-[#00D9C8] text-sm">#{selectedContent?.content_number || '—'}</span>
              <span className="truncate">{selectedContent?.title}</span>
            </DialogTitle>
          </DialogHeader>
          {selectedContent && (
            <div className="flex-1 overflow-y-auto p-1 space-y-4" data-testid="expanded-modal-content">
              {/* Image */}
              {selectedContent.image_urls?.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {selectedContent.image_urls.map((url, i) => (
                    <img key={i} src={url} alt="" className="w-full rounded-lg border border-[#1A2B42] object-cover aspect-video" />
                  ))}
                </div>
              )}

              {/* Anti-Halluzination: Quellen & Grounding (self-hiding wenn leer) */}
              <GroundingSourcesPanel
                sources={selectedContent.grounding_sources}
                linkWarnings={selectedContent.link_warnings || []}
                factWarnings={selectedContent.fact_warnings || []}
              />

              {/* Pressemitteilung expanded */}
              {selectedContent.platform === 'pressemitteilung' && selectedContent.structured_content ? (
                <div className="space-y-4">
                  {PRESS_RELEASE_FIELDS.map(field => {
                    const val = editedStructuredContent[field.key] || '';
                    return (
                      <div key={field.key}>
                        <Label className="text-[#A0AEC0] text-xs mb-1.5 block">{field.label}</Label>
                        {field.multiline ? (
                          <RichTextFieldEditor
                            content={val}
                            onChange={(html) => setEditedStructuredContent(prev => ({ ...prev, [field.key]: html }))}
                            maxChars={field.max}
                            placeholder={field.label}
                            minHeight={field.key === 'body_text' ? '350px' : '180px'}
                            testId={`expanded-press-${field.key}`}
                          />
                        ) : (
                          <Input
                            value={val}
                            onChange={(e) => setEditedStructuredContent(prev => ({ ...prev, [field.key]: e.target.value }))}
                            className="bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white h-11 text-sm"
                            data-testid={`expanded-press-${field.key}`}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : selectedContent.platform === 'produktbeschreibung' ? (
                <ProductDescriptionViewer
                  ref={productViewerExpandedRef}
                  data={selectedContent.structured_content || {}}
                  factWarnings={selectedContent.fact_warnings || []}
                  heroImageUrl={(selectedContent.image_urls && selectedContent.image_urls[0]) || ''}
                  language={selectedContent.language}
                  contentId={selectedContent.id}
                  articleKey={`${selectedContent.id}-${selectedContent.updated_at || ''}`}
                  onSave={async (updated) => {
                    try {
                      const r = await axios.put(`${API}/content/${selectedContent.id}`,
                        { structured_content: updated },
                        { headers: getAuthHeader() });
                      if (r.data?.content) {
                        const newUpdatedAt = r.data.content.updated_at || new Date().toISOString();
                        setSelectedContent(prev => ({ ...prev, structured_content: updated, updated_at: newUpdatedAt }));
                        setContent(prev => prev.map(c => c.id === selectedContent.id ? { ...c, structured_content: updated, updated_at: newUpdatedAt } : c));
                      }
                      toast.success('Gespeichert');
                    } catch (e) {
                      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
                      throw e;
                    }
                  }}
                  getAuthHeader={getAuthHeader}
                />
              ) : selectedContent.platform === 'newsletter' && selectedContent.blog_structured ? (
                <NewsletterViewer
                  ref={blogViewerExpandedRef}
                  blogData={selectedContent.blog_structured}
                  fallbackTitle={selectedContent.title || ''}
                  heroImageUrl={(selectedContent.image_urls && selectedContent.image_urls[0]) || ''}
                  language={selectedContent.language}
                  brandName={authUser?.company_name || ''}
                  articleKey={`${selectedContent.id}-${selectedContent.updated_at || ''}`}
                  onSave={async (updated) => {
                    try {
                      const r = await axios.put(`${API}/content/${selectedContent.id}`,
                        { blog_structured: updated },
                        { headers: getAuthHeader() });
                      if (r.data?.content) {
                        const newUpdatedAt = r.data.content.updated_at || new Date().toISOString();
                        setSelectedContent(prev => ({ ...prev, blog_structured: updated, updated_at: newUpdatedAt, text_content: r.data.content.text_content || prev.text_content }));
                        setContent(prev => prev.map(c => c.id === selectedContent.id ? { ...c, blog_structured: updated, updated_at: newUpdatedAt, text_content: r.data.content.text_content || c.text_content } : c));
                      }
                      toast.success('Gespeichert');
                    } catch (e) {
                      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
                      throw e;
                    }
                  }}
                />
              ) : selectedContent.platform === 'blog' ? (
                <>
                {selectedContent.blog_structured ? (
                  <BlogArticleViewer
                    ref={blogViewerExpandedRef}
                    blogData={selectedContent.blog_structured}
                    fallbackTitle={selectedContent.title || blogTitle || ''}
                    heroImageUrl={(selectedContent.image_urls && selectedContent.image_urls[0]) || ''}
                    primaryColor="#1e9ead"
                    language={selectedContent.language}
                    articleKey={`${selectedContent.id}-${selectedContent.updated_at || ''}`}
                    onSave={async (updated, cleanHtml) => {
                      try {
                        const r = await axios.put(`${API}/content/${selectedContent.id}`,
                          { blog_structured: updated, ...(cleanHtml ? { content_html: cleanHtml } : {}) },
                          { headers: getAuthHeader() });
                        if (r.data?.content) {
                          const newUpdatedAt = r.data.content.updated_at || new Date().toISOString();
                          setSelectedContent(prev => ({ ...prev, blog_structured: updated, content_html: cleanHtml || prev.content_html, updated_at: newUpdatedAt, text_content: r.data.content.text_content || prev.text_content }));
                          setContent(prev => prev.map(c => c.id === selectedContent.id ? { ...c, blog_structured: updated, content_html: cleanHtml || c.content_html, updated_at: newUpdatedAt, text_content: r.data.content.text_content || c.text_content } : c));
                        }
                        toast.success('Gespeichert');
                      } catch (e) {
                        toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
                        throw e;
                      }
                    }}
                  />
                ) : (
                <div className="space-y-3">
                  <div>
                    <Label className="text-[#A0AEC0] text-xs mb-1.5 block">Titel</Label>
                    <Input value={blogTitle} onChange={(e) => setBlogTitle(e.target.value)} className="bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white text-lg font-semibold" />
                  </div>
                  <div>
                    <Label className="text-[#A0AEC0] text-xs mb-1.5 block">Überschrift</Label>
                    <Input value={blogSubtitle} onChange={(e) => setBlogSubtitle(e.target.value)} className="bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white" />
                  </div>
                  <BlogEditor content={editedText} onChange={setEditedText} maxChars={getPlatformLimit(selectedContent.platform)} />
                </div>
                )}
                {/* WordPress-Publish läuft jetzt über die Ziel-Auswahl im
                    Schedule-/Freigeben-Dialog. Die alte Direkt-Sektion wurde entfernt. */}
                {/* TODO: entfernen nach erfolgreichem Test des neuen Freigabe-Flows —
                    alter "An Website senden"-Direkt-Button. Ersetzt durch die Ziel-Chips
                    (Website) + Sofort/Geplant im Freigeben-/Schedule-Dialog. Komponente +
                    Import bleiben als Fallback erhalten, bis explizit "jetzt löschen".
                <SupabasePublishSection
                  contentPiece={selectedContent}
                  getAuthHeader={getAuthHeader}
                  getContentHtml={() => blogViewerExpandedRef.current?.getCleanHtml?.()}
                  onPublished={(sb) => {
                    setSelectedContent(prev => ({ ...prev, ...sb }));
                    setContent(prev => prev.map(c => c.id === selectedContent.id ? { ...c, ...sb } : c));
                  }}
                />
                */}
                </>
              ) : selectedContent.platform === 'pinterest' ? (
                <div className="space-y-4">
                  <div>
                    <Label className="text-[#A0AEC0] text-xs mb-1.5 block">Pin-Titel</Label>
                    <Input
                      value={editedStructuredContent.pin_title || ''}
                      onChange={(e) => setEditedStructuredContent(prev => ({ ...prev, pin_title: e.target.value.slice(0, 100) }))}
                      maxLength={100}
                      className="bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white h-11 text-sm"
                      data-testid="expanded-pinterest-title"
                    />
                    <span className="text-xs text-[#5A6C7D] mt-1 block">{(editedStructuredContent.pin_title || '').length} / 100</span>
                  </div>
                  <div>
                    <Label className="text-[#A0AEC0] text-xs mb-1.5 block">Pin-Beschreibung</Label>
                    <Textarea
                      value={editedStructuredContent.pin_description || ''}
                      onChange={(e) => setEditedStructuredContent(prev => ({ ...prev, pin_description: e.target.value.slice(0, 500) }))}
                      maxLength={500}
                      className="min-h-[250px] bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white text-sm leading-relaxed resize-y"
                      data-testid="expanded-pinterest-desc"
                    />
                    <span className="text-xs text-[#5A6C7D] mt-1 block">{(editedStructuredContent.pin_description || '').length} / 500</span>
                  </div>
                  <div>
                    <Label className="text-[#A0AEC0] text-xs mb-1.5 block">Link (optional)</Label>
                    <Input
                      value={editedStructuredContent.pin_link || ''}
                      onChange={(e) => setEditedStructuredContent(prev => ({ ...prev, pin_link: e.target.value }))}
                      className="bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white h-11 text-sm"
                      placeholder="https://..."
                      data-testid="expanded-pinterest-link"
                    />
                  </div>
                </div>
              ) : selectedContent.platform === 'youtube' ? (
                <div className="space-y-4">
                  <div>
                    <Label className="text-[#A0AEC0] text-xs mb-1.5 block">Video-Titel</Label>
                    <Input
                      value={editedStructuredContent.youtube_title || ''}
                      onChange={(e) => setEditedStructuredContent(prev => ({ ...prev, youtube_title: e.target.value }))}
                      maxLength={100}
                      className="bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white h-11 text-sm"
                      data-testid="expanded-youtube-title"
                    />
                  </div>
                  <div>
                    <Label className="text-[#A0AEC0] text-xs mb-1.5 block">Skript</Label>
                    <Textarea
                      value={editedStructuredContent.youtube_script || ''}
                      onChange={(e) => setEditedStructuredContent(prev => ({ ...prev, youtube_script: e.target.value }))}
                      className="min-h-[200px] bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white text-sm leading-relaxed resize-y"
                      data-testid="expanded-youtube-script"
                    />
                  </div>
                  <div>
                    <Label className="text-[#A0AEC0] text-xs mb-1.5 block">YouTube-Beschreibung</Label>
                    <Textarea
                      value={editedStructuredContent.youtube_description || ''}
                      onChange={(e) => setEditedStructuredContent(prev => ({ ...prev, youtube_description: e.target.value }))}
                      className="min-h-[150px] bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white text-sm leading-relaxed resize-y"
                      data-testid="expanded-youtube-desc"
                    />
                  </div>
                  <div>
                    <Label className="text-[#A0AEC0] text-xs mb-1.5 block">Tags (SEO)</Label>
                    <Input
                      value={editedStructuredContent.youtube_tags || ''}
                      onChange={(e) => setEditedStructuredContent(prev => ({ ...prev, youtube_tags: e.target.value }))}
                      className="bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white h-11 text-sm"
                      placeholder="keyword1, keyword2"
                      data-testid="expanded-youtube-tags"
                    />
                  </div>
                </div>
              ) : selectedContent.platform?.startsWith('custom_') ? (
                <div className="space-y-4">
                  {(() => {
                    const cc = customChannels.find(c => c.id === selectedContent.platform);
                    if (!cc) return <p className="text-[#5A6C7D] text-sm">{lang === 'de' ? 'Kanal-Konfiguration nicht gefunden.' : 'Channel configuration not found.'}</p>;
                    const customFields = selectedContent.custom_fields || {};
                    return cc.fields.map((field) => {
                      const fieldValue = editedStructuredContent[`custom_${field.name}`] ?? customFields[field.name] ?? '';
                      return (
                        <div key={field.name}>
                          <div className="flex items-center justify-between mb-1.5">
                            <Label className="text-[#A0AEC0] text-xs">{field.name}</Label>
                            <span className="text-xs text-[#5A6C7D]">{fieldValue.length} / {field.maxLength}</span>
                          </div>
                          {field.maxLength > 200 ? (
                            <Textarea
                              value={fieldValue}
                              onChange={(e) => setEditedStructuredContent(prev => ({ ...prev, [`custom_${field.name}`]: e.target.value }))}
                              style={{ minHeight: field.maxLength > 1000 ? '300px' : '180px' }}
                              className="bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white text-sm resize-y"
                              placeholder={field.name}
                              data-testid={`expanded-custom-${field.name}`}
                            />
                          ) : (
                            <Input
                              value={fieldValue}
                              onChange={(e) => setEditedStructuredContent(prev => ({ ...prev, [`custom_${field.name}`]: e.target.value }))}
                              className="bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white h-11 text-sm"
                              placeholder={field.name}
                              data-testid={`expanded-custom-${field.name}`}
                            />
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>
              ) : selectedContent.platform === 'linkedin' ? (
                <LinkedInPostEditor
                  text={editedText}
                  hashtags={selectedContent.content_hashtags || ''}
                  authorName={authUser?.company_name || authUser?.name || 'Dein Unternehmen'}
                  authorTagline={
                    (selectedContent.linkedin_planning?.stance)
                    || (lang === 'de' ? 'LinkedIn-Beitrag · Vorschau' : 'LinkedIn post · preview')
                  }
                  language={lang}
                  maxChars={getPlatformLimit(selectedContent.platform)}
                  minHeight={600}
                  contentKey={`${selectedContent.id}-${selectedContent.updated_at || ''}`}
                  onChange={(next) => setEditedText(next)}
                  onSave={async (next) => {
                    try {
                      setEditedText(next);
                      const r = await axios.put(
                        `${API}/content/${selectedContent.id}`,
                        { text_content: next, content_hashtags: '' },
                        { headers: getAuthHeader() }
                      );
                      const newUpdatedAt = r.data?.content?.updated_at || new Date().toISOString();
                      setSelectedContent(prev => ({ ...prev, text_content: next, content_hashtags: '', updated_at: newUpdatedAt }));
                      setContent(prev => prev.map(c => c.id === selectedContent.id ? { ...c, text_content: next, content_hashtags: '', updated_at: newUpdatedAt } : c));
                      toast.success(lang === 'de' ? 'Gespeichert' : 'Saved');
                    } catch (e) {
                      toast.error(e.response?.data?.detail || (lang === 'de' ? 'Speichern fehlgeschlagen' : 'Save failed'));
                      throw e;
                    }
                  }}
                />
              ) : (
                <div>
                  <Label className="text-[#A0AEC0] text-xs mb-1.5 block">Text</Label>
                  <Textarea
                    value={editedText}
                    onChange={(e) => setEditedText(e.target.value)}
                    className="min-h-[400px] bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white resize-y text-sm leading-relaxed"
                    data-testid="expanded-text-editor"
                  />
                  <div className="flex items-center justify-between text-xs px-2 py-1.5 mt-1 rounded bg-[#0A1628] text-[#5A6C7D]">
                    <span>{editedText.length.toLocaleString()} / {getPlatformLimit(selectedContent.platform).toLocaleString()} {t.produktion.charactersLabel}</span>
                  </div>
                </div>
              )}

              {/* Tags in expanded */}
              {tags.length > 0 && (
                <div className="border-t border-[#1A2B42] pt-3">
                  <Label className="text-[#A0AEC0] text-xs mb-2 block">Tags</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {tags.map(tag => {
                      const isActive = (selectedContent.tags || []).includes(tag.name);
                      return (
                        <button key={tag.id} onClick={() => handleToggleTag(selectedContent.id, tag.name)}
                          className={`text-xs px-2.5 py-1 rounded-full border transition-all ${isActive ? 'border-current opacity-100' : 'border-transparent opacity-50 hover:opacity-80'}`}
                          style={{ color: tag.color, backgroundColor: `${tag.color}15` }}>
                          {tag.name} {isActive && '✓'}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="flex-shrink-0 border-t border-[#1A2B42] pt-3">
            <Button variant="outline" onClick={() => setShowExpandedModal(false)} className="border-[#1A2B42] text-[#A0AEC0]">
              {t.common.close}
            </Button>
            <Button
              onClick={async () => {
                // Blog: delegate to BlogArticleViewer's internal save so
                // edits in the editable JSON viewer are persisted. Do NOT
                // close the modal — user should be able to continue editing.
                if ((selectedContent?.platform === 'blog' || selectedContent?.platform === 'newsletter') && selectedContent?.blog_structured && blogViewerExpandedRef.current?.save) {
                  await blogViewerExpandedRef.current.save();
                  return;
                }
                if (selectedContent?.platform === 'produktbeschreibung' && productViewerExpandedRef.current?.save) {
                  await productViewerExpandedRef.current.save();
                  return;
                }
                if (selectedContent?.platform === 'pressemitteilung' || selectedContent?.platform === 'pinterest' || selectedContent?.platform === 'youtube') {
                  handleSaveStructuredContent();
                } else if (selectedContent?.platform?.startsWith('custom_')) {
                  handleSaveCustomFields();
                } else {
                  handleSaveText();
                }
                setShowExpandedModal(false);
              }}
              disabled={isSaving}
              className="bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628]"
              data-testid="expanded-save-btn"
            >
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : t.common.save}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tag Manager Modal */}
      <Dialog open={showTagManager} onOpenChange={setShowTagManager}>
        <DialogContent className="bg-[#1A2B42] border-[#0A1628] max-w-sm" data-testid="tag-manager-modal">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Tag className="w-5 h-5 text-[#00D9C8]" />
              {t.produktion.manageTags}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Create new tag */}
            <div className="flex gap-2">
              <Input
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                placeholder={t.produktion.newTagName}
                className="flex-1 bg-[#0A1628] border-transparent focus:border-[#00D9C8] text-white text-sm h-9"
                onKeyDown={(e) => { if (e.key === 'Enter') handleCreateTag(); }}
                data-testid="new-tag-input"
              />
              <input
                type="color"
                value={newTagColor}
                onChange={(e) => setNewTagColor(e.target.value)}
                className="w-9 h-9 rounded cursor-pointer border-0 bg-transparent"
                data-testid="new-tag-color"
              />
              <Button onClick={handleCreateTag} size="sm" className="h-9 px-3 bg-[#00D9C8] text-[#0A1628]" data-testid="create-tag-btn">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {/* Existing tags */}
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {tags.map(tag => (
                <div key={tag.id} className="flex items-center justify-between bg-[#0A1628] p-2.5 rounded" data-testid={`tag-item-${tag.id}`}>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: tag.color }} />
                    <span className="text-sm text-white">{tag.name}</span>
                  </div>
                  <button
                    onClick={() => handleDeleteTag(tag.id)}
                    className="p-1 text-[#5A6C7D] hover:text-red-400 transition-colors"
                    data-testid={`delete-tag-${tag.id}`}
                  >
                    <Trash className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              {tags.length === 0 && (
                <p className="text-[#5A6C7D] text-sm text-center py-4">{t.produktion.noTagsYet}</p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Folder Move Modal */}
      <Dialog open={showFolderMoveModal} onOpenChange={(open) => { if (!open) { setShowFolderMoveModal(false); setMovingContentId(null); } }}>
        <DialogContent className="bg-[#1A2B42] border-[#0A1628] max-w-sm" data-testid="folder-move-modal">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FolderInput className="w-5 h-5 text-[#00D9C8]" />
              {t.produktion.moveToFolder}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <button
              onClick={() => handleMoveToFolder(movingContentId, null)}
              className="w-full flex items-center gap-2 p-3 bg-[#0A1628] hover:bg-[#0A1628]/80 rounded text-sm text-[#A0AEC0] hover:text-white transition-colors"
              data-testid="move-no-folder"
            >
              <X className="w-4 h-4" />
              {t.produktion.removeFromFolder}
            </button>
            {folders.map(folder => (
              <button
                key={folder.id}
                onClick={() => handleMoveToFolder(movingContentId, folder.id)}
                className="w-full flex items-center gap-2 p-3 bg-[#0A1628] hover:bg-[#0A1628]/80 rounded text-sm text-white transition-colors"
                data-testid={`move-to-folder-${folder.id}`}
              >
                <FolderOpen className="w-4 h-4 text-[#00D9C8]" />
                {folder.name}
                <span className="ml-auto text-xs text-[#5A6C7D]">({folder.content_count})</span>
              </button>
            ))}
            {folders.length === 0 && (
              <p className="text-[#5A6C7D] text-sm text-center py-4">{t.produktion.noFoldersYet}</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <ImageStyleModal
        open={showImageStyleModal}
        onOpenChange={setShowImageStyleModal}
        contentId={selectedContent?.id}
        contentText={selectedContent?.text_content}
        platform={selectedContent?.platform}
        onImageGenerated={(urls) => {
          // Dedup: never reference the same asset twice (Bug B).
          const mergeUnique = (arr) => Array.from(new Set([...(arr || []), ...(urls || [])]));
          setSelectedContent(prev => ({ ...prev, image_urls: mergeUnique(prev.image_urls) }));
          setContent(prev => prev.map(c =>
            c.id === selectedContent?.id
              ? { ...c, image_urls: mergeUnique(c.image_urls) }
              : c
          ));
          const max = getMaxImages();
          setSelectedImages(prev => Array.from(new Set([...prev, ...(urls || [])])).slice(0, max));
        }}
        getAuthHeader={getAuthHeader}
        lang={lang}
      />

      <UpgradeGatePopup
        open={showVideoGate}
        onOpenChange={setShowVideoGate}
        featureTitle={lang === 'de' ? 'Video-Generierung (YouTube)' : 'Video generation (YouTube)'}
        featureDescription={lang === 'de'
          ? 'Video-Content und YouTube-Publishing sind im aktuellen Paket nicht enthalten. Upgrade auf Growth, um automatisch Videos erstellen und veröffentlichen zu können.'
          : 'Video content and YouTube publishing are not included in your current plan. Upgrade to Growth to create and publish videos automatically.'}
        requiredPlan="growth"
      />
    </div>
  );
};

export default Produktion;
